import type { TOptions } from 'i18next';
import type { RequiredDeep } from 'type-fest';
import { z } from 'zod';

import { httpStatusCodeSchema } from '../data-contract/error';
import { userRoleSchema } from '../data-contract/user-role';

const roleSchema = z
    .object({
        description: z.string(),
        label: z.string().default('not set'),
        title: z.string(),
    })
    .partial();

const errorTranslationSchema = z
    .object({
        action: z.string(),
        message: z.string(),
        title: z.string(),
    })
    .partial();

const documentationArticleSchema = z
    .object({
        description: z.string(),
        title: z.string(),
    })
    .partial();

const onboardingProfileVisibilitySettingSchema = z
    .object({
        subtitle: z.string(),
        title: z.string(),
    })
    .partial();

const pluralizeSchema = z
    .object({
        few: z.string(),
        many: z.string(),
        manyDefault: z.string(),
        one: z.string(),
        oneDefault: z.string(),
        other: z.string(),
        two: z.string(),
    })
    .partial();

const skillOverwriteSchema = z
    .object({
        analysis: z.string(),
        architecture: z.string(),
        architectureLower: z.string(),
        cluster: z.string(),
        clusterLower: z.string(),
        clusters: z.string(),
        clustersLower: z.string(),
        data: z.string(),
        details: z.string(),
        detailsLower: z.string(),
        family: z.string(),
        familyLower: z.string(),
        gap: z.string(),
        gapLower: z.string(),
        group: z.string(),
        groupLower: z.string(),
        groups: z.string(),
        groupsLower: z.string(),
        level: z.string(),
        levels: z.string(),
        many: z.string(),
        manyLower: z.string(),
        mosaic: z.string(),
        mosaicLower: z.string(),
        one: z.string(),
        oneLower: z.string(),
        profile: z.string(),
        profileLower: z.string(),
        recommendations: z.string(),
        recommendationsLower: z.string(),
        type: z.string(),
        typeLower: z.string(),
    })
    .partial();

const reviewOverwriteSchema = z
    .object({
        noun: z.string(),
        nounLower: z.string(),
        reviewedStatus: z.string(),
        reviewedStatusAdjective: z.string(),
        reviewedStatusAdjectiveLower: z.string(),
        reviewedStatusLower: z.string(),
        reviewedStatusUpper: z.string(),
        reviewer: z.string(),
        reviewerLower: z.string(),
        reviewers: z.string(),
        reviewersDative: z.string(),
        reviewersLower: z.string(),
        verb: z.string(),
        verbGerund: z.string(),
        verbImperative: z.string(),
        verbImperativeLower: z.string(),
        verbLower: z.string(),
    })
    .partial();

const publishOverwriteSchema = z
    .object({
        publicationNoun: z.string(),
        publicationNounLower: z.string(),
        publicationNouns: z.string(),
        publicationNounsLower: z.string(),
        publishedStatus: z.string(),
        publishedStatusAdjective: z.string(),
        publishedStatusLower: z.string(),
        publishedStatusUpper: z.string(),
        verb: z.string(),
        verbLower: z.string(),
    })
    .partial();

const jobProfileOverwriteSchema = z
    .object({
        many: z.string(),
        manyLower: z.string(),
        one: z.string(),
        oneLower: z.string(),
        templateMany: z.string(),
        templateManyLower: z.string(),
        templateOne: z.string(),
        templateOneLower: z.string(),
    })
    .partial();

const careerFrameworkOverwriteSchema = z
    .object({
        many: z.string(),
        manyLower: z.string(),
        one: z.string(),
        oneLower: z.string(),
    })
    .partial();

const dashboardV2SetupCardSchema = z
    .object({
        description: z.string(),
        title: z.string(),
    })
    .partial();

const dashboardV2SetupSchema = z
    .object({
        'aria-label': z
            .object({
                'next-step': z.string(),
                processing: z.string(),
            })
            .partial(),
        cards: z
            .object({
                'career-framework': dashboardV2SetupCardSchema,
                'company-info': dashboardV2SetupCardSchema,
                'file-upload': dashboardV2SetupCardSchema,
                'generate-job-families-and-clusters': dashboardV2SetupCardSchema,
                'job-profile-template': dashboardV2SetupCardSchema,
            })
            .partial(),
        menu: z
            .object({
                description: z.string(),
                title: z.string(),
            })
            .partial(),
        progress: z.string(),
        processing: z
            .object({
                'generate-job-architecture': z.string(),
            })
            .partial(),
        status: z
            .object({
                done: z.string(),
                processing: z.string(),
                'still-to-do': z.string(),
            })
            .partial(),
    })
    .partial();

export const translationResourceSchema = z
    .object({
        common: z
            .object({
                actions: z.string(),
                add: z.string(),
                adjust: z.string(),
                apply: z.string(),
                'archive-named': z.string(),
                assign: z.string(),
                back: z.string(),
                'back-to-jsa': z.string(),
                bookmark: z.string(),
                boost: z.string(),
                brand: z.string(),
                by: z.string(),
                cancel: z.string(),
                career: z.string(),
                close: z.string(),
                collapse: z.string(),
                competencies: z.string(),
                completed: z.string(),
                confirm: z.string(),
                continue: z.string(),
                copy: z.string(),
                create: z.string(),
                'create-account': z.string(),
                cut: z.string(),
                date: z.string(),
                delete: z.string(),
                'delete-my-account': z.string(),
                'delete-named': z.string(),
                'deleted-account': z.string(),
                description: z.string(),
                discard: z.string(),
                document: z.string(),
                done: z.string(),
                'dont-show-again': z.string(),
                download: z.string(),
                'download-manual': z.string(),
                'external-link': z
                    .object({
                        description: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'download-pdf': z.string(),
                edit: z.string(),
                'edit-named': z.string(),
                email: z.string(),
                'email-hidden': z.string(),
                end: z.string(),
                enums: z
                    .object({
                        duration: z
                            .object({
                                'fixed-term': z.string(),
                                permanent: z.string(),
                            })
                            .partial(),
                        'job-profile-review-state': z
                            .object({
                                approved: z.string(),
                                assigned: z.string(),
                            })
                            .partial(),
                        'job-seeking-status': z
                            .object({
                                'not open': z.string(),
                                open: z.string(),
                            })
                            .partial(),
                        'job-state': z
                            .object({
                                archived: z.string(),
                                draft: z.string(),
                                hired: z.string(),
                                published: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'relocation-preference': z
                            .object({
                                'not open': z.string(),
                                open: z.string(),
                                'relocation only': z.string(),
                            })
                            .partial(),
                        'seniority-level': z
                            .object({
                                0: z.string(),
                                1: z.string(),
                                10: z.string(),
                                2: z.string(),
                                3: z.string(),
                                4: z.string(),
                                5: z.string(),
                                6: z.string(),
                                7: z.string(),
                                8: z.string(),
                                9: z.string(),
                            })
                            .partial(),
                        skillType: z
                            .object({
                                hard: z.string(),
                                language: z.string(),
                                soft: z.string(),
                            })
                            .partial(),
                        'work-place': z
                            .object({
                                'on-site': z.string(),
                                remote: z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                exclude: z.string(),
                expand: z.string(),
                export: z.string(),
                exporting: z.string(),
                'featured-at': z.string(),
                feedback: z.string(),
                filters: z
                    .object({
                        apply: z.string(),
                        city: z.string(),
                        'experience-level': z.string(),
                        'job-seeking': z
                            .object({
                                'not open': z.string(),
                                open: z.string(),
                            })
                            .partial(),
                        'job-seeking-status': z.string(),
                        relocation: z.string(),
                        'relocation-preference': z
                            .object({
                                'not open': z.string(),
                                open: z.string(),
                                'relocation only': z.string(),
                            })
                            .partial(),
                        state: z.string(),
                    })
                    .partial(),
                finish: z.string(),
                help: z.string(),
                imprint: z.string(),
                'in-progress': z.string(),
                language: z.string(),
                languages: z.object({
                    'cs-CZ': z.string(),
                    'de-DE': z.string(),
                    'en-US': z.string(),
                    'es-ES': z.string(),
                    'es-MX': z.string(),
                    'fi-FI': z.string(),
                    'fr-CA': z.string(),
                    'fr-FR': z.string(),
                    'hr-HR': z.string(),
                    'hu-HU': z.string(),
                    'it-IT': z.string(),
                    'ja-JP': z.string(),
                    'ko-KR': z.string(),
                    'ms-MY': z.string(),
                    'nl-NL': z.string(),
                    'pl-PL': z.string(),
                    'pt-BR': z.string(),
                    'pt-PT': z.string(),
                    'ro-RO': z.string(),
                    'sk-SK': z.string(),
                    'sr-RS': z.string(),
                    'sv-SE': z.string(),
                    'zh-CN': z.string(),
                    'zh-TW': z.string(),
                }),
                'last-modified': z.string(),
                'load-more': z.string(),
                loading: z.string(),
                login: z.string(),
                'login-button': z.string(),
                login2: z.string(),
                login3: z.string(),
                logout: z.string(),
                more: z.string(),
                'more-options-for': z.string(),
                name: z.string(),
                new: z.string(),
                no: z.string(),
                'no-selection': z.string(),
                open: z.string(),
                options: z.string(),
                others: z.string(),
                paste: z.string(),
                'phone-hidden': z.string(),
                planned: z.string(),
                private: z.string(),
                profile: z.string(),
                'read-more': z.string(),
                reject: z.string(),
                reload: z.string(),
                remove: z.string(),
                reset: z.string(),
                save: z.string(),
                search: z.string(),
                'select-all': z.string(),
                settings: z.string(),
                setup: z.string(),
                share: z.string(),
                shared: z.string(),
                'show-all': z.string(),
                'show-less': z.string(),
                'show-more': z.string(),
                'sign-in': z.string(),
                size: z.string(),
                skill: z.string(),
                'skills-and-competencies': z.string(),
                'skills-unavailable': z.string(),
                skip: z.string(),
                start: z.string(),
                'start-audio-transcription': z.string(),
                status: z.string(),
                'stop-recording': z.string(),
                'structured-content': z.string(),
                submit: z.string(),
                thinking: z.string(),
                title: z.string(),
                to: z.string(),
                today: z.string(),
                type: z.string(),
                unwatch: z.string(),
                'user-name-invisible': z.string(),
                'user-name-unavailable': z.string(),
                watch: z.string(),
                welcome: z.string(),
                'worked-for': z.string(),
                yes: z.string(),
                yesterday: z.string(),
                your: z.string(),
            })
            .partial(),
        chat: z
            .object({
                artifact: z
                    .object({
                        'available-until': z.string(),
                        download: z.string(),
                        expired: z.string(),
                    })
                    .partial(),
                'alignment-bulk-run': z
                    .object({
                        failed: z.string(),
                        incomplete: z.string(),
                        'more-not-shown': z.string(),
                        progress: z.string(),
                        skipped: z.string(),
                        started: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'position-bulk-run': z
                    .object({
                        failed: z.string(),
                        incomplete: z.string(),
                        'more-not-shown': z.string(),
                        progress: z.string(),
                        skipped: z.string(),
                        succeeded: z.string(),
                        title: z
                            .object({
                                'assign-reviewer': z.string(),
                                publish: z.string(),
                                'remove-reviewer': z.string(),
                                review: z.string(),
                                unknown: z.string(),
                                unpublish: z.string(),
                                unreview: z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                'position-plan': z
                    .object({
                        cancelled: z.string(),
                        'status-unavailable': z.string(),
                    })
                    .partial(),
                'starter-prompts': z
                    .object({
                        items: z
                            .object({
                                'alignment-employee-comments': z.string(),
                                'alignment-employee-automations': z.string(),
                                'alignment-employee-next': z.string(),
                                'alignment-manager-comments': z.string(),
                                'alignment-manager-decide': z.string(),
                                'alignment-manager-summary': z.string(),
                                'alignment-team-start': z.string(),
                                'alignment-team-waiting': z.string(),
                                'any-next-steps': z.string(),
                                'any-no-reviewer': z.string(),
                                'any-strengths': z.string(),
                                'cluster-duplicate-skills': z.string(),
                                'cluster-excel-overview': z.string(),
                                'family-duplicate-skills': z.string(),
                                'family-excel-overview': z.string(),
                                'level-compare-merge': z.string(),
                                'level-review': z.string(),
                                'role-compare-merge': z.string(),
                                'role-review': z.string(),
                            })
                            .partial(),
                        label: z.string(),
                    })
                    .partial(),
                attach: z.string(),
                'auto-close': z
                    .object({
                        accept: z.string(),
                        'keep-open': z.string(),
                        title: z.string(),
                    })
                    .partial(),
                beta: z.string(),
                collapse: z.string(),
                'composer-placeholder': z.string(),
                copy: z
                    .object({
                        copied: z.string(),
                        label: z.string(),
                    })
                    .partial(),
                disclaimer: z.string(),
                'error-fallback': z.string(),
                expand: z.string(),
                feedback: z
                    .object({
                        close: z.string(),
                        description: z.string(),
                        'details-placeholder': z.string(),
                        error: z.string(),
                        'negative-aria-label': z.string(),
                        'negative-tooltip': z.string(),
                        'positive-aria-label': z.string(),
                        'positive-tooltip': z.string(),
                        sending: z.string(),
                        submit: z.string(),
                        tags: z
                            .object({
                                clear_actionable: z.string(),
                                incorrect_incomplete: z.string(),
                                lost_context: z.string(),
                                missing_job_or_skill_context: z.string(),
                                other: z.string(),
                                request_followed: z.string(),
                                request_not_followed: z.string(),
                                task_resolved: z.string(),
                                useful_job_architecture_context: z.string(),
                                useful_skill_context: z.string(),
                                wrong_scope: z.string(),
                            })
                            .partial(),
                        title: z.string(),
                    })
                    .partial(),
                history: z.string(),
                'history-attention': z.string(),
                'message-unavailable': z.string(),
                'new-chat': z.string(),
                'next-question': z.string(),
                'open-compare-view': z.string(),
                'previous-question': z.string(),
                'reply-in-progress': z.string(),
                'proposal-edit': z
                    .object({
                        'close-compare': z.string(),
                        compare: z.string(),
                        'compare-with-current': z.string(),
                        current: z.string(),
                        edit: z.string(),
                        edited: z.string(),
                        loading: z.string(),
                        'no-current-value': z.string(),
                        'no-proposed-value': z.string(),
                        proposed: z.string(),
                        'proposed-text': z.string(),
                        unavailable: z.string(),
                        'unknown-user': z.string(),
                    })
                    .partial(),
                'framework-changes': z
                    .object({
                        move: z.string(),
                        'levels-left-empty': z.string(),
                        'levels-left-empty_one': z.string(),
                        'pay-removed': z.string(),
                        preview: z.string(),
                        'hide-preview': z.string(),
                        previewing: z.string(),
                    })
                    .partial(),
                record: z.string(),
                'create-items-request': z
                    .object({
                        title: z.string(),
                    })
                    .partial(),
                'regenerate-request': z
                    .object({
                        title: z.string(),
                        version: z.string(),
                        'with-comments': z.string(),
                    })
                    .partial(),
                retry: z.string(),
                send: z.string(),
                'send-failed': z.string(),
                'answer-failed': z.string(),
                'question-expired': z.string(),
                stop: z.string(),
                'thread-status': z
                    .object({
                        'needs-input': z.string(),
                        'on-hold': z.string(),
                        running: z.string(),
                        unseen: z.string(),
                    })
                    .partial(),
                'threads-rail': z
                    .object({
                        'delete-description': z.string(),
                        'delete-error': z.string(),
                        'delete-title': z.string(),
                        filters: z
                            .object({
                                'needs-input': z.string(),
                                running: z.string(),
                                unread: z.string(),
                            })
                            .partial(),
                        title: z.string(),
                    })
                    .partial(),
                'thread-busy': z.string(),
                'agent-activity': z.string(),
                'untitled-thread': z.string(),
                'toggle-aria-label': z.string(),
            })
            .partial(),
        components: z
            .object({
                'add-to-ambitions-button': z.string(),
                'add-to-profile-button': z.string(),
                'anonymize-button': z
                    .object({
                        'user-names-hide': z.string(),
                        'user-names-show': z.string(),
                    })
                    .partial(),
                'data-grid-panel': z
                    .object({
                        footer: z
                            .object({
                                'hide-all': z.string(),
                                'show-all': z.string(),
                            })
                            .partial(),
                        title: z.string(),
                        'title-placeholder': z.string(),
                    })
                    .partial(),
                'form-date-picker-field': z
                    .object({
                        clear: z.string(),
                        'next-month': z.string(),
                        'previous-month': z.string(),
                        today: z.string(),
                    })
                    .partial(),
                'invisible-indicator': z.string(),
                'learning-opportunity-card': z
                    .object({
                        bookmark: z.string(),
                        caption: z.string(),
                        featured: z.string(),
                        'featured-by': z.string(),
                        'skill-message': z.string(),
                    })
                    .partial(),
                'location-filter': z
                    .object({
                        city: z.string(),
                        region: z.string(),
                    })
                    .partial(),
                'remove-from-ambitions-button': z.string(),
                'skill-references': z
                    .object({
                        competencies: z.string(),
                        employees: z.string(),
                        error: z.string(),
                        'job-postings': z.string(),
                        'learning-opportunities': z.string(),
                        levels: z.string(),
                        roles: z.string(),
                        tasks: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                skills: z
                    .object({
                        added: z.string(),
                        'added-language-skills': z.string(),
                        'added-skills': z.string(),
                        'no-skills-attached': z.string(),
                        'no-skills-found': z.string(),
                        removed: z.string(),
                        'search-placeholder': z.string(),
                        'show-skill-recommendation': z.string(),
                        'suggested-hard-skills': z.string(),
                        'suggested-language-skills': z.string(),
                        'suggested-skills': z.string(),
                        'suggested-soft-skills': z.string(),
                    })
                    .partial(),
                'skills-detection': z
                    .object({
                        'button-label': z.string(),
                        'caption-disabled': z.string(),
                        'caption-enabled': z.string(),
                    })
                    .partial(),
                'skills-field': z
                    .object({
                        name: z.string(),
                    })
                    .partial(),
                table: z
                    .object({
                        'configure-columns': z.string(),
                        zoom: z.string(),
                        'zoom-in': z.string(),
                        'zoom-out': z.string(),
                        'zoom-reset': z.string(),
                    })
                    .partial(),
                'take-course': z.string(),
                'tile-resize-handle': z.string(),
                'url-tile-access': z
                    .object({
                        'denied-description': z.string(),
                        'denied-title': z.string(),
                    })
                    .partial(),
            })
            .partial(),
        customization: z
            .object({
                alert: z.string(),
                reset: z.string(),
            })
            .partial(),
        dialogs: z
            .object({
                buttons: z
                    .object({
                        cancel: z.string(),
                        save: z.string(),
                    })
                    .partial(),
                common: z
                    .object({
                        'not-revertible-action-warn': z.string(),
                    })
                    .partial(),
                delete: z
                    .object({
                        'certificate-confirm-title': z.string(),
                        'course-confirm-title': z.string(),
                        'education-confirm-title': z.string(),
                        'job-profile-confirm-text': z.string(),
                        'job-profile-confirm-title': z.string(),
                        'project-confirm-title': z.string(),
                        'publication-confirm-title': z.string(),
                        'work-confirm-title': z.string(),
                    })
                    .partial(),
                discard: z
                    .object({
                        content: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'general-information': z
                    .object({
                        location: z.string(),
                    })
                    .partial(),
                job: z
                    .object({
                        status: z.string(),
                        'title-delete': z.string(),
                    })
                    .partial(),
                'language-select-disabled-tooltip': z.string(),
                'learning-opportunities': z
                    .object({
                        'search-placeholder': z.string(),
                        text: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'learning-opportunity': z
                    .object({
                        'exclude-featured-users': z.string(),
                        'title-delete': z.string(),
                    })
                    .partial(),
                'no-consent': z
                    .object({
                        back: z.string(),
                        content: z.string(),
                        delete: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'shortlist-notes': z
                    .object({
                        'add-note': z.string(),
                        'add-note-anonymized': z.string(),
                        'add-note-placeholder': z.string(),
                    })
                    .partial(),
                skills: z
                    .object({
                        proficiency: z
                            .object({
                                language: z
                                    .object({
                                        0: z.string(),
                                        1: z.string(),
                                        2: z.string(),
                                        3: z.string(),
                                        4: z.string(),
                                        5: z.string(),
                                        6: z.string(),
                                    })
                                    .partial(),
                                skills: z
                                    .object({
                                        0: z.string(),
                                        1: z.string(),
                                        2: z.string(),
                                        3: z.string(),
                                        4: z.string(),
                                        5: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                    })
                    .partial(),
                terms: z
                    .object({
                        'consent-label': z.string(),
                        'consent-label-first-part': z.string(),
                        'consent-label-second-part': z.string(),
                        'last-updated': z.string(),
                        'load-error': z.string(),
                        loading: z.string(),
                        proceed: z.string(),
                        title: z.string(),
                        'validation-message': z.string(),
                    })
                    .partial(),
                'update-user-profile-skills': z
                    .object({
                        'agree-update-profile-skills': z.string(),
                        headline: z.string(),
                        'limit-exceed-agree': z.string(),
                        'limit-text': z.string(),
                        'reject-update-profile-skills': z.string(),
                        'update-skills-text': z.string(),
                    })
                    .partial(),
                'user-settings': z
                    .object({
                        'account-deletion-message': z.string(),
                        'account-logged-out': z.string(),
                        'delete-account': z.string(),
                        'no-i-want-to-stay': z.string(),
                        'see-all-translations': z.string(),
                    })
                    .partial(),
            })
            .partial(),
        errors: z
            .object({
                common: errorTranslationSchema,
                component: z
                    .object({
                        dashboard: z
                            .object({
                                summary: z
                                    .object({
                                        ambitions: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        drawer: z
                            .object({
                                ambitions: z.string(),
                                'candidate-matching-info': z.string(),
                                languages: z.string(),
                                'profile-preview': z.string(),
                                skills: z.string(),
                                'skills-detail': z.string(),
                            })
                            .partial(),
                        'featured-opportunities': z.string(),
                        'global-search': z
                            .object({
                                employees: z.string(),
                                'job-levels': z.string(),
                                'job-openings': z.string(),
                                'job-roles': z.string(),
                                'learning-opportunities': z.string(),
                                skills: z.string(),
                            })
                            .partial(),
                        job: z
                            .object({
                                'candidate-recommendations-section': z.string(),
                                'candidate-shortlist-section': z.string(),
                                create: z.string(),
                                edit: z.string(),
                                'job-matching-section': z.string(),
                                'job-skills': z.string(),
                                'update-ambitions': z.string(),
                            })
                            .partial(),
                        'job-profiles': z
                            .object({
                                'job-profile-created': z.string(),
                            })
                            .partial(),
                        'job-recommendation-list-content': z.string(),
                        'job-recommendations-filter': z.string(),
                        'learning-opportunities': z
                            .object({
                                'add-to-profile': z.string(),
                                'already-completed': z.string(),
                                create: z.string(),
                                description: z.string(),
                                edit: z.string(),
                                'featured-users': z.string(),
                                skills: z.string(),
                            })
                            .partial(),
                        profile: z
                            .object({
                                ambitions: z.string(),
                                'certificates-history': z.string(),
                                'course-history': z.string(),
                                'education-history': z.string(),
                                languages: z.string(),
                                'project-history': z.string(),
                                'publication-history': z.string(),
                                skills: z.string(),
                                'skills-suggestions': z.string(),
                                'work-history': z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                fatal: z
                    .object({
                        message: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                network: z
                    .object({
                        message: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'rate-limited': z.string(),
                'required-fields-empty': z.string(),
                'tenant-not-found': z
                    .object({
                        action: z.string(),
                        'form-label': z.string(),
                        'input-label': z.string(),
                        message: z.string(),
                        placeholder: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                status: z.record(httpStatusCodeSchema.transform(String), errorTranslationSchema),
            })
            .partial(),
        globalsearch: z
            .object({
                'exact-skills': z.string(),
                'go-to': z.string(),
                'job-levels': z.string(),
                'job-roles': z.string(),
                jobs: z.string(),
                'learning-opportunities': z.string(),
                'no-results': z.string(),
                'placeholder-features': z.string(),
                'placeholder-min-length': z.string(),
                'placeholder-multiple-skills': z.string(),
                'placeholder-to-search': z.string(),
                positions: z.string(),
                'related-skills': z.string(),
                results: z.string(),
                'see-more': z.string(),
                skills: z.string(),
                'skills-failed': z.string(),
                title: z.string(),
                users: z.string(),
            })
            .partial(),
        navigation: z
            .object({
                agents: z.string(),
                'ai-transformation': z.string(),
                alignment: z.string(),
                'analytics-dashboard': z.string(),
                'career-frameworks': z.string(),
                'company-settings': z.string(),
                'control-room': z.string(),
                dashboard: z.string(),
                documentation: z.string(),
                'go-to': z.string(),
                group: z
                    .object({
                        architecture: z.string(),
                        people: z.string(),
                        talent: z.string(),
                    })
                    .partial(),
                'graph-canvas': z.string(),
                'growth-goals': z.string(),
                home: z.string(),
                'job-architecture': z.string(),
                'job-management': z.string(),
                'job-postings': z.string(),
                'job-profiles': z.string(),
                'learning-management': z.string(),
                'my-growth': z.string(),
                'position-management': z.string(),
                profile: z.string(),
                scope: z
                    .object({
                        company: z.string(),
                        label: z.string(),
                        me: z.string(),
                        team: z.string(),
                    })
                    .partial(),
                'shortcut-sequence': z.string(),
                skills: z.string(),
                'talent-dashboard': z.string(),
                'talent-directory': z.string(),
                tasks: z.string(),
                'team-alignment': z.string(),
                'users-and-roles': z.string(),
            })
            .partial(),
        overwrites: z
            .object({
                careerFramework: careerFrameworkOverwriteSchema,
                jobCluster: pluralizeSchema,
                jobFamily: pluralizeSchema,
                jobLevel: pluralizeSchema,
                jobProfile: jobProfileOverwriteSchema,
                jobRole: pluralizeSchema,
                publish: publishOverwriteSchema,
                review: reviewOverwriteSchema,
                skill: skillOverwriteSchema,
            })
            .partial(),
        pages: z
            .object({
                'ai-transformation': z
                    .object({
                        band: z.object({
                            high: z.string(),
                            low: z.string(),
                            moderate: z.string(),
                            'no-data': z.string(),
                            none: z.string(),
                            'very-high': z.string(),
                        }),
                        chart: z.object({
                            'axis-task-count': z.string(),
                            loading: z.string(),
                            retry: z.string(),
                            'no-architectures': z.string(),
                            'search-error': z.string(),
                            'search-loading': z.string(),
                            'axis-exposure': z.string(),
                            'zoom-in': z.string(),
                            'zoom-out': z.string(),
                            empty: z.string(),
                            error: z.string(),
                            matches: z.string(),
                            'show-more': z.string(),
                            truncated: z.string(),
                        }),
                        filter: z.object({
                            architecture: z.string(),
                            layer: z.string(),
                            'search-placeholder': z.string(),
                        }),
                        handover: z.object({
                            high: z.string(),
                            low: z.string(),
                            moderate: z.string(),
                            none: z.string(),
                            'very-high': z.string(),
                        }),
                        'handover-chart': z.object({
                            'destination-released': z.string(),
                            'destination-retained': z.string(),
                            empty: z.string(),
                            error: z.string(),
                            'flow-label': z.string(),
                            'items-note': z.string(),
                            'source-commodity': z.string(),
                            'source-contribution': z.string(),
                            'source-unclassified': z.string(),
                            'title-destinations': z.string(),
                            'title-sources': z.string(),
                        }),
                        'item-tile': z.object({
                            coverage: z.string(),
                            loading: z.string(),
                            'not-assessed': z.string(),
                            'not-found': z.string(),
                            error: z.string(),
                            'open-level': z.string(),
                            'open-role': z.string(),
                            'open-task': z.string(),
                            quadrant: z.string(),
                            'task-column-handover': z.string(),
                            'task-column-task': z.string(),
                            'task-effort-share': z.string(),
                            'task-mix': z.string(),
                            'task-own-share': z.string(),
                            'task-released': z.string(),
                            tasks: z.string(),
                        }),
                        kpi: z.object({
                            'assessed-count': z.string(),
                            coverage: z.string(),
                            'coverage-note': z.string(),
                            'coverage-hint': z.string(),
                            exposure: z.string(),
                            'exposure-hint': z.string(),
                            horizon: z.string(),
                            'item-count': z.string(),
                            remaining: z.string(),
                            'remaining-hint': z.string(),
                            'task-count': z.string(),
                        }),
                        layer: z.object({
                            cluster: z.string(),
                            family: z.string(),
                            level: z.string(),
                            role: z.string(),
                        }),
                        'layer-one': z.object({
                            cluster: z.string(),
                            family: z.string(),
                            level: z.string(),
                            role: z.string(),
                        }),
                        'outcome-chart': z.object({
                            'block-note': z.string(),
                            'cell-label': z.string(),
                            empty: z.string(),
                            'not-assessed': z.string(),
                        }),
                        'page-title': z.string(),
                        quadrant: z.object({
                            'automation-opportunity': z.object({
                                subtitle: z.string(),
                                title: z.string(),
                            }),
                            'low-priority': z.object({
                                subtitle: z.string(),
                                title: z.string(),
                            }),
                        }),
                        questions: z.object({
                            'picker-label': z.string(),
                            'how-do-roles-change': z.object({
                                subtitle: z.string(),
                                title: z.string(),
                            }),
                            'how-is-work-handed-over': z.object({
                                subtitle: z.string(),
                                title: z.string(),
                            }),
                            'where-in-the-architecture': z.object({
                                subtitle: z.string(),
                                title: z.string(),
                            }),
                            'where-is-automatable-work': z.object({
                                subtitle: z.string(),
                                title: z.string(),
                            }),
                            'where-to-start': z.object({
                                subtitle: z.string(),
                                title: z.string(),
                            }),
                        }),
                        ranking: z.object({
                            'column-band': z.string(),
                            'column-path': z.string(),
                            error: z.string(),
                            title: z.string(),
                        }),
                        strategy: z.object({
                            architecture: z.object({
                                error: z.string(),
                                none: z.string(),
                            }),
                            board: z.object({
                                automatic: z.string(),
                                days: z.string(),
                                days_one: z.string(),
                                empty: z.string(),
                                error: z.string(),
                                'move-error': z.string(),
                                'move-to': z.string(),
                                share: z.string(),
                                'stage-age': z.string(),
                                'stage-age_one': z.string(),
                            }),
                            count: z.object({
                                positions: z.string(),
                                positions_one: z.string(),
                                tasks: z.string(),
                                tasks_one: z.string(),
                            }),
                            error: z.string(),
                            filter: z.object({
                                department: z.string(),
                                'job-cluster': z.string(),
                                'job-family': z.string(),
                                'job-level': z.string(),
                                'job-role': z.string(),
                                label: z.string(),
                                location: z.string(),
                                'matrix-selection': z.string(),
                                potential: z.string(),
                                search: z.string(),
                            }),
                            'group-tile': z.object({
                                empty: z.string(),
                                error: z.string(),
                                level: z.string(),
                                loading: z.string(),
                                overview: z.string(),
                                role: z.string(),
                                'tasks-empty': z.string(),
                                'tasks-error': z.string(),
                            }),
                            list: z.object({
                                'assign-error': z.string(),
                                'change-employee': z.string(),
                                'clear-selection': z.string(),
                                employee: z.string(),
                                empty: z.string(),
                                error: z.string(),
                                manager: z.string(),
                                more: z.string(),
                                position: z.string(),
                                priority: z.string(),
                                task: z.string(),
                                vacant: z.string(),
                                'who-does-it': z.string(),
                            }),
                            matrix: z.object({
                                'axis-impact': z.string(),
                                'axis-score': z.string(),
                                'axis-time': z.string(),
                                empty: z.string(),
                                error: z.string(),
                                hours: z.string(),
                                loading: z.string(),
                                'selection-clear-hint': z.string(),
                                'selection-hint': z.string(),
                                title: z.string(),
                                truncated: z.string(),
                                unassessed: z.string(),
                                unrated: z.string(),
                                untimed: z.string(),
                                'y-axis': z.string(),
                                'y-axis-hours': z.string(),
                                'y-axis-impact': z.string(),
                            }),
                            'page-title': z.string(),
                            priority: z.object({
                                'add-assessed-tasks': z.string(),
                                'add-assessed-tasks_one': z.string(),
                                'add-other-tasks': z.string(),
                                'add-other-tasks_one': z.string(),
                                error: z.string(),
                                partial: z.string(),
                                prioritize: z.string(),
                                'prioritize-name': z.string(),
                                prioritized: z.string(),
                                'prioritized-all-tasks': z.string(),
                                'prioritized-all-tasks_one': z.string(),
                                'prioritized-tasks': z.string(),
                                'prioritized-tasks_one': z.string(),
                                'prioritized-with-tasks': z.string(),
                                'prioritized-with-tasks_one': z.string(),
                                'prioritized-without-tasks': z.string(),
                                'remove-name': z.string(),
                                undo: z.string(),
                            }),
                            'score-level': z.object({
                                high: z.string(),
                                low: z.string(),
                                medium: z.string(),
                                'not-assessed': z.string(),
                                'very-high': z.string(),
                            }),
                            status: z.object({
                                defined: z.string(),
                                'full-position': z.string(),
                                'full-task': z.string(),
                                idle: z.string(),
                                prioritized: z.string(),
                                scaling: z.string(),
                                starting: z.string(),
                            }),
                            'status-short': z.object({
                                defined: z.string(),
                                full: z.string(),
                                idle: z.string(),
                                prioritized: z.string(),
                                scaling: z.string(),
                                starting: z.string(),
                            }),
                            tabs: z.object({
                                opportunities: z.string(),
                                progress: z.string(),
                            }),
                            view: z.object({
                                label: z.string(),
                                levels: z.string(),
                                positions: z.string(),
                                roles: z.string(),
                                tasks: z.string(),
                            }),
                        }),
                    })
                    .partial(),
                'business-intelligence': z
                    .object({
                        innovation: z
                            .object({
                                text: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'skill-gap': z
                            .object({
                                average: z.string(),
                                options: z
                                    .object({
                                        monthly: z.string(),
                                        quarterly: z.string(),
                                        yearly: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        'skill-groups': z
                            .object({
                                title: z.string(),
                            })
                            .partial(),
                        'trending-jobs': z
                            .object({
                                clicks: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'trending-skills': z
                            .object({
                                impressions: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        visibility: z
                            .object({
                                'anonymous-profiles': z.string(),
                                'public-profiles': z.string(),
                                title: z.string(),
                                'total-employees': z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                'career-frameworks': z
                    .object({
                        'add-mode-description': z.string(),
                        'empty-state-description': z.string(),
                        'add-error-generic': z.string(),
                        'add-error-unreachable': z.string(),
                        'add-level-action': z.string(),
                        'add-level-to-track-action': z.string(),
                        'add-mode-title': z.string(),
                        'add-template-action': z.string(),
                        'add-track-action': z.string(),
                        'all-frameworks': z.string(),
                        'architecture-picker-label': z.string(),
                        'chat-create-badge': z.string(),
                        'chat-create-context': z.string(),
                        'chat-create-message': z.string(),
                        'chat-create-question': z.string(),
                        'create-framework-action': z.string(),
                        'create-framework-button': z.string(),
                        'scratch-basics-subtitle': z.string(),
                        'scratch-data-subtitle': z.string(),
                        'scratch-data-unreadable': z.string(),
                        'scratch-data-unreadable_one': z.string(),
                        'scratch-data-title': z.string(),
                        'scratch-review-ai-subtitle': z.string(),
                        'scratch-review-criteria-label': z.string(),
                        'scratch-review-suggested': z.string(),
                        'scratch-review-edit': z.string(),
                        'scratch-review-done': z.string(),
                        'scratch-create': z.string(),
                        'scratch-creating': z.string(),
                        'translation-failed': z.string(),
                        'translation-pending': z.string(),
                        'scratch-creation-failed': z.string(),
                        'scratch-assessing': z.string(),
                        'scratch-researching': z.string(),
                        'scratch-research-company-label': z.string(),
                        'scratch-assessment-failed': z.string(),
                        'scratch-assessment-retry': z.string(),
                        'scratch-assessment-skip': z.string(),
                        'scratch-question-tracks': z.string(),
                        'scratch-track-executive': z.string(),
                        'scratch-question-level-titles': z.string(),
                        'scratch-level-titles-placeholder': z.string(),
                        'scratch-level-titles-ai': z.string(),
                        'scratch-level-titles-manual': z.string(),
                        'scratch-question-level-count': z.string(),
                        'scratch-level-count-unit': z.string(),
                        'scratch-question-criteria': z.string(),
                        'scratch-question-criteria-hint': z.string(),
                        'scratch-criterion-scope-and-impact': z.string(),
                        'scratch-criterion-autonomy': z.string(),
                        'scratch-criterion-expertise': z.string(),
                        'scratch-criterion-people-leadership': z.string(),
                        'scratch-criterion-business-impact': z.string(),
                        'scratch-criterion-other-placeholder': z.string(),
                        'scratch-found-in': z.string(),
                        'scratch-data-waiting': z.string(),
                        'scratch-data-waiting_one': z.string(),
                        'scratch-framework-subtitle': z.string(),
                        'scratch-framework-title': z.string(),
                        'scratch-questions-subtitle': z.string(),
                        'scratch-questions-title': z.string(),
                        'scratch-start-assisted-description': z.string(),
                        'scratch-start-from-existing-description': z.string(),
                        'scratch-start-recommended': z.string(),
                        'scratch-step-data': z.string(),
                        'scratch-step-framework': z.string(),
                        'scratch-step-questions': z.string(),
                        'scratch-levels-subtitle': z.string(),
                        'scratch-levels-title': z.string(),
                        'scratch-levels-unit': z.string(),
                        'scratch-name-placeholder': z.string(),
                        'scratch-name-title': z.string(),
                        'scratch-review-levels-label': z.string(),
                        'scratch-review-name-label': z.string(),
                        'scratch-review-subtitle': z.string(),
                        'scratch-review-title': z.string(),
                        'scratch-review-tracks-label': z.string(),
                        'scratch-start-assisted': z.string(),
                        'scratch-start-from-existing': z.string(),
                        'scratch-start-subtitle': z.string(),
                        'scratch-start-title': z.string(),
                        'scratch-step-start': z.string(),
                        'scratch-step-basics': z.string(),
                        'scratch-step-levels': z.string(),
                        'scratch-step-review': z.string(),
                        'scratch-step-tracks': z.string(),
                        'scratch-track-individual-contributor': z.string(),
                        'scratch-track-management': z.string(),
                        'scratch-track-specialist': z.string(),
                        'scratch-tracks-other-placeholder': z.string(),
                        'scratch-tracks-subtitle': z.string(),
                        'scratch-tracks-title': z.string(),
                        adding: z.string(),
                        'continue-with-selection': z.string(),
                        'copy-and-customise': z.string(),
                        'copy-name': z.string(),
                        'create-blank-framework': z.string(),
                        'created-not-opened': z.string(),
                        'created-partially': z.string(),
                        creating: z.string(),
                        'custom-framework-default-name': z.string(),
                        'custom-template-name': z.string(),
                        'default-track-name': z.string(),
                        'delete-framework-action': z.string(),
                        'delete-level-action': z.string(),
                        'delete-track-action': z.string(),
                        'discard-changes-switch-framework': z.string(),
                        'discard-changes-title': z.string(),
                        'empty-state-title': z.string(),
                        'filter-templates-action': z.string(),
                        'framework-updated': z.string(),
                        'framework-updated-unknown': z.string(),
                        'level-count': z.string(),
                        'levels-column-title': z.string(),
                        'level-details-amount-range': z.string(),
                        'level-details-previous-level': z.string(),
                        'level-details-next-level': z.string(),
                        'level-details-level-number': z.string(),
                        'level-details-to-reach-level': z.string(),
                        'level-details-top-of-track': z.string(),
                        'level-details-no-progression': z.string(),
                        'level-details-no-progression-in-language': z.string(),
                        'level-details-pay-grade': z.string(),
                        'level-details-track-range': z.string(),
                        'level-details-no-framework-pay-title': z.string(),
                        'level-details-no-level-pay': z.string(),
                        'level-details-no-default-pay': z.string(),
                        'level-details-no-salary-range': z.string(),
                        'level-details-add-pay-data': z.string(),
                        'level-details-add-salary-range': z.string(),
                        'loading-architecture': z.string(),
                        'matrix-aria-label': z.string(),
                        'more-actions': z.string(),
                        'select-framework': z.string(),
                        'select-template-button': z.string(),
                        'template-already-active': z.string(),
                        'template-badge-activated': z.string(),
                        'template-badge-certified': z.string(),
                        'template-empty': z.string(),
                        'template-filter-activated': z.string(),
                        'template-filter-all': z.string(),
                        'template-filter-market': z.string(),
                        'template-filter-union': z.string(),
                        'template-metadata': z.string(),
                        'template-preview-empty': z.string(),
                        'template-preview-loading': z.string(),
                        'template-system': z.string(),
                        'template-type-custom': z.string(),
                        'template-type-market': z.string(),
                        'template-type-union': z.string(),
                        'templates-loading': z.string(),
                        'track-count': z.string(),
                        'track-details-levels': z.string(),
                        'track-details-no-levels': z.string(),
                        'track-details-salary-coverage': z.string(),
                        'track-details-region-coverage': z.string(),
                        'track-details-no-track-pay': z.string(),
                        'track-details-previous-track': z.string(),
                        'track-details-next-track': z.string(),
                        'track-description-placeholder': z.string(),
                        'framework-used-in': z.string(),
                        'remove-framework-from-architecture-action': z.string(),
                        'remove-framework-from-architecture-dialog-title': z.string(),
                        'remove-framework-from-architecture-dialog-description': z.string(),
                        'remove-framework-from-architecture-dialog-description-unnamed': z.string(),
                        'assign-to-architecture-action': z.string(),
                        'assign-to-architecture-error': z.string(),
                    })
                    .partial(),
                debug: z
                    .object({
                        'commit-hash': z.string(),
                        description: z.string(),
                        'executed-at': z.string(),
                        'frontend-hash': z.string(),
                        messages: z
                            .object({
                                'all-reachable': z.string(),
                                'blocked-detected': z.string(),
                                'blocked-error-prefix': z.string(),
                            })
                            .partial(),
                        'not-run': z.string(),
                        'run-again': z.string(),
                        running: z.string(),
                        services: z
                            .object({
                                api: z
                                    .object({
                                        name: z.string(),
                                        'used-for': z.string(),
                                    })
                                    .partial(),
                                realtime: z
                                    .object({
                                        name: z.string(),
                                        'used-for': z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        status: z
                            .object({
                                blocked: z.string(),
                                checking: z.string(),
                                reachable: z.string(),
                            })
                            .partial(),
                        table: z
                            .object({
                                'aria-label': z.string(),
                                domain: z.string(),
                                service: z.string(),
                                status: z.string(),
                                'used-for': z.string(),
                            })
                            .partial(),
                        title: z.string(),
                    })
                    .partial(),
                'company-home': z
                    .object({
                        chat: z
                            .object({
                                hide: z.string(),
                                resume: z.string(),
                            })
                            .partial(),
                        journey: z
                            .object({
                                'architecture-name': z.string(),
                                asked: z.string(),
                                'board-label': z.string(),
                                chips: z
                                    .object({
                                        done: z.string(),
                                        failed: z.string(),
                                        'in-progress': z.string(),
                                        'needs-job-architecture': z.string(),
                                        'needs-linked-positions': z.string(),
                                        next: z.string(),
                                        'not-started': z.string(),
                                        running: z.string(),
                                        skipped: z.string(),
                                        waiting: z.string(),
                                    })
                                    .partial(),
                                composer: z
                                    .object({
                                        placeholder: z.string(),
                                        reply: z.string(),
                                    })
                                    .partial(),
                                connect: z.string(),
                                'connect-hint': z.string(),
                                facts: z
                                    .object({
                                        reading: z.string(),
                                        reading_one: z.string(),
                                        unreadable: z.string(),
                                        unreadable_one: z.string(),
                                        updated: z.string(),
                                    })
                                    .partial(),
                                figures: z
                                    .object({
                                        'awaiting-review': z.string(),
                                        'awaiting-review_one': z.string(),
                                        matching: z.string(),
                                        'my-reviews': z.string(),
                                        'my-reviews_one': z.string(),
                                        suggestions: z.string(),
                                        suggestions_one: z.string(),
                                    })
                                    .partial(),
                                greeting: z
                                    .object({
                                        afternoon: z.string(),
                                        'afternoon-without-name': z.string(),
                                        evening: z.string(),
                                        'evening-without-name': z.string(),
                                        'first-visit': z.string(),
                                        morning: z.string(),
                                        'morning-without-name': z.string(),
                                    })
                                    .partial(),
                                headline: z
                                    .object({
                                        'ai-transformation': z.string(),
                                        assessing: z.string(),
                                        assessing_one: z.string(),
                                        documents: z.string(),
                                        documents_one: z.string(),
                                        'job-architecture-failed': z.string(),
                                        'job-architecture-generating': z.string(),
                                        learning: z.string(),
                                        learning_one: z.string(),
                                        'level-links': z.string(),
                                        'level-matching': z.string(),
                                        'levels-generating': z.string(),
                                        'levels-in-review': z.string(),
                                        'levels-in-review_one': z.string(),
                                        'levels-need-reviewers': z.string(),
                                        'levels-need-reviewers_one': z.string(),
                                        'levels-to-publish': z.string(),
                                        'levels-to-publish_one': z.string(),
                                        'no-documents': z.string(),
                                        'no-job-architecture': z.string(),
                                        'no-positions': z.string(),
                                        'no-roles': z.string(),
                                        'not-assessed': z.string(),
                                        'not-connected': z.string(),
                                        people: z.string(),
                                        people_one: z.string(),
                                        positions: z.string(),
                                        'positions-failed': z.string(),
                                        'positions-generating': z.string(),
                                        positions_one: z.string(),
                                        'roles-generating': z.string(),
                                        'roles-in-review': z.string(),
                                        'roles-in-review_one': z.string(),
                                        'roles-need-reviewers': z.string(),
                                        'roles-need-reviewers_one': z.string(),
                                        'roles-published': z.string(),
                                        'roles-to-publish': z.string(),
                                        'roles-to-publish_one': z.string(),
                                        setup: z.string(),
                                    })
                                    .partial(),
                                locked: z
                                    .object({
                                        'ai-transformation': z.string(),
                                        'job-architecture': z.string(),
                                    })
                                    .partial(),
                                milestones: z
                                    .object({
                                        defined: z.string(),
                                        'job-architecture': z.string(),
                                        links: z.string(),
                                        'not-connected': z.string(),
                                        of: z.string(),
                                        positions: z.string(),
                                        prioritized: z.string(),
                                        publication: z.string(),
                                        setup: z.string(),
                                    })
                                    .partial(),
                                'open-page': z.string(),
                                opens: z.string(),
                                passing: z
                                    .object({
                                        'documents-for-setup': z.string(),
                                        'documents-reading': z.string(),
                                        'documents-reading-positions': z.string(),
                                        'job-architecture-generating': z.string(),
                                        'level-matching': z.string(),
                                        'positions-generating': z.string(),
                                        'reviews-pending': z.string(),
                                        'reviews-pending-guide': z.string(),
                                        'reviews-pending-links': z.string(),
                                    })
                                    .partial(),
                                'prompt-busy': z.string(),
                                'prompt-hint': z.string(),
                                skip: z.string(),
                                skipped: z.string(),
                                status: z
                                    .object({
                                        action: z.string(),
                                        failed: z.string(),
                                        none: z.string(),
                                        'not-started': z.string(),
                                        waiting: z.string(),
                                    })
                                    .partial(),
                                'step-of': z.string(),
                                steps: z
                                    .object({
                                        'add-level-tasks': z
                                            .object({
                                                label: z.string(),
                                            })
                                            .partial(),
                                        'assign-level-reviewers': z
                                            .object({
                                                prompt: z.string(),
                                                prompt_one: z.string(),
                                            })
                                            .partial(),
                                        'assign-reviewers': z
                                            .object({
                                                ask: z.string(),
                                                label: z.string(),
                                            })
                                            .partial(),
                                        'assign-role-reviewers': z
                                            .object({
                                                prompt: z.string(),
                                                prompt_one: z.string(),
                                            })
                                            .partial(),
                                        'connect-job-architecture': z
                                            .object({
                                                label: z.string(),
                                            })
                                            .partial(),
                                        'continue-position-setup': z
                                            .object({
                                                label: z.string(),
                                            })
                                            .partial(),
                                        'create-job-architecture': z
                                            .object({
                                                label: z.string(),
                                            })
                                            .partial(),
                                        'create-levels': z
                                            .object({
                                                ask: z.string(),
                                                label: z.string(),
                                                prompt: z.string(),
                                                prompt_one: z.string(),
                                            })
                                            .partial(),
                                        'create-position-architecture': z
                                            .object({
                                                label: z.string(),
                                            })
                                            .partial(),
                                        'create-roles': z
                                            .object({
                                                ask: z.string(),
                                                label: z.string(),
                                                prompt: z.string(),
                                            })
                                            .partial(),
                                        define: z
                                            .object({
                                                label: z.string(),
                                            })
                                            .partial(),
                                        'finish-ja-setup': z
                                            .object({
                                                label: z.string(),
                                            })
                                            .partial(),
                                        follow: z
                                            .object({
                                                label: z.string(),
                                            })
                                            .partial(),
                                        'ja-generation-failed': z
                                            .object({
                                                label: z.string(),
                                            })
                                            .partial(),
                                        'link-positions': z
                                            .object({
                                                label: z.string(),
                                            })
                                            .partial(),
                                        prioritize: z
                                            .object({
                                                label: z.string(),
                                            })
                                            .partial(),
                                        publish: z
                                            .object({
                                                ask: z.string(),
                                                label: z.string(),
                                            })
                                            .partial(),
                                        'publish-levels': z
                                            .object({
                                                prompt: z.string(),
                                                prompt_one: z.string(),
                                            })
                                            .partial(),
                                        'publish-roles': z
                                            .object({
                                                prompt: z.string(),
                                                prompt_one: z.string(),
                                            })
                                            .partial(),
                                        'restart-level-mapping': z
                                            .object({
                                                label: z.string(),
                                            })
                                            .partial(),
                                        'retry-link-apply': z
                                            .object({
                                                label: z.string(),
                                            })
                                            .partial(),
                                        'retry-position-plan': z
                                            .object({
                                                label: z.string(),
                                            })
                                            .partial(),
                                        'review-link-suggestions': z
                                            .object({
                                                label: z.string(),
                                            })
                                            .partial(),
                                        'review-mine': z
                                            .object({
                                                ask: z.string(),
                                                label: z.string(),
                                            })
                                            .partial(),
                                        'review-mine-levels': z
                                            .object({
                                                prompt: z.string(),
                                                prompt_one: z.string(),
                                            })
                                            .partial(),
                                        'review-mine-roles': z
                                            .object({
                                                prompt: z.string(),
                                                prompt_one: z.string(),
                                            })
                                            .partial(),
                                        'upload-documents': z
                                            .object({
                                                label: z.string(),
                                            })
                                            .partial(),
                                    })
                                    .partial(),
                                'steps-toggle': z.string(),
                                'strip-label': z.string(),
                                subjects: z
                                    .object({
                                        'knowledge-base': z.string(),
                                        'talent-intelligence': z.string(),
                                        'work-architecture': z.string(),
                                    })
                                    .partial(),
                                summary: z
                                    .object({
                                        ask: z.string(),
                                        prompt: z.string(),
                                    })
                                    .partial(),
                                undo: z.string(),
                                why: z
                                    .object({
                                        'add-level-tasks': z.string(),
                                        'assign-reviewers': z.string(),
                                        'connect-job-architecture': z.string(),
                                        'continue-position-setup': z.string(),
                                        'create-job-architecture': z.string(),
                                        'create-levels': z.string(),
                                        'create-position-architecture': z.string(),
                                        'create-roles': z.string(),
                                        define: z.string(),
                                        'finish-ja-setup': z.string(),
                                        hint: z.string(),
                                        'link-positions': z.string(),
                                        prioritize: z.string(),
                                        prompt: z.string(),
                                        publish: z.string(),
                                        'review-link-suggestions': z.string(),
                                        'review-mine': z.string(),
                                        'setup-documents': z.string(),
                                        'upload-documents': z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        subtitle: z.string(),
                        tabs: z
                            .object({
                                'knowledge-base': z.string(),
                            })
                            .partial(),
                        'work-architecture': z
                            .object({
                                'published-of': z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                'company-settings': z
                    .object({
                        'company-goals': z
                            .object({
                                add: z.string(),
                                delete: z.string(),
                                'delete-description': z.string(),
                                'delete-failed': z.string(),
                                'delete-title': z.string(),
                                description: z.string(),
                                'description-placeholder': z.string(),
                                edit: z.string(),
                                empty: z.string(),
                                keep: z.string(),
                                'load-failed': z.string(),
                                'no-description': z.string(),
                                retry: z.string(),
                                'save-failed': z.string(),
                                title: z.string(),
                                'title-placeholder': z.string(),
                            })
                            .partial(),
                        'danger-zone': z
                            .object({
                                'delete-button': z.string(),
                                'delete-card-description': z.string(),
                                'delete-card-title': z.string(),
                                'delete-dialog-description': z.string(),
                                'delete-dialog-title': z.string(),
                                subtitle: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'deleted-successfully': z.string(),
                        description: z.string(),
                        'discard-changes-description': z.string(),
                        'discard-changes-description-leave': z.string(),
                        'discard-changes-title': z.string(),
                        'failed-to-delete': z.string(),
                        'failed-to-save': z.string(),
                        fields: z
                            .object({
                                'company-description': z
                                    .object({
                                        empty: z.string(),
                                        label: z.string(),
                                        placeholder: z.string(),
                                    })
                                    .partial(),
                                'company-name': z
                                    .object({
                                        empty: z.string(),
                                        label: z.string(),
                                        placeholder: z.string(),
                                        required: z.string(),
                                    })
                                    .partial(),
                                'company-size': z
                                    .object({
                                        empty: z.string(),
                                        label: z.string(),
                                        options: z
                                            .object({
                                                gigantic: z.string(),
                                                huge: z.string(),
                                                large: z.string(),
                                                medium: z.string(),
                                                small: z.string(),
                                            })
                                            .partial(),
                                        placeholder: z.string(),
                                        required: z.string(),
                                    })
                                    .partial(),
                                'job-architecture-name': z
                                    .object({
                                        empty: z.string(),
                                        label: z.string(),
                                        placeholder: z.string(),
                                        required: z.string(),
                                    })
                                    .partial(),
                                jsa: z
                                    .object({
                                        placeholder: z.string(),
                                    })
                                    .partial(),
                                'organization-description': z
                                    .object({
                                        empty: z.string(),
                                        label: z.string(),
                                        placeholder: z.string(),
                                    })
                                    .partial(),
                                'organization-name': z
                                    .object({
                                        empty: z.string(),
                                        label: z.string(),
                                        placeholder: z.string(),
                                        required: z.string(),
                                    })
                                    .partial(),
                                'organization-size': z
                                    .object({
                                        empty: z.string(),
                                        label: z.string(),
                                        placeholder: z.string(),
                                        required: z.string(),
                                    })
                                    .partial(),
                                'top-priorities': z
                                    .object({
                                        empty: z.string(),
                                        label: z.string(),
                                        placeholder: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        'job-architecture-description': z.string(),
                        'job-architecture-title': z.string(),
                        'organization-description': z.string(),
                        'organization-title': z.string(),
                        'jsa-terminology': z
                            .object({
                                description: z.string(),
                                plural: z.string(),
                                singular: z.string(),
                            })
                            .partial(),
                        'keep-editing': z.string(),
                        onboarding: z
                            .object({
                                'invite-users': z
                                    .object({
                                        'complete-button': z.string(),
                                        description: z.string(),
                                        'invite-users-link': z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        'saved-successfully': z.string(),
                        'skill-change-events': z
                            .object({
                                'affected-users': z.string(),
                                'affected-users_one': z.string(),
                                causes: z
                                    .object({
                                        'bulk-removal': z.string(),
                                        manual: z.string(),
                                        'skill-archived': z.string(),
                                        'skill-deleted': z.string(),
                                        'skill-replaced': z.string(),
                                        unknown: z.string(),
                                    })
                                    .partial(),
                                'change-types': z
                                    .object({
                                        archived: z.string(),
                                        deleted: z.string(),
                                        removed: z.string(),
                                        replaced: z.string(),
                                        'skill-type-updated': z.string(),
                                        'title-updated': z.string(),
                                        unknown: z.string(),
                                    })
                                    .partial(),
                                columns: z
                                    .object({
                                        actor: z.string(),
                                        affected: z.string(),
                                        change: z.string(),
                                        date: z.string(),
                                        scope: z.string(),
                                        skill: z.string(),
                                    })
                                    .partial(),
                                description: z.string(),
                                empty: z.string(),
                                'empty-in-range': z.string(),
                                'entity-types': z
                                    .object({
                                        'learning-opportunity': z.string(),
                                        level: z.string(),
                                        role: z.string(),
                                        task: z.string(),
                                        user: z.string(),
                                    })
                                    .partial(),
                                export: z
                                    .object({
                                        button: z.string(),
                                        description: z.string(),
                                        from: z.string(),
                                        pending: z.string(),
                                        submit: z.string(),
                                        title: z.string(),
                                        to: z.string(),
                                    })
                                    .partial(),
                                'export-file': z
                                    .object({
                                        columns: z
                                            .object({
                                                actor: z.string(),
                                                'affected-count': z.string(),
                                                cause: z.string(),
                                                'date-utc': z.string(),
                                                entity: z.string(),
                                                'entity-type': z.string(),
                                                'replaced-by-skill': z.string(),
                                                'replaced-by-skill-id': z.string(),
                                                'skill-id': z.string(),
                                                version: z.string(),
                                            })
                                            .partial(),
                                        'deleted-actor': z.string(),
                                        'sheet-name': z.string(),
                                        'system-actor': z.string(),
                                    })
                                    .partial(),
                                'load-error': z.string(),
                                scopes: z
                                    .object({
                                        global: z.string(),
                                        tenant: z.string(),
                                        unknown: z.string(),
                                    })
                                    .partial(),
                                'time-range': z
                                    .object({
                                        custom: z.string(),
                                        label: z.string(),
                                        presets: z
                                            .object({
                                                '15d': z.string(),
                                                '15m': z.string(),
                                                '1d': z.string(),
                                                '1h': z.string(),
                                                '1mo': z.string(),
                                                '2d': z.string(),
                                                '3d': z.string(),
                                                '4h': z.string(),
                                                '7d': z.string(),
                                                all: z.string(),
                                            })
                                            .partial(),
                                        'trigger-label': z.string(),
                                    })
                                    .partial(),
                                title: z.string(),
                                version: z
                                    .object({
                                        draft: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        skills: z
                            .object({
                                'confirm-dialog': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'non-permissive': z
                                    .object({
                                        'best-for': z.string(),
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                permissive: z
                                    .object({
                                        'best-for': z.string(),
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'short-descriptions': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'skill-catalog': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'expandable-descriptions': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'role-skill-proficiency': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'warning-signals': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        title: z.string(),
                        'users-and-roles': z
                            .object({
                                'bulk-actions': z
                                    .object({
                                        delete: z.string(),
                                        'reset-password': z.string(),
                                    })
                                    .partial(),
                                'change-role-dialog': z
                                    .object({
                                        'hr-talent': z.string(),
                                        'hr-talent-info': z.string(),
                                        'permissions-info': z.string(),
                                        roles: z.string(),
                                        'save-button': z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'confirm-delete': z
                                    .object({
                                        button: z.string(),
                                        'description-plural': z.string(),
                                        'description-singular': z.string(),
                                        'more-hidden': z.string(),
                                        'title-plural': z.string(),
                                        'title-singular': z.string(),
                                    })
                                    .partial(),
                                description: z.string(),
                                errors: z
                                    .object({
                                        'delete-failed': z.string(),
                                        'invite-failed': z.string(),
                                        'reset-password-failed': z.string(),
                                        'some-delete-failed': z.string(),
                                        'update-role-failed': z.string(),
                                    })
                                    .partial(),
                                'invite-dialog': z
                                    .object({
                                        'assign-review-title': z.string(),
                                        description: z.string(),
                                        email: z.string(),
                                        'email-placeholder': z.string(),
                                        'first-name': z.string(),
                                        'first-name-placeholder': z.string(),
                                        'invite-button': z.string(),
                                        language: z.string(),
                                        'last-name': z.string(),
                                        'last-name-placeholder': z.string(),
                                        method: z.string(),
                                        'method-email': z.string(),
                                        'method-sso': z.string(),
                                        role: z.string(),
                                        roles: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'invite-button': z.string(),
                                roles: z
                                    .object({
                                        'admin-job-architect': z.string(),
                                        hiring_manager: z.string(),
                                        hr_talent: z.string(),
                                        'job-architect': z.string(),
                                        job_architect: z.string(),
                                        manager: z.string(),
                                        recruiter: z.string(),
                                        reviewer: z.string(),
                                        talent_developer: z.string(),
                                        transformation_lead: z.string(),
                                        user: z.string(),
                                        viewer: z.string(),
                                    })
                                    .partial(),
                                'row-actions': z
                                    .object({
                                        'change-role': z.string(),
                                        'change-role-sso-disabled': z.string(),
                                        'position-areas': z.string(),
                                        'position-areas-invited': z.string(),
                                        'position-areas-needs-hr-talent': z.string(),
                                        'remove-user': z.string(),
                                    })
                                    .partial(),
                                success: z
                                    .object({
                                        'invite-sent': z.string(),
                                        'reset-password-sent': z.string(),
                                        'role-updated': z.string(),
                                        'users-deleted': z.string(),
                                    })
                                    .partial(),
                                table: z
                                    .object({
                                        actions: z.string(),
                                        active: z.string(),
                                        authentication: z.string(),
                                        'authentication-types': z
                                            .object({
                                                password: z.string(),
                                                sso: z.string(),
                                            })
                                            .partial(),
                                        invited: z.string(),
                                        'last-signed-in': z.string(),
                                        name: z.string(),
                                        'roles-and-permissions': z.string(),
                                        'search-placeholder': z.string(),
                                        'selected-count': z.string(),
                                        status: z.string(),
                                    })
                                    .partial(),
                                title: z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                'cv-reimport': z
                    .object({
                        headline: z.string(),
                        preview: z
                            .object({
                                description: z.string(),
                                save: z.string(),
                                'sub-headline': z.string(),
                            })
                            .partial(),
                        result: z
                            .object({
                                continue: z.string(),
                                'found-items': z.string(),
                                'merge-profile': z.string(),
                                'override-profile': z.string(),
                                prompt: z.string(),
                                'sub-headline': z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                dashboard: z
                    .object({
                        ambitions: z
                            .object({
                                'add-selected-ambitions-as-my-ambitions': z.string(),
                                'edit-button-label': z.string(),
                                'info-box': z
                                    .object({
                                        'first-paragraph': z.string(),
                                        'no-ambitions-button-label': z.string(),
                                        'no-ambitions-first-paragraph': z.string(),
                                        'no-ambitions-second-paragraph': z.string(),
                                        'second-paragraph': z.string(),
                                    })
                                    .partial(),
                                'see-courses': z.string(),
                                'tab-label': z.string(),
                            })
                            .partial(),
                        'dashboard-card': z
                            .object({
                                'matching-score': z.string(),
                                new: z.string(),
                                'relevant-missing-skill': z.string(),
                                'relevant-skill': z.string(),
                            })
                            .partial(),
                        'featured-learning-opportunities': z
                            .object({
                                subtitle: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        headline: z.string(),
                        'job-recommendations': z
                            .object({
                                filters: z
                                    .object({
                                        'sub-title': z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                headline: z.string(),
                                'headline-short': z.string(),
                                'job-card': z
                                    .object({
                                        type: z.string(),
                                    })
                                    .partial(),
                                'no-jobs': z
                                    .object({
                                        'no-recommendations': z
                                            .object({
                                                headline: z.string(),
                                                link: z.string(),
                                                text: z.string(),
                                            })
                                            .partial(),
                                        'not-enough-skills': z
                                            .object({
                                                headline: z.string(),
                                                link: z.string(),
                                                text: z.string(),
                                            })
                                            .partial(),
                                    })
                                    .partial(),
                                'show-more': z.string(),
                                'skills-based-sub-headline': z.string(),
                                'sub-headline': z.string(),
                            })
                            .partial(),
                        'learning-opportunity-recommendations': z
                            .object({
                                'empty-results': z.string(),
                                headline: z.string(),
                                'show-more': z.string(),
                                'sub-headline': z.string(),
                                subtitle: z.string(),
                                title: z.string(),
                                'view-all': z.string(),
                            })
                            .partial(),
                        'welcome-back': z.string(),
                    })
                    .partial(),
                'denied-page': z
                    .object({
                        subtitle: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                documentation: z
                    .object({
                        'ai-agents-hr': z
                            .object({
                                'ai-systems-hr-deepdive': documentationArticleSchema,
                                'chatbots-vs-agents': documentationArticleSchema,
                                'connect-cobrainer-mcp-to-microsoft-365-copilot': documentationArticleSchema,
                                'hr-processes-agent-automation': documentationArticleSchema,
                                'mcp-model-context-protocol': documentationArticleSchema,
                                'mcp-servers-hr-space': documentationArticleSchema,
                                'skill-files-cobrainer-ecosystem': documentationArticleSchema,
                                subtitle: z.string(),
                                title: z.string(),
                                'what-is-a-tool-call': documentationArticleSchema,
                            })
                            .partial(),
                        'ai-impact-on-jobs': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'ai-transformation': z
                            .object({
                                'ai-impact-on-jobs': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'human-ai-hybrid-organizations': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'skills-mapping-headcount-courses': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                subtitle: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'article-4-gender-neutral-pay': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'article-6-equal-work': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'back-to-dashboard': z.string(),
                        'back-to-documentation': z.string(),
                        'best-practices-leveling': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'connect-cobrainer-mcp-to-microsoft-365-copilot': documentationArticleSchema,
                        'eu-pay-transparency-directive': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'foundational-concepts': z
                            .object({
                                'great-job-role-description': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'job-families-clusters-roles-levels': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'job-role-templates': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'risk-categories-compliance-equal-pay': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'skills-competency-clusters-proficiency': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                subtitle: z.string(),
                                title: z.string(),
                                'what-is-job-architecture': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        'get-started': z
                            .object({
                                title: z.string(),
                            })
                            .partial(),
                        glossary: z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'grading-frameworks': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'grading-leveling': z
                            .object({
                                'best-practices-leveling': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'grading-frameworks': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'pay-bands-career-tracks': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                subtitle: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'great-job-role-description': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'hero-subtitle': z.string(),
                        'hero-title': z.string(),
                        'human-ai-hybrid-organizations': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'implementation-governance': z
                            .object({
                                'implementation-guide': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'measurement-kpis': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'resources-templates-integration': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                subtitle: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'implementation-guide': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'international-pay-transparency': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        introduction: z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'job-families-clusters-roles-levels': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'job-role-templates': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'landing-subtitle': z.string(),
                        'landing-title': z.string(),
                        'measurement-kpis': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'pay-bands-career-tracks': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'pay-transparency': z
                            .object({
                                'article-4-gender-neutral-pay': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'article-6-equal-work': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'eu-pay-transparency-directive': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'international-pay-transparency': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                subtitle: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'performance-reviews-skills-architecture': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'quick-start': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'resources-templates-integration': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'risk-categories-compliance-equal-pay': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'skills-based': z
                            .object({
                                'performance-reviews-skills-architecture': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'skills-drive-talent-processes': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                subtitle: z.string(),
                                title: z.string(),
                                'transitioning-skills-based-org': z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        'skills-competency-clusters-proficiency': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'skills-drive-talent-processes': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'skills-mapping-headcount-courses': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        subtitle: z.string(),
                        title: z.string(),
                        'transitioning-skills-based-org': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'what-is-job-architecture': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                'edit-about-me': z
                    .object({
                        'no-about': z.string(),
                        placeholder: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'edit-contact-details': z
                    .object({
                        email: z.string(),
                        phone: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'edit-personal-info': z
                    .object({
                        'job-seeking': z.string(),
                        'relocation-preference': z.string(),
                        'seniority-level': z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'edit-profile-details': z
                    .object({
                        department: z.string(),
                        'job-title': z.string(),
                        location: z.string(),
                        phone: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                feedback: z
                    .object({
                        'change-skill-language': z.string(),
                        'change-skill-reason': z.string(),
                        'change-skill-reason-placeholder': z.string(),
                        'create-skill-placeholder': z.string(),
                        'create-skill-skill-type': z.string(),
                        'feedback-description': z.string(),
                        'provide-feedback-description': z.string(),
                        'provide-feedback-title': z.string(),
                        'report-content-issue': z.string(),
                        'report-skill': z.string(),
                        'report-skill-reason': z.string(),
                        'report-skill-reason-placeholder': z.string(),
                        'request-new-skill': z.string(),
                        'request-new-skill-title-placeholder': z.string(),
                        'send-error': z.string(),
                        'skill-content-report-success-message': z.string(),
                        'skill-content-report-success-title': z.string(),
                        'skill-report-success-message': z.string(),
                        'skill-report-success-title': z.string(),
                        'skill-request-success-message': z.string(),
                        'skill-request-success-title': z.string(),
                        'submit-feedback': z.string(),
                    })
                    .partial(),
                'graph-canvas': z
                    .object({
                        added: z
                            .object({
                                copy: z.string(),
                                empty: z.string(),
                                expanded: z.string(),
                                'not-found': z.string(),
                                remove: z.string(),
                                shown: z.string(),
                            })
                            .partial(),
                        architecture: z
                            .object({
                                'add-all': z.string(),
                                empty: z.string(),
                                'no-families': z.string(),
                                placeholder: z.string(),
                                toggle: z.string(),
                                select: z.string(),
                            })
                            .partial(),
                        catalog: z
                            .object({
                                add: z.string(),
                                next: z.string(),
                                'no-results': z.string(),
                                page: z.string(),
                                previous: z.string(),
                                random: z.string(),
                                'search-jobs': z.string(),
                                'search-skills': z.string(),
                                'search-users': z.string(),
                                'type-to-search': z.string(),
                            })
                            .partial(),
                        categories: z
                            .object({
                                architecture: z.string(),
                                job: z.string(),
                                skill: z.string(),
                                user: z.string(),
                            })
                            .partial(),
                        connection: z
                            .object({
                                clear: z.string(),
                                common: z.string(),
                                direct: z.string(),
                                loading: z.string(),
                                none: z.string(),
                                picking: z.string(),
                                via: z.string(),
                            })
                            .partial(),
                        empty: z.string(),
                        gizmo: z
                            .object({
                                label: z.string(),
                                'look-from': z.string(),
                            })
                            .partial(),
                        hint: z.string(),
                        'hint-connection': z.string(),
                        'hint-selection': z.string(),
                        kinds: z
                            .object({
                                architecture: z.string(),
                                cluster: z.string(),
                                family: z.string(),
                                'hard-skill': z.string(),
                                job: z.string(),
                                'language-skill': z.string(),
                                level: z.string(),
                                role: z.string(),
                                'soft-skill': z.string(),
                                user: z.string(),
                            })
                            .partial(),
                        legend: z
                            .object({
                                labels: z.string(),
                                visible: z.string(),
                            })
                            .partial(),
                        menu: z
                            .object({
                                'connect-to': z.string(),
                                'connect-with': z.string(),
                                expand: z.string(),
                                highlight: z.string(),
                                open: z.string(),
                                'open-in-tile': z.string(),
                                'open-in-tile-unavailable': z.string(),
                                remove: z.string(),
                                'remove-key': z.string(),
                                'remove-loose-ends': z.string(),
                            })
                            .partial(),
                        settings: z
                            .object({
                                'expand-profile-skills': z.string(),
                                fade: z.string(),
                                'flow-dots': z.string(),
                                labels: z.string(),
                                'node-limit': z.string(),
                                'random-count': z.string(),
                                'related-skills': z.string(),
                                'rotation-speed': z.string(),
                                'show-structure': z.string(),
                                'show-two-hops': z.string(),
                                visibility: z.string(),
                            })
                            .partial(),
                        status: z
                            .object({
                                failed: z.string(),
                                limit: z.string(),
                                loading: z.string(),
                                'out-of-view': z.string(),
                            })
                            .partial(),
                        title: z.string(),
                        toolbar: z
                            .object({
                                'add-architecture': z.string(),
                                'add-jobs': z.string(),
                                'add-skills': z.string(),
                                'add-users': z.string(),
                                added: z.string(),
                                clear: z.string(),
                                recenter: z.string(),
                                rotate: z.string(),
                                settings: z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                'global-search': z
                    .object({
                        headlines: z
                            .object({
                                courses: z.string(),
                                created: z.string(),
                                'employee-name': z.string(),
                                'employee-skills': z.string(),
                                employees: z.string(),
                                'job-architecture': z.string(),
                                'job-cluster': z.string(),
                                'job-family': z.string(),
                                'job-level': z.string(),
                                'job-postings': z.string(),
                                'job-role': z.string(),
                                jobs: z.string(),
                                'learning-opportunities': z.string(),
                                published: z.string(),
                                'required-skills': z.string(),
                                skill: z.string(),
                                'skill-gap-relative': z.string(),
                                'trained-skills': z.string(),
                            })
                            .partial(),
                        navigation: z
                            .object({
                                employees: z.string(),
                                'job-levels': z.string(),
                                'job-openings': z.string(),
                                'job-roles': z.string(),
                                'learning-opportunities': z.string(),
                                skills: z.string(),
                            })
                            .partial(),
                        'no-data': z
                            .object({
                                employees: z.string(),
                                'job-levels': z.string(),
                                'job-openings': z.string(),
                                'job-roles': z.string(),
                                'learning-opportunities': z.string(),
                                skills: z.string(),
                            })
                            .partial(),
                        placeholder: z.string(),
                        'search-headline': z.string(),
                    })
                    .partial(),
                growth: z
                    .object({
                        actions: z
                            .object({
                                agree: z.string(),
                                archive: z.string(),
                                'archive-confirm': z.string(),
                                'archive-description': z.string(),
                                'archive-title': z.string(),
                                'ask-agent': z.string(),
                                comment: z.string(),
                                'confirm-outcome': z.string(),
                                extend: z.string(),
                                'keep-private': z.string(),
                                'mark-done': z.string(),
                                'more-actions': z.string(),
                                'new-goal': z.string(),
                                'new-goal-manually': z.string(),
                                'new-goal-options': z.string(),
                                'new-goal-with-agent': z.string(),
                                'propose-outcome': z.string(),
                                'send-back': z.string(),
                                'share-with-manager': z.string(),
                                'write-with-agent': z.string(),
                            })
                            .partial(),
                        checkpoint: z
                            .object({
                                'check-in': z.string(),
                                done: z.string(),
                                due: z.string(),
                                empty: z.string(),
                                'mark-label': z.string(),
                                next: z.string(),
                                'note-label': z.string(),
                                'note-placeholder': z.string(),
                                overdue: z.string(),
                                title: z.string(),
                                upcoming: z.string(),
                                update: z.string(),
                                'value-label': z.string(),
                            })
                            .partial(),
                        'checkpoint-marks': z
                            .object({
                                changed: z.string(),
                                'needs-support': z.string(),
                                'on-track': z.string(),
                            })
                            .partial(),
                        comments: z
                            .object({
                                empty: z.string(),
                                placeholder: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        detail: z
                            .object({
                                description: z.string(),
                                'learning-hint': z.string(),
                                'learning-take': z.string(),
                                'learning-taken': z.string(),
                                measure: z.string(),
                                'no-due': z.string(),
                                'no-measure': z.string(),
                                'not-done-yet': z.string(),
                                'outcome-note-placeholder': z.string(),
                                'outcome-proposed': z.string(),
                                'outcome-title': z.string(),
                                'pays-into': z.string(),
                                progress: z.string(),
                                returned: z.string(),
                                'send-back-placeholder': z.string(),
                                'started-at': z.string(),
                            })
                            .partial(),
                        empty: z
                            .object({
                                'my-goals': z.string(),
                                'no-shared': z.string(),
                                waiting: z.string(),
                            })
                            .partial(),
                        form: z
                            .object({
                                'description-label': z.string(),
                                'description-placeholder': z.string(),
                                'due-label': z.string(),
                                guidance: z
                                    .object({
                                        how: z.string(),
                                        what: z.string(),
                                        when: z.string(),
                                        why: z.string(),
                                    })
                                    .partial(),
                                'guidance-footnote': z.string(),
                                'guidance-title': z.string(),
                                'measure-label': z.string(),
                                'no-company-goal': z.string(),
                                'number-start': z.string(),
                                'number-target': z.string(),
                                'number-unit': z.string(),
                                'pays-into-label': z.string(),
                                'title-label': z.string(),
                                'title-placeholder': z.string(),
                            })
                            .partial(),
                        'load-error': z.string(),
                        'measure-help': z
                            .object({
                                done: z
                                    .object({
                                        description: z.string(),
                                        example: z.string(),
                                    })
                                    .partial(),
                                'example-prefix': z.string(),
                                none: z
                                    .object({
                                        description: z.string(),
                                        example: z.string(),
                                    })
                                    .partial(),
                                number: z
                                    .object({
                                        description: z.string(),
                                        example: z.string(),
                                        'unit-placeholder': z.string(),
                                    })
                                    .partial(),
                                percent: z
                                    .object({
                                        description: z.string(),
                                        example: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        'measure-types': z
                            .object({
                                done: z.string(),
                                milestones: z.string(),
                                none: z.string(),
                                number: z.string(),
                                percent: z.string(),
                            })
                            .partial(),
                        'measure-values': z
                            .object({
                                milestones: z.string(),
                                'milestones-total': z.string(),
                                'milestones-total_one': z.string(),
                                milestones_one: z.string(),
                                number: z.string(),
                                percent: z.string(),
                            })
                            .partial(),
                        onboarding: z
                            .object({
                                'company-subtitle': z.string(),
                                'company-title': z.string(),
                                'continue-editing': z.string(),
                                'create-own': z.string(),
                                done: z.string(),
                                edit: z.string(),
                                edited: z.string(),
                                editing: z.string(),
                                'example-body': z.string(),
                                'example-label': z.string(),
                                'example-title': z.string(),
                                generating: z.string(),
                                'generation-failed': z.string(),
                                'good-goals-subtitle': z.string(),
                                'good-goals-title': z.string(),
                                intro: z.string(),
                                'kept-private': z.string(),
                                next: z.string(),
                                'no-company-goals': z.string(),
                                'own-growth': z.string(),
                                'recommended-hint': z.string(),
                                'recommended-title': z.string(),
                                retry: z.string(),
                                send: z.string(),
                                sent: z.string(),
                                start: z.string(),
                                steps: z
                                    .object({
                                        company: z.string(),
                                        'good-goals': z.string(),
                                        suggestions: z.string(),
                                    })
                                    .partial(),
                                'suggestions-subtitle': z.string(),
                                title: z.string(),
                                'tracking-label': z.string(),
                            })
                            .partial(),
                        organization: z
                            .object({
                                'all-teams': z.string(),
                                'filter-label': z.string(),
                                lead: z.string(),
                                'people-count': z.string(),
                                'people-count_one': z.string(),
                                'team-count': z.string(),
                                'team-count_one': z.string(),
                            })
                            .partial(),
                        outcomes: z
                            .object({
                                dropped: z.string(),
                                partly: z.string(),
                                reached: z.string(),
                            })
                            .partial(),
                        retry: z.string(),
                        sections: z
                            .object({
                                'change-notice': z.string(),
                                done: z.string(),
                                'needs-you': z.string(),
                                open: z.string(),
                                private: z.string(),
                                team: z.string(),
                                waiting: z.string(),
                            })
                            .partial(),
                        states: z
                            .object({
                                agreed: z.string(),
                                done: z.string(),
                                draft: z.string(),
                                'in-review': z.string(),
                                private: z.string(),
                                returned: z.string(),
                                stale: z.string(),
                                waiting: z.string(),
                            })
                            .partial(),
                        tabs: z
                            .object({
                                'all-teams': z.string(),
                                'my-goals': z.string(),
                                'my-team': z.string(),
                            })
                            .partial(),
                        team: z
                            .object({
                                'without-goals': z.string(),
                                'without-goals_one': z.string(),
                            })
                            .partial(),
                        title: z.string(),
                        unavailable: z
                            .object({
                                body: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'waiting-kinds': z
                            .object({
                                agreement: z.string(),
                                'needs-support': z.string(),
                                outcome: z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                'dashboard-v2': z
                    .object({
                        'architecture-status': z
                            .object({
                                'backend-api-missing': z.string(),
                                buckets: z.record(z.string(), z.string()),
                                'fallback-name': z.string(),
                                'histogram-title': z.string(),
                                'histogram-tooltip': z.string(),
                                kpis: z.record(z.string(), z.string()),
                                levels: z.string(),
                                'monthly-edits': z.string(),
                                'monthly-edits-breakdown': z.record(z.string(), z.string()),
                                'monthly-edits-missing': z.string(),
                                roles: z.string(),
                                'summary-aria': z.string(),
                                title: z.string(),
                                tooltips: z.record(z.string(), z.string()),
                                total: z.string(),
                                'value-derived-from-prototype': z.string(),
                            })
                            .partial(),
                        changelog: z
                            .object({
                                empty: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'graph-stats': z
                            .object({
                                'aria-label': z.string(),
                                'job-roles': z.string(),
                                'published-roles': z.string(),
                                'unreviewed-roles': z.string(),
                            })
                            .partial(),
                        guide: z
                            .object({
                                'error-description': z.string(),
                                'error-title': z.string(),
                                'no-architecture': z.string(),
                                'publication-assign-role-reviewers-description': z.string(),
                                'publication-description': z.string(),
                                'publication-invite-users-description': z.string(),
                                'publication-invite-users-title': z.string(),
                                'publication-review-publish-roles-title': z.string(),
                                'publication-title': z.string(),
                                retry: z.string(),
                                'tip-cta': z.string(),
                                'tip-description': z.string(),
                                'tip-dismiss': z.string(),
                                'tip-dialog-title': z.string(),
                                'tip-image-alt': z.string(),
                                'tip-title': z.string(),
                            })
                            .partial(),
                        inbox: z
                            .object({
                                'empty-message': z.string(),
                                'empty-title': z.string(),
                                'loading-message': z.string(),
                                'loading-title': z.string(),
                                mentioned: z.string(),
                                'new-comment': z.string(),
                            })
                            .partial(),
                        'left-panel': z
                            .object({
                                filters: z.record(z.string(), z.string()),
                                'open-architecture': z.string(),
                                'open-job-profile-management': z.string(),
                                'search-placeholder': z.string(),
                                subtitle: z.string(),
                                'tabs-aria-label': z.string(),
                                tabs: z.record(z.string(), z.string()),
                                'welcome-back': z.string(),
                            })
                            .partial(),
                        'profile-tile': z
                            .object({
                                'load-error-title': z.string(),
                                'no-role-available': z.string(),
                                'notification-not-linked': z.string(),
                                'try-again': z.string(),
                            })
                            .partial(),
                        review: z
                            .object({
                                badges: z.record(z.string(), z.string()),
                                empty: z.string(),
                                'error-message': z.string(),
                                'error-title': z.string(),
                                'item-types': z.record(z.string(), z.string()),
                                'no-results': z.string(),
                                subtitles: z.record(z.string(), z.string()),
                            })
                            .partial(),
                        setup: dashboardV2SetupSchema,
                        'testing-menu': z.record(z.string(), z.string()),
                    })
                    .partial(),
                'invitation-dialog': z
                    .object({
                        'add-position': z.string(),
                        'invitation-success-message': z.string(),
                        'invite-button': z.string(),
                        position: z.string(),
                        subtitle: z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'job-architecture': z
                    .object({
                        'adjust-job-profile': z.string(),
                        'adjust-with-ai': z.string(),
                        'apply-changes-to-all-languages-label': z.string(),
                        'assign-job-profile': z.string(),
                        'automatically-select-career-track': z.string(),
                        'automatically-select-career-track-description': z.string(),
                        'blue-collar-label': z.string(),
                        'career-track-add-label': z.string(),
                        'career-track-headline-label': z.string(),
                        'career-track-label': z.string(),
                        'cobrainer-default-leveling-label': z.string(),
                        'company-profile-settings-career-framework-approach-description': z.string(),
                        'company-profile-settings-career-framework-approach-title': z.string(),
                        'company-profile-settings-career-framework-create-own-subtitle': z.string(),
                        'company-profile-settings-career-framework-create-own-title': z.string(),
                        'company-profile-settings-career-framework-predefined-subtitle': z.string(),
                        'company-profile-settings-career-framework-predefined-title': z.string(),
                        'company-profile-settings-company-description-hint': z.string(),
                        'company-profile-settings-company-description-title': z.string(),
                        'company-profile-settings-company-details': z.string(),
                        'company-profile-settings-company-details-description': z.string(),
                        'company-profile-settings-company-name-description': z.string(),
                        'company-profile-settings-company-name-title': z.string(),
                        'company-profile-settings-company-priorities-description': z.string(),
                        'company-profile-settings-company-priorities-title': z.string(),
                        'company-profile-settings-executive-roles-description': z.string(),
                        'company-profile-settings-executive-roles-title': z.string(),
                        'company-profile-settings-jsa-hint': z.string(),
                        'company-profile-settings-jsa-title': z.string(),
                        'company-profile-settings-number-of-employees-description': z.string(),
                        'company-profile-settings-number-of-employees-title': z.string(),
                        'company-size-number-placeholder': z.string(),
                        'create-job-architecture-button-label': z.string(),
                        'create-job-levels': z.string(),
                        'create-manually': z.string(),
                        'created-by': z.string(),
                        'created-success-job-architecture-button-label': z.string(),
                        'custom-leveling-framework-label': z.string(),
                        'delete-career-track-confirmation-dialog-description': z.string(),
                        'delete-confirmation-dialog-delete': z.string(),
                        'delete-confirmation-dialog-description': z.string(),
                        'delete-confirmation-dialog-discard': z.string(),
                        'delete-confirmation-dialog-title': z.string(),
                        'delete-job-architecture-button-label': z.string(),
                        'delete-job-architecture-dialog-description': z.string(),
                        'delete-job-architecture-dialog-title': z.string(),
                        'delete-job-architecture-label': z.string(),
                        'describe-ai-changes': z.string(),
                        description: z.string(),
                        'employees-with-this-skill': z.string(),
                        'enter-job-profile-title': z.string(),
                        'enter-valid-title': z.string(),
                        'executive-roles-in-job-levels': z.string(),
                        'executive-roles-in-own-job-family': z.string(),
                        'export-button': z.string(),
                        'export-error': z.string(),
                        'export-pdf-button': z.string(),
                        'export-pdf-disabled-hint': z.string(),
                        'export-pdf-error': z.string(),
                        'export-title': z.string(),
                        'generate-job-levels': z.string(),
                        'generate-with-ai': z.string(),
                        'generate-with-ai-dialog-description': z.string(),
                        'generate-with-ai-dialog-subtitle': z.string(),
                        'generate-with-ai-dialog-title': z.string(),
                        'generate-with-ai-setup-dialog-final-step-description': z.string(),
                        'generate-with-ai-setup-dialog-final-step-title': z.string(),
                        'job-cluster': z.string(),
                        'job-family': z.string(),
                        'job-level': z.string(),
                        'job-level-career-track-default-setting': z.string(),
                        'job-level-generate-with-ai-settings-dialog-career-track-selection-title': z.string(),
                        'job-level-generate-with-ai-settings-dialog-select-auto-subtitle': z.string(),
                        'job-level-generate-with-ai-settings-dialog-select-auto-title': z.string(),
                        'job-level-generate-with-ai-settings-dialog-select-auto-with-content-sub-subtitle': z.string(),
                        'job-level-generate-with-ai-settings-dialog-select-auto-with-skills-sub-subtitle': z.string(),
                        'job-level-generate-with-ai-settings-dialog-select-auto-with-skills-subtitle': z.string(),
                        'job-level-generate-with-ai-settings-dialog-select-auto-with-skills-title': z.string(),
                        'job-level-generate-with-ai-settings-dialog-select-manual-subtitle': z.string(),
                        'job-level-generate-with-ai-settings-dialog-select-manual-title': z.string(),
                        'job-level-generate-with-ai-settings-dialog-title': z.string(),
                        'job-levels-add-level-label': z.string(),
                        'job-levels-label': z.string(),
                        'job-profile': z.string(),
                        'job-profile-accept-changes': z.string(),
                        'job-profile-empty-competency-placeholder': z.string(),
                        'job-profile-generate-entire-description-button-label': z.string(),
                        'job-profile-generic-error': z.string(),
                        'job-profile-popup-description-generate-button-label': z.string(),
                        'job-profile-popup-description-generate-placeholder-label': z.string(),
                        'job-profile-popup-headline': z.string(),
                        'job-profile-redetect-all-skills': z.string(),
                        'job-profile-redetect-all-skills-placeholder': z.string(),
                        'job-profile-reject-changes': z.string(),
                        'job-profile-skill-no-suggestions-error': z.string(),
                        'job-profile-skill-search-placeholder-label': z.string(),
                        'job-profile-skill-suggestions-add-label': z.string(),
                        'job-profile-skill-suggestions-adjust-label': z.string(),
                        'job-profile-skill-suggestions-failed-error': z.string(),
                        'job-profile-skill-suggestions-remove-label': z.string(),
                        'job-profile-title-duplicate-error': z.string(),
                        'job-profile-title-invalid-error': z.string(),
                        'job-profile-validation-error': z.string(),
                        'job-profiles-with-this-skill': z.string(),
                        'job-role': z.string(),
                        'jobs-with-this-skill': z.string(),
                        'jsa-settings-drawer-label': z.string(),
                        'last-modified': z.string(),
                        'mark-as-reviewed': z.string(),
                        'market-based-leveling-framework-label': z.string(),
                        'new-job-architecture': z.string(),
                        'new-job-architecture-dialog-description': z.string(),
                        'new-job-architecture-dialog-title': z.string(),
                        'no-job-level-frameworks-existing': z.string(),
                        'not-assigned': z.string(),
                        'not-verified': z.string(),
                        'redetect-skills': z.string(),
                        'redirect-to-job-profile-dialog-cancel': z.string(),
                        'redirect-to-job-profile-dialog-continue': z.string(),
                        'redirect-to-job-profile-dialog-description': z.string(),
                        'redirect-to-job-profile-dialog-title': z.string(),
                        'regenerate-job-profile': z.string(),
                        'remove-competency': z.string(),
                        'required-skills': z.string(),
                        revert: z.string(),
                        'review-by': z.string(),
                        rewrite: z.string(),
                        'select-a-job-architecture': z.string(),
                        'select-a-job-cluster': z.string(),
                        'select-a-job-family': z.string(),
                        'select-a-job-level': z.string(),
                        'select-a-job-role': z.string(),
                        'select-a-skill-cluster': z.string(),
                        'select-a-skill-family': z.string(),
                        'select-job-architecture': z.string(),
                        'select-trade-unions-dialog-title': z.string(),
                        'select-trade-unions-hint-label': z.string(),
                        'skill-cluster': z.string(),
                        'skill-family': z.string(),
                        'skill-limit-exceeded-error': z.string(),
                        skills: z.string(),
                        'sort-alphabetically': z.string(),
                        'tab-slider-jobs': z.string(),
                        'tab-slider-skills': z.string(),
                        title: z.string(),
                        'trade-union': z
                            .object({
                                djv: z.string(),
                                evg: z.string(),
                                gdl: z.string(),
                                gew: z.string(),
                                ig_bau: z.string(),
                                ig_bce: z.string(),
                                ig_metall: z.string(),
                                marburger_bund: z.string(),
                                market_based: z.string(),
                                ngg: z.string(),
                                ufo: z.string(),
                                verdi: z.string(),
                            })
                            .partial(),
                        'type-here': z.string(),
                        'union-based-leveling-framework-label': z.string(),
                        verified: z.string(),
                        'verified-status': z.string(),
                        'verify-job-profile': z.string(),
                        'white-collar-label': z.string(),
                    })
                    .partial(),
                'job-architecture-v2': z
                    .object({
                        'action-bar': z.object({
                            'newer-version-available': z.string(),
                            'newer-version-available-aria': z.string(),
                        }),
                        'add-career-track-button': z.string(),
                        'add-competency-button': z.string(),
                        'add-job-role': z.string(),
                        'add-level-button': z.string(),
                        'add-level-select-role-first': z.string(),
                        'add-level-select-single-role': z.string(),
                        'adjust-with-ai': z.string(),
                        'an-error-occurred': z.string(),
                        archive: z.string(),
                        unpublish: z.string(),
                        'unpublish-description': z.string(),
                        'unpublish-title': z.string(),
                        'archive-job-levels-description': z.string(),
                        'archive-job-roles-description': z.string(),
                        'archive-success': z.string(),
                        'archive-title': z.string(),
                        'archived-items': z.string(),
                        'archived-items-subtitle': z.string(),
                        'archived-items-subtitle-plural': z.string(),
                        'archived-levels': z.string(),
                        'archived-notification': z.string(),
                        'archived-roles': z.string(),
                        'assign-reviewer-multi-description': z.string(),
                        'assign-reviewer-multi-title': z.string(),
                        'audit-table': z.object({
                            'add-reviewer': z.string(),
                            'aria-label': z.string(),
                            'change-reviewer': z.string(),
                            column: z.object({
                                status: z.string(),
                                user: z.string(),
                                when: z.string(),
                            }),
                            'no-publisher': z.string(),
                            pending: z.string(),
                            row: z.object({
                                drafted: z.string(),
                                publish: z.string(),
                                published: z.string(),
                                review: z.string(),
                                reviewed: z.string(),
                            }),
                        }),
                        'career-framework-title': z.string(),
                        'career-tracks-header': z.string(),
                        'change-proficiency-level': z.string(),
                        'highlight-proficiency': z.string(),
                        'for-you-filter': z.string(),
                        changelog: z.object({
                            actions: z
                                .object({
                                    archived: z.string(),
                                    'assigned-reviewer': z.string(),
                                    changed: z.string(),
                                    'comment-created': z.string(),
                                    'comment-replied': z.string(),
                                    'comment-resolved': z.string(),
                                    copied: z.string(),
                                    created: z.string(),
                                    deleted: z.string(),
                                    edited: z.string(),
                                    moved: z.string(),
                                    published: z.string(),
                                    reviewed: z.string(),
                                    'generations-cancelled': z.string(),
                                    'skill-bulk-removed': z.string(),
                                    'skills-updated': z.string(),
                                    'task-created': z.string(),
                                    'task-deleted': z.string(),
                                    'task-edited': z.string(),
                                    'task-skills-updated': z.string(),
                                    'tasks-reordered': z.string(),
                                    unarchived: z.string(),
                                    'unassigned-reviewer': z.string(),
                                    'version-created': z.string(),
                                })
                                .partial(),
                            'entity-types': z
                                .object({
                                    root: z.string(),
                                })
                                .partial(),
                            from: z.string(),
                            in: z.string(),
                            'no-entries': z.string(),
                            of: z.string(),
                            skills: z
                                .object({
                                    added: z.string(),
                                    'level-changed': z.string(),
                                    removed: z.string(),
                                })
                                .partial(),
                            subtitle: z.string(),
                            title: z.string(),
                            to: z.string(),
                            today: z.string(),
                            'unknown-user': z.string(),
                            version: z.string(),
                            yesterday: z.string(),
                        }),
                        'collapse-all': z.string(),
                        comments: z.object({
                            composer: z.object({
                                placeholder: z.string(),
                                submit: z.string(),
                            }),
                            'cross-language': z.object({
                                trigger: z.string(),
                                'trigger-aria': z.string(),
                            }),
                            'copy-error': z.string(),
                            'copy-success': z.string(),
                            'paste-error': z.string(),
                            errors: z.object({
                                'cross-section': z.string(),
                                'select-text-first': z.string(),
                            }),
                            group: z.object({
                                empty: z.string(),
                                role: z.object({
                                    parent: z.string(),
                                    task: z.string(),
                                }),
                            }),
                            'header-action': z.object({
                                label: z.string(),
                            }),
                            hidden: z.object({
                                groups: z.object({
                                    'section-fallback': z.string(),
                                    'skill-fallback': z.string(),
                                    title: z.string(),
                                }),
                            }),
                            section: z.object({
                                'close-label': z.string(),
                                title: z.string(),
                            }),
                            'selection-button': z.object({
                                label: z.string(),
                            }),
                            'view-mode': z.object({
                                active: z.string(),
                                hidden: z.string(),
                            }),
                            thread: z.object({
                                delete: z.string(),
                                'delete-confirm-description': z.string(),
                                'delete-confirm-title': z.string(),
                                reply: z.string(),
                                'reply-placeholder': z.string(),
                                'reply-submit': z.string(),
                                resolve: z.string(),
                                'resolve-disabled-edit-mode': z.string(),
                                'resolved-by': z.string(),
                                'unknown-author': z.string(),
                            }),
                        }),
                        comparision: z.object({
                            'tasks-section': z.string(),
                            'title-section': z.string(),
                        }),
                        'complete-setup': z.string(),
                        'create-family-and-clusters': z.string(),
                        'create-items': z.object({
                            button: z.string(),
                            placeholder: z.string(),
                            submit: z.string(),
                            upload: z.object({
                                'add-to-knowledge-base': z.string(),
                                'attachment-deleted': z.string(),
                                button: z.string(),
                                'delete-confirm-description': z.string(),
                                'delete-confirm-title': z.string(),
                                'delete-error': z.string(),
                                'delete-forbidden-error': z.string(),
                                'limit-tooltip': z.string(),
                                'select-from-knowledge-base': z.string(),
                                'selected-from-knowledge-base': z.string(),
                                'selection-summary': z.string(),
                                'total-size-limit-exceeded': z.string(),
                                'upload-new': z.string(),
                            }),
                        }),
                        'create-manually': z.string(),
                        'create-new': z.string(),
                        'create-only-families': z.string(),
                        'create-whole-architecture': z.string(),
                        'cut-disable-due-sap-integration': z.string(),
                        'delete-job-cluster': z.string(),
                        'delete-job-cluster-description': z.string(),
                        'delete-job-clusters-description': z.string(),
                        'delete-job-families-description': z.string(),
                        'delete-job-family': z.string(),
                        'delete-job-family-description': z.string(),
                        'delete-job-level-description': z.string(),
                        'delete-job-levels-description': z.string(),
                        'delete-job-roles-description': z.string(),
                        'delete-leveling-framework-dialog-description': z.string(),
                        'delete-leveling-framework-dialog-title': z.string(),
                        'delete-permanently-description': z.string(),
                        'delete-permanently-title': z.string(),
                        'delete-track-dialog-description': z.string(),
                        'delete-track-dialog-title': z.string(),
                        'drag-and-drop-cancelled': z.string(),
                        'drag-and-drop-dropped-at-position': z.string(),
                        'drag-and-drop-group-title': z.string(),
                        'drag-and-drop-instructions': z.string(),
                        'drag-and-drop-moved-over': z.string(),
                        'drag-and-drop-moved-to': z.string(),
                        'drag-and-drop-over-position': z.string(),
                        'drag-and-drop-picked-up': z.string(),
                        'sort-active': z.string(),
                        'sort-active-drag-disabled': z.string(),
                        'turn-off-sorting': z.string(),
                        'edit-card-dialog-add-title': z.string(),
                        'edit-card-dialog-description': z.string(),
                        'edit-card-dialog-edit-title': z.string(),
                        'edit-cluster': z.string(),
                        'edit-family': z.string(),
                        'edit-form-description-placeholder': z.string(),
                        'edit-form-title-min-length-error': z.string(),
                        'edit-form-title-placeholder': z.string(),
                        'edit-form-title-required-error': z.string(),
                        'edit-level-dialog-add-pay-band': z.string(),
                        'edit-level-dialog-add-title': z.string(),
                        'edit-level-dialog-compensation-frequency-annual': z.string(),
                        'edit-level-dialog-compensation-frequency-hourly': z.string(),
                        'edit-level-dialog-compensation-grade-setup-heading': z.string(),
                        'edit-level-dialog-country-pay-bands-empty': z.string(),
                        'edit-level-dialog-country-pay-bands-heading': z.string(),
                        'edit-level-dialog-country-pay-bands-helper': z.string(),
                        'edit-level-dialog-default-pay-band-heading': z.string(),
                        'edit-level-dialog-default-pay-band-helper': z.string(),
                        'edit-level-dialog-employment-type-label': z.string(),
                        'edit-level-dialog-invalid-number': z.string(),
                        'edit-level-dialog-fringe-benefits-label': z.string(),
                        'edit-level-dialog-fringe-benefits-placeholder': z.string(),
                        'edit-level-dialog-level-label': z.string(),
                        'edit-level-dialog-long-term-incentive-label': z.string(),
                        'edit-level-dialog-long-term-incentive-placeholder': z.string(),
                        'edit-level-dialog-pay-band-column-country-or-region': z.string(),
                        'edit-level-dialog-pay-band-column-currency': z.string(),
                        'edit-level-dialog-pay-band-column-frequency': z.string(),
                        'edit-level-dialog-pay-band-column-name': z.string(),
                        'edit-level-dialog-pay-band-copy-from-default-aria': z.string(),
                        'edit-level-dialog-pay-band-copy-suffix': z.string(),
                        'edit-level-dialog-pay-band-country-aria': z.string(),
                        'edit-level-dialog-pay-band-country-aria-default': z.string(),
                        'edit-level-dialog-pay-band-country-placeholder': z.string(),
                        'edit-level-dialog-pay-band-country-placeholder-default': z.string(),
                        'edit-level-dialog-pay-band-currency-aria': z.string(),
                        'edit-level-dialog-pay-band-currency-aria-default': z.string(),
                        'edit-level-dialog-pay-band-duplicate-aria': z.string(),
                        'edit-level-dialog-pay-band-frequency-aria': z.string(),
                        'edit-level-dialog-pay-band-frequency-aria-default': z.string(),
                        'edit-level-dialog-pay-band-maximum-aria-default': z.string(),
                        'edit-level-dialog-pay-band-midpoint-aria-default': z.string(),
                        'edit-level-dialog-pay-band-minimum-aria-default': z.string(),
                        'edit-level-dialog-pay-band-name-aria': z.string(),
                        'edit-level-dialog-pay-band-name-aria-default': z.string(),
                        'edit-level-dialog-pay-band-name-placeholder': z.string(),
                        'edit-level-dialog-pay-band-name-placeholder-default': z.string(),
                        'edit-level-dialog-pay-band-remove-aria': z.string(),
                        'edit-level-dialog-pay-grade-label': z.string(),
                        'edit-level-dialog-pay-grade-placeholder': z.string(),
                        'edit-level-dialog-pay-type-label': z.string(),
                        'edit-level-dialog-progression-heading': z.string(),
                        'edit-level-dialog-progression-helper': z.string(),
                        'edit-level-dialog-progression-placeholder': z.string(),
                        'edit-level-dialog-salary-incomplete': z.string(),
                        'edit-level-dialog-salary-maximum': z.string(),
                        'edit-level-dialog-salary-midpoint': z.string(),
                        'edit-level-dialog-salary-minimum': z.string(),
                        'edit-level-dialog-salary-order-below': z.string(),
                        'edit-level-dialog-salary-order-exceeds': z.string(),
                        'edit-level-dialog-salary-order-range': z.string(),
                        'edit-level-dialog-salary-placeholder': z.string(),
                        'edit-level-dialog-salary-range-label': z.string(),
                        'edit-level-dialog-save': z.string(),
                        'edit-level-dialog-tab-compensation': z.string(),
                        'edit-level-dialog-tab-general': z.string(),
                        'edit-level-dialog-tab-progression': z.string(),
                        'edit-level-dialog-target-bonus-label': z.string(),
                        'edit-level-dialog-target-bonus-placeholder': z.string(),
                        'edit-level-dialog-title': z.string(),
                        'employment-type-at': z.string(),
                        'employment-type-tarif': z.string(),
                        'empty-cell-drop-placeholder': z.string(),
                        'empty-cell-placeholder': z.string(),
                        'expand-all': z.string(),
                        'api-keys': z
                            .object({
                                'created-at': z.string(),
                                'create-button': z.string(),
                                description: z.string(),
                                'duplicate-name-error': z.string(),
                                empty: z.string(),
                                'expires-at': z.string(),
                                'last-used-at': z.string(),
                                'load-error': z.string(),
                                'never-used': z.string(),
                                'used-within-last-hour': z.string(),
                                'revoke-action': z.string(),
                                'revoke-button': z.string(),
                                'revoke-confirm-description': z.string(),
                                'revoke-confirm-title': z.string(),
                                'revoke-confirm-warning': z.string(),
                                'save-key-description': z.string(),
                                'save-key-title': z.string(),
                                'search-placeholder': z.string(),
                                scope: z.string(),
                            })
                            .partial(),
                        'general-settings': z
                            .object({
                                description: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'define-job-profile-template-dialog': z.object({
                            continue: z.string(),
                            'custom-option-subtitle': z.string(),
                            'custom-option-title': z.string(),
                            'default-option-subtitle': z.string(),
                            'default-option-title': z.string(),
                            'requires-setup': z.string(),
                            'setup-template': z.string(),
                            title: z.string(),
                        }),
                        'cancel-generations': z.object({ label: z.string(), description: z.string() }),
                        'copy-all-roles-to-levels': z.object({
                            description: z.string(),
                            'disabled-reason': z.string(),
                            label: z.string(),
                        }),
                        'generate-all-job-roles-with-ai': z.string(),
                        'generate-job-clusters-with-ai': z.string(),
                        'generate-levels-dialog': z.object({
                            'automatic-description': z.string(),
                            'automatic-title': z.string(),
                            'available-frameworks': z.string(),
                            'copy-only-description': z.string(),
                            'copy-only-title': z.string(),
                            'create-button': z.string(),
                            'custom-framework-description': z.string(),
                            'custom-framework-title': z.string(),
                            'default-framework-description': z.string(),
                            'default-framework-title': z.string(),
                            'generate-button': z.string(),
                            'manual-description': z.string(),
                            'manual-title': z.string(),
                            'requires-setup': z.string(),
                            'select-framework-button': z.string(),
                            'setup-frameworks-button': z.string(),
                            title: z.string(),
                            'title-multiple-roles': z.string(),
                        }),
                        'generate-with-ai': z.string(),
                        'generate-with-ai-warning': z.string(),
                        'generate-with-ai-warning-description': z.string(),
                        'generation-started-cta': z.string(),
                        'generation-started-description': z.string(),
                        'generation-started-title': z.string(),
                        'in-flight': z.object({
                            'processing-architecture': z.string(),
                            'processing-documents': z.string(),
                        }),
                        'job-add-reviewer': z.string(),
                        'job-add-reviewer-notification': z.string(),
                        'job-assign-reviewer': z.string(),
                        'job-cluster': z.string(),
                        'job-cluster-description': z.string(),
                        'job-cluster-description-placeholder': z.string(),
                        'job-cluster-name-placeholder': z.string(),
                        'job-clusters': z.string(),
                        'job-families': z.string(),
                        'job-families-generate-with-ai': z.string(),
                        'job-family': z.string(),
                        'job-family-description': z.string(),
                        'job-family-description-placeholder': z.string(),
                        'job-family-description-with-families': z.string(),
                        'job-family-explanation': z.string(),
                        'job-family-name-placeholder': z.string(),
                        'job-level': z.string(),
                        'job-level-edit': z.string(),
                        'job-level-view': z.string(),
                        'job-levels': z.string(),
                        'job-levels-enter-level-name': z.string(),
                        'job-levels-generate-all-with-ai': z.string(),
                        'job-levels-generate-with-ai': z.string(),
                        'job-levels-title-placeholder': z.string(),
                        'job-profile-accept-changes': z.string(),
                        'job-profile-compare-proposal': z.string(),
                        'job-profile-compare-proposal-expired': z.string(),
                        'job-profile-compare-versions': z.string(),
                        'job-profile-compare-versions-disabled-tooltip': z.string(),
                        'job-profile-compare-levels': z.string(),
                        'job-profile-level-comparison-no-skills': z.string(),
                        'job-profile-level-not-required': z.string(),
                        'job-profile-compare-roles': z.string(),
                        'job-profile-compare-with': z.string(),
                        'job-profile-compare-selected-roles': z.string(),
                        'job-profile-compare-roles-min-tooltip': z.string(),
                        'job-profile-compare-roles-max-tooltip': z.string(),
                        'job-profile-role-comparison-add-role': z.string(),
                        'job-profile-role-comparison-remove-role': z.string(),
                        'job-profile-level-comparison-remove-level': z.string(),
                        'job-profile-role-comparison-search-placeholder': z.string(),
                        'job-profile-role-comparison-no-results': z.string(),
                        'job-profile-role-comparison-no-skills': z.string(),
                        'job-profile-compare-with-level': z.string(),
                        'job-profile-compare-levels-min-tooltip': z.string(),
                        'job-profile-compare-levels-max-tooltip': z.string(),
                        'job-profile-level-comparison-add-level': z.string(),
                        'job-profile-level-comparison-search-placeholder': z.string(),
                        'job-profile-level-comparison-no-results': z.string(),
                        'job-profile-competency-search-placeholder': z.string(),
                        'job-profile-copied-to-clipboard': z.string(),
                        'job-profile-version-updated-toast': z.string(),
                        'job-profile-error-prefix': z.string(),
                        'job-profile-generating-notification': z.string(),
                        'job-profile-no-data-available': z.string(),
                        'job-profile-no-skill-level': z.string(),
                        'job-profile-new-version': z.string(),
                        shortcuts: z.object({
                            'create-cluster': z.string(),
                            'create-family': z.string(),
                            'create-level': z.string(),
                            'create-role': z.string(),
                            'open-cluster-menu': z.string(),
                            'open-family-menu': z.string(),
                            'open-item-actions': z.string(),
                            'open-language-menu': z.string(),
                            'open-page-menu': z.string(),
                            'open-role-menu': z.string(),
                            'open-setup-progress': z.string(),
                            'show-board-view': z.string(),
                            'show-table-view': z.string(),
                        }),
                        'job-profile-regenerate': z.string(),
                        'job-profile-edit-manually': z.string(),
                        'job-profile-edit-with-ai': z.string(),
                        'job-profile-save-without-auto-translation': z.string(),
                        'job-profile-reject-changes': z.string(),
                        'job-profile-required-skills': z.string(),
                        'job-profile-search-placeholder': z.string(),
                        'job-profile-skill-added-toast': z.string(),
                        'job-profile-skill-already-added-tooltip': z.string(),
                        'job-profile-skill-count': z.string(),
                        'job-profile-skill-count_one': z.string(),
                        'job-profile-skill-level': z.string(),
                        'job-profile-skill-search-placeholder': z.string(),
                        'job-profile-skill-suggestions-add-label': z.string(),
                        'job-profile-skill-suggestions-adjust-skills-label': z.string(),
                        'job-profile-skill-suggestions-remove-label': z.string(),
                        'job-profile-status-approved': z.string(),
                        'job-profile-status-assigned': z.string(),
                        'job-profile-status-draft': z.string(),
                        'job-profile-status-published': z.string(),
                        'job-profile-status-reviewed': z.string(),
                        'job-profile-title': z.string(),
                        'job-profile-unknown-user': z.string(),
                        'job-profile-updates-available': z.string(),
                        'job-profile-version': z.string(),
                        'job-profile-version-compare-left': z.string(),
                        'job-profile-version-compare-right': z.string(),
                        'job-profile-version-for': z.string(),
                        'job-profile-version-placeholder': z.string(),
                        'job-ready-to-publish': z.string(),
                        'job-ready-to-review': z.string(),
                        'job-role': z.string(),
                        'job-role-description': z.string(),
                        'job-role-description-with-roles': z.string(),
                        'job-role-publish-button': z.string(),
                        'job-role-publish-description': z.string(),
                        'job-role-publish-new-version-notification': z.string(),
                        'job-role-publish-notification': z.string(),
                        'job-role-publish-title': z.string(),
                        'job-role-review-new-version-notification': z.string(),
                        'job-role-review-notification': z.string(),
                        'job-roles': z.string(),
                        'job-roles-add-level': z.string(),
                        'job-roles-and-levels': z.string(),
                        'job-roles-delete-confirmation': z.string(),
                        'job-roles-delete-title': z.string(),
                        'job-roles-edit-role': z.string(),
                        'job-roles-enter-role-name': z.string(),
                        'job-roles-error-occurred': z.string(),
                        'job-roles-generate-all-with-ai': z.string(),
                        'job-roles-generate-levels-with-ai': z.string(),
                        'job-roles-generate-with-ai': z.string(),
                        'job-roles-profiles': z.string(),
                        'job-roles-reviewed': z.string(),
                        'job-roles-reviewers': z.string(),
                        'job-roles-view-role': z.string(),
                        'job-unassigned': z.string(),
                        'knowledge-base': z
                            .object({
                                'action-needed': z.string(),
                                'attention-message': z
                                    .object({
                                        'document-protected': z.string(),
                                        'document-too-complex': z.string(),
                                        'unsupported-format': z.string(),
                                    })
                                    .partial(),
                                'delete-document-description': z.string(),
                                'delete-document-title': z.string(),
                                'delete-documents-description': z.string(),
                                description: z.string(),
                                'documents-need-attention': z.string(),
                                'documents-need-attention_one': z.string(),
                                'download-started-confirm': z.string(),
                                'download-started-description': z.string(),
                                'download-started-title': z.string(),
                                'drop-text': z.string(),
                                'drop-zone-label': z.string(),
                                'file-group-documents': z.string(),
                                'file-group-spreadsheets': z.string(),
                                'file-group-text': z.string(),
                                'drop-zone-max-file-size': z.string(),
                                'failed-to-delete-document': z.string(),
                                'failed-to-delete-documents': z.string(),
                                'failed-to-download-document': z.string(),
                                'failed-to-download-documents': z.string(),
                                'failed-to-obtain-upload-urls': z.string(),
                                'failed-to-upload-file': z.string(),
                                'file-empty-exception': z.string(),
                                'file-label': z.string(),
                                'file-name-too-long-exception': z.string(),
                                'file-path-too-long-exception': z.string(),
                                'file-too-large-exception': z.string(),
                                files: z.string(),
                                folder: z.string(),
                                'info-company-specific': z.string(),
                                'info-current': z.string(),
                                'info-description': z.string(),
                                'info-readable': z.string(),
                                'info-relevant': z.string(),
                                'info-title': z.string(),
                                'max-number-of-files-per-batch-exceeded-exception': z.string(),
                                'no-documents': z.string(),
                                'refresh-statuses': z.string(),
                                'search-placeholder': z.string(),
                                'selected-file': z.string(),
                                status: z
                                    .object({
                                        DELETED: z.string(),
                                        ERROR: z.string(),
                                        INDEXED: z.string(),
                                        PROCESSING: z.string(),
                                        UPLOAD_FAILED: z.string(),
                                        UPLOADING: z.string(),
                                    })
                                    .partial(),
                                'supported-formats': z.string(),
                                'table-description': z.string(),
                                'table-title': z.string(),
                                title: z.string(),
                                'upload-button': z.string(),
                            })
                            .partial(),
                        languages: z
                            .object({
                                'active-badge': z.string(),
                                'add-button': z.string(),
                                'billing-info': z.string(),
                                'complete-coverage': z.string(),
                                'default-badge': z.string(),
                                description: z.string(),
                                statistics: z.object({
                                    clusters: z.string(),
                                    families: z.string(),
                                    levels: z.string(),
                                    roles: z.string(),
                                    skills: z.string(),
                                }),
                                title: z.string(),
                            })
                            .partial(),
                        'subscription-billing': z
                            .object({
                                languages: z
                                    .object({
                                        activated: z.string(),
                                        'badge-active': z.string(),
                                        'badge-default': z.string(),
                                        'checkout-pending': z.string(),
                                        'delete-language': z.string(),
                                        description: z.string(),
                                        included: z.string(),
                                        status: z.object({
                                            active: z.string(),
                                            default: z.string(),
                                            free: z.string(),
                                        }),
                                        dialog: z
                                            .object({
                                                'additional-cost': z.string(),
                                                'added-to-invoice': z.string(),
                                                'cancel-button': z.string(),
                                                'confirm-button': z.string(),
                                                description: z.string(),
                                                'job-profiles': z.string(),
                                                skills: z.string(),
                                                'tasks-created': z.string(),
                                                title: z.string(),
                                                'translation-impact': z.string(),
                                            })
                                            .partial(),
                                        panel: z
                                            .object({
                                                'cancel-button': z.string(),
                                                'confirm-button': z.string(),
                                                'contact-sales': z.string(),
                                                description: z.string(),
                                                pricing: z.string(),
                                                'total-per-month': z.string(),
                                            })
                                            .partial(),
                                        'delete-dialog': z
                                            .object({
                                                cancel: z.string(),
                                                description: z.string(),
                                                confirm: z.string(),
                                                title: z.string(),
                                            })
                                            .partial(),
                                        error: z
                                            .object({
                                                'delete-failed': z.string(),
                                                'purchase-failed': z.string(),
                                            })
                                            .partial(),
                                        'per-month': z.string(),
                                        'profiles-x-price': z.string(),
                                        'search-placeholder': z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        'leveling-framework-create-button': z.string(),
                        'leveling-framework-create-button-from-template': z.string(),
                        'leveling-framework-create-title': z.string(),
                        'leveling-framework-create-with-ai-button': z.string(),
                        'leveling-framework-currency-label': z.string(),
                        'leveling-framework-description-label': z.string(),
                        'leveling-framework-description-placeholder': z.string(),
                        'leveling-framework-edit-title': z.string(),
                        'leveling-framework-label': z.string(),
                        'leveling-framework-list-description': z.string(),
                        'leveling-framework-list-empty-state': z.string(),
                        'leveling-framework-list-loading': z.string(),
                        'leveling-framework-load-error': z.string(),
                        'leveling-framework-loading': z.string(),
                        'leveling-framework-english-name-required-error': z.string(),
                        'leveling-framework-modify-with-ai-button': z.string(),
                        'leveling-framework-name-label': z.string(),
                        'leveling-framework-name-min-length': z.string(),
                        'leveling-framework-name-placeholder': z.string(),
                        'leveling-framework-name-required': z.string(),
                        'leveling-framework-non-deletable-tooltip': z.string(),
                        'leveling-framework-non-editable-tooltip': z.string(),
                        'leveling-framework-predefined-activate-button': z.string(),
                        'leveling-framework-predefined-dialog-title': z.string(),
                        'leveling-framework-predefined-empty': z.string(),
                        'leveling-framework-predefined-selected': z.string(),
                        'leveling-framework-saving': z.string(),
                        'leveling-framework-use-predefined-button': z.string(),
                        levels: z.string(),
                        'manage-career-tracks-and-levels-subtitle': z.string(),
                        'mark-job-role-reviewed-content': z.string(),
                        'mark-job-role-reviewed-title': z.string(),
                        'no-archived-levels': z.string(),
                        'no-archived-roles': z.string(),
                        onboarding: z.object({
                            'accepting-changes': z.string().optional(),
                            'apply-selected': z.string().optional(),
                            'confirm-structure-button': z.string().optional(),
                            'confirm-structure-dialog-description': z.string().optional(),
                            'confirm-structure-dialog-title': z.string().optional(),
                            notification: z
                                .object({
                                    'assign-level-reviewers-description': z.string(),
                                    'assign-level-reviewers-link': z.string(),
                                    'assign-level-reviewers-title': z.string(),
                                    'assign-reviewers-description': z.string(),
                                    'assign-reviewers-link': z.string(),
                                    'assign-reviewers-title': z.string(),
                                    'back-button': z.string(),
                                    'confirm-framework-button': z.string(),
                                    'continue-button': z.string(),
                                    'define-career-framework-description': z.string(),
                                    'define-career-framework-title': z.string(),
                                    'define-framework-button': z.string(),
                                    'define-leveling-framework-description': z.string(),
                                    'define-leveling-framework-title': z.string(),
                                    'generate-all-roles-button': z.string(),
                                    'generate-all-roles-description': z.string(),
                                    'generate-all-roles-title': z.string(),
                                    'generate-levels-complete-button': z.string(),
                                    'generate-levels-description': z.string(),
                                    'generate-levels-link': z.string(),
                                    'generate-levels-title': z.string(),
                                    'generate-roles-description': z.string(),
                                    'generate-roles-title': z.string(),
                                    'review-action-required-description': z.string(),
                                    'review-action-required-title': z.string(),
                                    'review-publish-description': z.string(),
                                    'review-publish-levels-description': z.string(),
                                    'review-publish-levels-link': z.string(),
                                    'review-publish-levels-title': z.string(),
                                    'review-publish-link': z.string(),
                                    'review-publish-title': z.string(),
                                    'review-structure-description': z.string(),
                                    'review-structure-title': z.string(),
                                    'start-reviewing-button': z.string(),
                                })
                                .optional(),
                            'pending-changes-description': z.string(),
                            progress: z
                                .object({
                                    'leveling-label': z.string(),
                                    'leveling-subtitle': z.string(),
                                    'roles-label': z.string(),
                                    'roles-subtitle': z.string(),
                                    'setup-label': z.string(),
                                    'setup-subtitle': z.string(),
                                    'structure-label': z.string(),
                                    'structure-subtitle': z.string(),
                                })
                                .optional(),
                            'regenerate-clusters': z.string(),
                            'regenerate-families': z.string(),
                            'reject-all': z.string().optional(),
                            'review-banner-summary': z.string().optional(),
                            'review-title': z.string(),
                        }),
                        'open-separate-page': z.string(),
                        'origin-language-label': z.string(),
                        'origin-language-tooltip': z.string(),
                        'pay-type-hourly': z.string(),
                        'pay-type-salaried': z.string(),
                        prompts: z
                            .object({
                                'archived-question': z.string(),
                                'assign-myself': z.string(),
                                'assign-reviewer-question': z.string(),
                                'change-reviewer': z.string(),
                                'comment-review-question': z.string(),
                                'comment-review-question-fallback': z.string(),
                                compare: z.string(),
                                'compare-versions': z.string(),
                                'delete-permanently': z.string(),
                                'fallback-title': z.string(),
                                'generating-question': z.string(),
                                'generating-status': z.string(),
                                'idle-assign': z.string(),
                                'idle-comments': z.string(),
                                'idle-publish': z.string(),
                                'idle-review': z.string(),
                                'looks-right': z.string(),
                                'make-changes': z.string(),
                                'not-assigned': z.string(),
                                'publish-question': z.string(),
                                'publish-question-new-version': z.string(),
                                'review-question': z.string(),
                                'review-question-new-version': z.string(),
                                'review-comments': z.string(),
                            })
                            .partial(),
                        'regenerate-dialog': z.object({
                            'additional-prompt-placeholder': z.string(),
                            'apply-comments': z.string(),
                            'apply-target': z.string(),
                            'apply-to-description': z.string(),
                            'apply-to-skills': z.string(),
                            'regenerate-with-comments': z.string(),
                            'comment-count': z.string(),
                            'comment-count_one': z.string(),
                            description: z.string(),
                            'include-replies': z.string(),
                        }),
                        restore: z.string(),
                        'restore-success': z.string(),
                        export: z
                            .object({
                                'menu-label': z.string(),
                                'picker-title': z.string(),
                                'picker-description': z.string(),
                                'picker-job-architecture-title': z.string(),
                                'picker-job-architecture-description': z.string(),
                                'picker-job-architecture-email-required': z.string(),
                                'picker-changelog-title': z.string(),
                                'picker-changelog-description': z.string(),
                                'picker-cancel': z.string(),
                                'changelog-title': z.string(),
                                'changelog-description': z.string(),
                                'changelog-date-range-title': z.string(),
                                'changelog-date-range-description': z.string(),
                                'changelog-from-label': z.string(),
                                'changelog-to-label': z.string(),
                                'changelog-scope-title': z.string(),
                                'changelog-scope-description': z.string(),
                                'changelog-scope-whole': z.string(),
                                'changelog-scope-by-family': z.string(),
                                'changelog-scope-by-cluster': z.string(),
                                'changelog-send-to-title': z.string(),
                                'changelog-send-to-description': z.string(),
                                'changelog-back': z.string(),
                                'changelog-submit': z.string(),
                                'changelog-success-title': z.string(),
                                'changelog-success-message': z.string(),
                                'changelog-error': z.string(),
                                'job-architecture-success-title': z.string(),
                                'job-architecture-success-message': z.string(),
                                'job-architecture-completed': z.string(),
                                'job-architecture-failed': z.string(),
                            })
                            .partial()
                            .optional(),
                        'export-file': z
                            .object({
                                columns: z
                                    .object({
                                        'latest-version': z.string(),
                                        version: z.string(),
                                        'version-status': z.string(),
                                    })
                                    .partial(),
                                sheets: z
                                    .object({
                                        structure: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial()
                            .optional(),
                        'review-state': z
                            .object({
                                draft: z.string(),
                                'new-version': z.string(),
                                published: z.string(),
                            })
                            .partial(),
                        'job-profile-status-stamp': z.object({
                            published: z.string(),
                            reviewed: z.string(),
                            'version-prefix': z.string(),
                        }),
                        'search-modal': z
                            .object({
                                'error-description': z.string(),
                                'error-title': z.string(),
                                'filter-reviewer': z.string(),
                                'filter-type': z.string(),
                                'no-results-description': z.string(),
                                'no-results-title': z.string(),
                                'search-placeholder': z.string(),
                            })
                            .partial(),
                        'search-min-length': z.string(),
                        'search-reviewers-placeholder': z.string(),
                        'select-architecture': z.object({
                            all: z.string(),
                            current: z.string(),
                            placeholder: z.string(),
                        }),
                        settings: z
                            .object({
                                'last-modified': z.string(),
                                'no-architectures': z.object({
                                    description: z.string(),
                                    title: z.string(),
                                }),
                                placeholder: z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                sidebar: z
                                    .object({
                                        account: z.string(),
                                        'api-keys': z.string(),
                                        'career-framework': z.string(),
                                        'company-profile': z.string(),
                                        'default-position-architecture': z.string(),
                                        general: z.string(),
                                        'ja-company-profile': z.string(),
                                        'job-profile': z.string(),
                                        'knowledge-base': z.string(),
                                        'language-and-visibility': z.string(),
                                        languages: z.string(),
                                        notifications: z.string(),
                                        'sb-languages': z.string(),
                                        'skill-change-events': z.string(),
                                        'skills-management': z.string(),
                                        terminology: z.string(),
                                        'user-profile': z.string(),
                                        'users-and-roles': z.string(),
                                    })
                                    .partial(),
                                tabs: z
                                    .object({
                                        'job-architecture': z.string(),
                                        organization: z.string(),
                                        'personal-preferences': z.string(),
                                        'position-architecture': z.string(),
                                        'subscription-billing': z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        'setup-dialog': z
                            .object({
                                'build-job-architecture': z.string(),
                                'generate-all-families-clusters-roles': z.string(),
                                'company-information': z
                                    .object({
                                        'company-description': z
                                            .object({
                                                label: z.string(),
                                                placeholder: z.string(),
                                            })
                                            .partial(),
                                        'company-domain': z
                                            .object({
                                                label: z.string(),
                                                placeholder: z.string(),
                                            })
                                            .partial(),
                                        'company-name': z
                                            .object({
                                                label: z.string(),
                                                placeholder: z.string(),
                                            })
                                            .partial(),
                                        'company-size': z
                                            .object({
                                                label: z.string(),
                                                'max-error': z.string(),
                                                'min-error': z.string(),
                                                placeholder: z.string(),
                                                required: z.string(),
                                            })
                                            .partial(),
                                        confirm: z.string(),
                                        description: z.string(),
                                        error: z
                                            .object({
                                                body: z.string(),
                                                title: z.string(),
                                                'try-again': z.string(),
                                            })
                                            .partial(),
                                        'invalid-delimiters': z.string(),
                                        'looking-up': z.string(),
                                        'rewrite-description': z.string(),
                                        sr: z
                                            .object({
                                                'generating-announcement': z.string(),
                                                'loaded-announcement': z.string(),
                                            })
                                            .partial(),
                                        title: z.string(),
                                        'top-priorities': z
                                            .object({
                                                label: z.string(),
                                                placeholder: z.string(),
                                            })
                                            .partial(),
                                    })
                                    .partial(),
                                generating: z
                                    .object({
                                        title: z.string(),
                                    })
                                    .optional(),
                                'next-step': z.string(),
                                overview: z.object({
                                    description: z.string(),
                                    goals: z.object({
                                        'eu-pay-transparency': z.string(),
                                        'eu-pay-transparency-desc': z.string(),
                                        other: z.string(),
                                        'other-desc': z.string(),
                                        'other-placeholder': z.string(),
                                        reorganization: z.string(),
                                        'reorganization-desc': z.string(),
                                        'sap-success-factors': z.string(),
                                        'sap-success-factors-desc': z.string(),
                                    }),
                                    'section-description': z.string(),
                                    'section-title': z.string(),
                                    title: z.string(),
                                }),
                                steps: z
                                    .object({
                                        'company-information': z.string(),
                                        'company-information-desc': z.string(),
                                        overview: z.string(),
                                        'overview-desc': z.string(),
                                        'upload-data': z.string(),
                                        'upload-data-desc': z.string(),
                                    })
                                    .partial(),
                                'upload-data': z.object({
                                    description: z.string(),
                                    skip: z.string(),
                                    title: z.string(),
                                }),
                            })
                            .optional(),
                        'show-skill-details': z.string(),
                        'open-skill': z.string(),
                        'skill-action-switcher': z.object({
                            'adjust-with-ai': z.string(),
                            'describe-ai-changes': z.string(),
                            search: z.string(),
                            send: z.string(),
                        }),
                        'skill-details-error': z.string(),
                        'skill-details-loading': z.string(),
                        'skill-details-no-data': z.string(),
                        'skill-limit-reached-level': z.string(),
                        'skill-limit-reached-role': z.string(),
                        'skill-redetection-notification': z.object({
                            'redetect-skills': z.string(),
                            'redetected-skills': z.string(),
                            'redetecting-skills': z.string(),
                        }),
                        skills: z.object({
                            'add-button': z.string(),
                            'add-new-skill-title': z.string(),
                            'bulk-removal': z.object({
                                action: z.string(),
                                'action-multiple': z.string(),
                                'action-multiple_one': z.string(),
                                'architectures-not-included': z.string(),
                                'architectures-not-included_one': z.string(),
                                'cannot-be-undone': z.string(),
                                'completed-toast': z.string(),
                                'completed-toast-multiple': z.string(),
                                'completed-toast-multiple_one': z.string(),
                                'completed-toast_one': z.string(),
                                'confirm-button': z.string(),
                                'confirm-button_one': z.string(),
                                deleting: z.string(),
                                description: z.string(),
                                description_one: z.string(),
                                'disabled-unused': z.string(),
                                'failed-toast': z.string(),
                                'failed-toast_one': z.string(),
                                receipt: z.object({
                                    counts: z.string(),
                                    'scope-employees': z.string(),
                                    'scope-learning-opportunities': z.string(),
                                    'scope-tasks': z.string(),
                                    'scopes-included': z.string(),
                                }),
                                'result-failed': z.string(),
                                'result-failed_one': z.string(),
                                'result-removed': z.string(),
                                'result-removed_one': z.string(),
                                'scope-rows': z.object({
                                    employees: z.string(),
                                    'learning-opportunities': z.string(),
                                    tasks: z.string(),
                                }),
                                'skipped-unused': z.string(),
                                'skipped-unused_one': z.string(),
                                success: z.string(),
                                success_one: z.string(),
                                'table-headers': z.object({
                                    levels: z.string(),
                                    roles: z.string(),
                                    scope: z.string(),
                                }),
                            }),
                            'change-skill': z.string(),
                            'change-sync-status': z.string(),
                            delete: z.object({
                                button: z.string(),
                                'confirm-description': z.string(),
                                'confirm-title': z.string(),
                                'disabled-has-assignments': z.string(),
                                'disabled-synced': z.string(),
                            }),
                            'error-prefix': z.string(),
                            'filter-architecture-empty-message': z.string(),
                            'filter-architecture-search-placeholder': z.string(),
                            'filter-architecture-section-label': z.string(),
                            'filter-button': z.string(),
                            'filter-level-empty-message': z.string(),
                            'filter-skill-type-section-label': z.string(),
                            'filter-source-section-label': z.string(),
                            'filter-level-label': z.string(),
                            'filter-level-search-placeholder': z.string(),
                            'filter-role-empty-message': z.string(),
                            'filter-role-label': z.string(),
                            'filter-role-search-placeholder': z.string(),
                            'filter-scoped-by-other-filters': z.string(),
                            'filter-selection-out-of-scope': z.string(),
                            'filter-selected-count': z.string(),
                            'filter-status-section-label': z.string(),
                            'more-items-not-shown': z.string(),
                            'not-synced-tooltip': z.string(),
                            'page-title': z.string(),
                            'reference-count': z.object({
                                competencies: z.string(),
                                courses: z.string(),
                                'job-architectures': z.string(),
                                'job-levels': z.string(),
                                'job-roles': z.string(),
                                tasks: z.string(),
                            }),
                            'search-min-length-hint': z.string(),
                            'search-placeholder': z.string(),
                            'search-skill-label': z.string(),
                            'search-skill-placeholder': z.string(),
                            'skill-type-label': z.string(),
                            'sort-first-results': z.string(),
                            source: z.object({
                                attributed: z.string(),
                                manual: z.string(),
                            }),
                            'sync-status': z.object({
                                NEEDS_REVIEW: z.object({
                                    title: z.string(),
                                }),
                                NOT_SYNCED: z.object({
                                    explanation: z.string(),
                                    title: z.string(),
                                }),
                                SYNC: z.object({
                                    explanation: z.string(),
                                    title: z.string(),
                                }),
                                SYNC_WHEN_IN_PROFILE: z.object({
                                    explanation: z.string(),
                                    title: z.string(),
                                    tooltip: z.string(),
                                }),
                            }),
                            'sync-status-trigger': z.string(),
                            statistics: z.object({
                                'deactivated-tooltip': z.string(),
                                'synced-tooltip': z.string(),
                                'to-review-tooltip': z.string(),
                                'total-tooltip': z.string(),
                            }),
                            'sync-when-in-profile-description': z.string(),
                            'sync-when-in-profile-label': z.string(),
                            'table-headers': z.object({
                                'added-at': z.string(),
                                competencies: z.string(),
                                courses: z.string(),
                                employees: z.string(),
                                'job-architectures': z.string(),
                                'job-levels': z.string(),
                                'job-roles': z.string(),
                                name: z.string(),
                                'skill-type': z.string(),
                                source: z.string(),
                                'sync-status': z.string(),
                                tasks: z.string(),
                            }),
                        }),
                        standalone: z.object({
                            'view-in-job-architecture': z.string(),
                        }),
                        statistics: z.object({
                            'level-count': z.string(),
                            'levels-with-reviewers': z.string(),
                            'linked-positions': z.string(),
                            profiles: z.string(),
                            'published-levels': z.string(),
                            'published-roles': z.string(),
                            reviewed: z.string(),
                            'reviewed-levels': z.string(),
                            'reviewed-roles': z.string(),
                            reviewers: z.string(),
                            'role-count': z.string(),
                            'roles-with-reviewers': z.string(),
                        }),
                        suggested: z.string(),
                        'table-column': z.object({
                            level: z.string(),
                            name: z.string(),
                            'published-by': z.string(),
                            reviewer: z.string(),
                            roles: z.string(),
                            status: z.string(),
                            type: z.string(),
                        }),
                        tabs: z.object({
                            board: z.string(),
                            overview: z.string(),
                            'roles-and-levels': z.string(),
                            skills: z.string(),
                            table: z.string(),
                        }),
                        tasks: z.object({
                            unclustered: z.string(),
                            column: z.object({
                                children: z.string(),
                                name: z.string(),
                                parents: z.string(),
                                related: z.string(),
                            }),
                            'load-error': z.string(),
                            'no-data': z.string(),
                            'no-search-results': z.string(),
                            'page-title': z.string(),
                            'search-min-length-hint': z.string(),
                            relation: z.object({
                                children: z.string(),
                                'children-count': z.string(),
                                parents: z.string(),
                                'parents-count': z.string(),
                                related: z.string(),
                                'related-count': z.string(),
                            }),
                            'search-placeholder': z.string(),
                        }),
                        'translated-with-ai-tooltip': z.string(),
                        'unsaved-changes-dialog-description': z.string(),
                        'unsaved-changes-dialog-title': z.string(),
                        'validation-error-skills-missing-proficiency': z.string(),
                        'version-note': z.object({
                            add: z.string(),
                            'aria-label': z.string(),
                            'aria-label-input': z.string(),
                            cancel: z.string(),
                            collapse: z.string(),
                            edit: z.string(),
                            label: z.string(),
                            'locked-toast': z.string(),
                            placeholder: z.string(),
                            save: z.string(),
                        }),
                        'view-cluster': z.string(),
                        'view-family': z.string(),
                    })
                    .partial(),
                'job-detail': z
                    .object({
                        'additional-skills': z.string(),
                        apply: z.string(),
                        'apply-message': z.string(),
                        'candidate-matching': z
                            .object({
                                description: z.string(),
                                headline: z.string(),
                                matches: z.string(),
                                mismatches: z.string(),
                                'users-missing-skills': z.string(),
                                'users-skills': z.string(),
                            })
                            .partial(),
                        caption: z.string(),
                        contact: z.string(),
                        'contact-person-message': z.string(),
                        'general-info-title': z.string(),
                        greeting: z.string(),
                        job: z.string(),
                        'mail-subject': z.string(),
                        'proficiency-view': z.string(),
                        recommendations: z
                            .object({
                                'clear-all-filters': z.string(),
                                drawer: z
                                    .object({
                                        'action-button-title': z.string(),
                                        'employee-section': z
                                            .object({
                                                headline: z.string(),
                                                'sub-headline': z.string(),
                                            })
                                            .partial(),
                                        headline: z.string(),
                                        'other-employees': z.string(),
                                    })
                                    .partial(),
                                'employee-item': z
                                    .object({
                                        headline: z.string(),
                                        'matching-score-title': z.string(),
                                        'open-for-remote': z.string(),
                                        'skills-headline': z.string(),
                                        'skills-headline-name': z.string(),
                                    })
                                    .partial(),
                                'job-seeking': z
                                    .object({
                                        option: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'load-more': z.string(),
                                'no-candidates': z.string(),
                                'no-recommendations': z
                                    .object({
                                        headline: z.string(),
                                        text: z.string(),
                                    })
                                    .partial(),
                                'not-enough-skills': z
                                    .object({
                                        headline: z.string(),
                                        text: z.string(),
                                    })
                                    .partial(),
                                'relocation-preference': z
                                    .object({
                                        option: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'seniority-level': z.string(),
                                'seniority-level-selected': z.string(),
                                shortlist: z.string(),
                                shortlisted: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'required-skills-title': z.string(),
                        shortlist: z
                            .object({
                                'add-candidate': z.string(),
                                'add-note': z.string(),
                                'edit-note': z.string(),
                                'no-shortlist': z.string(),
                                note: z.string(),
                                'note-anonymized': z.string(),
                                'search-placeholder': z.string(),
                                'shortlisted-by': z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'skill-group-view': z.string(),
                        'skills-recommendation-fields-are-missed': z.string(),
                        'skills-recommendation-tooltip': z.string(),
                        title: z.string(),
                        'user-matching': z
                            .object({
                                description: z.string(),
                                headline: z.string(),
                                match: z.string(),
                                matches: z.string(),
                                'matching-skills': z.string(),
                                mismatches: z.string(),
                                'missing-skills': z.string(),
                                'relevant-learning-opportunities': z.string(),
                                'view-all': z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                'job-posting': z
                    .object({
                        'generate-description-with-ai': z.string(),
                        'generated-description-with-ai-error': z.string(),
                        'generated-description-with-ai-loading-states': z
                            .object({
                                1: z.string(),
                                2: z.string(),
                                3: z.string(),
                                4: z.string(),
                            })
                            .partial(),
                        'generated-description-with-ai-success': z.string(),
                        'generated-description-with-ai-success-title': z.string(),
                        language: z.string(),
                    })
                    .partial(),
                'job-profile-settings': z
                    .object({
                        activate: z.string(),
                        'activate-custom-template': z.string(),
                        'chat-create-badge': z.string(),
                        'chat-create-context': z.string(),
                        'chat-create-message': z.string(),
                        'chat-create-question': z.string(),
                        'add-blank-section': z.string(),
                        'add-from-cobrainer': z.string(),
                        'add-section': z.string(),
                        cancel: z.string(),
                        'cobrainer-certified-template': z.string(),
                        'continue-editing': z.string(),
                        'template-selected': z.string(),
                        'copy-to-job-level': z.string(),
                        'copy-to-job-role': z.string(),
                        'count-range': z.string(),
                        'create-custom-template': z.string(),
                        'custom-template': z.string(),
                        'custom-template-description': z.string(),
                        delete: z.string(),
                        'delete-and-reset': z.string(),
                        'delete-custom-template': z.string(),
                        'delete-template-description': z.string(),
                        description: z.string(),
                        'dont-show-again': z.string(),
                        edit: z.string(),
                        'edit-custom-template': z.string(),
                        'edit-names': z.string(),
                        'eu-pay-transparency-description': z.string(),
                        'eu-pay-transparency-warning': z.string(),
                        'expected-skills': z.string(),
                        'skill-preference': z.string(),
                        'skill-preference-info': z.string(),
                        'skill-preference-info-aria': z.string(),
                        'skill-preference-empty': z.string(),
                        optional: z.string(),
                        'skill-preference-placeholder': z.string(),
                        format: z.string(),
                        'format-bullets': z.string(),
                        'format-sentences': z.string(),
                        'format-tasks': z.string(),
                        'skills-per-task': z.string(),
                        'task-content-guidance': z.string(),
                        'task-content-guidance-help': z.string(),
                        'task-content-guidance-placeholder': z.string(),
                        guidance: z.string(),
                        'job-level-label': z.string(),
                        'job-level-sections': z.string(),
                        'job-role-label': z.string(),
                        'job-role-sections': z.string(),
                        'keep-section': z.string(),
                        'managed-template-default-description': z.string(),
                        'maximum-skills': z.string(),
                        'modify-cobrainer-template': z.string(),
                        'no-preview-data': z.string(),
                        'no-template-description': z.string(),
                        'no-template-title': z.string(),
                        'outdated-banner': z.string(),
                        'permanent-deletion-warning': z.string(),
                        preview: z.string(),
                        'remove-section': z.string(),
                        'sap-edit-notice-description': z.string(),
                        'sap-edit-notice-title': z.string(),
                        'sap-integration-description': z.string(),
                        'sap-integration-title': z.string(),
                        'section-name': z.string(),
                        'select-template-title': z.string(),
                        'skill-requirements': z.string(),
                        'job-role-skill-requirements': z.string(),
                        'job-level-skill-requirements': z.string(),
                        'template-description': z.string(),
                        'template-name': z.string(),
                        'template-preview-title': z.string(),
                        title: z.string(),
                        'eu-pay-transparency-badge': z.string(),
                        'managed-by-cobrainer': z.string(),
                        'managed-by-cobrainer-description': z.string(),
                        'count-separator': z.string(),
                        'editor-subtitle': z.string(),
                        'default-editor-subtitle': z.string(),
                        'template-name-placeholder': z.string(),
                        'template-description-placeholder': z.string(),
                        'guidance-placeholder-predefined': z.string(),
                        'guidance-placeholder-custom': z.string(),
                        'guidance-info-title': z.string(),
                        'guidance-info-example': z.string(),
                        'guidance-info-copy-example': z.string(),
                        'guidance-info-aria': z.string(),
                        'section-singular': z.string(),
                        'section-plural': z.string(),
                        'required-skills': z.string(),
                        'job-overview': z.string(),
                        sample: z.string(),
                        'missing-translations-error': z.string(),
                        'value-required-error': z.string(),
                        'value-range-error': z.string(),
                        'maximum-below-expected-error': z.string(),
                        'max-below-min-error': z.string(),
                        'section-name-too-long-error': z.string(),
                        'duplicate-section-name-error': z.string(),
                        'error-discriminating': z.string(),
                        'error-prompt-extraction': z.string(),
                        'error-contradicting': z.string(),
                        'error-guidance-risk': z.string(),
                        'error-save-failed': z.string(),
                    })
                    .partial(),
                'job-profiles': z
                    .object({
                        assignments: z
                            .object({
                                'add-employee': z.string(),
                                'add-employee-error': z.string(),
                                'add-employee-success': z.string(),
                                'assigned-by': z.string(),
                                filters: z
                                    .object({
                                        'drawer-title': z.string(),
                                        'profile-preferences': z.object({
                                            subtitle: z.string(),
                                            title: z.string(),
                                        }),
                                    })
                                    .partial(),
                                'load-more': z.string(),
                                'remove-assignment': z.string(),
                                'remove-assignment-confirmation': z.string(),
                                'remove-employee-error': z.string(),
                                'remove-employee-success': z.string(),
                                'search-placeholder': z.string(),
                            })
                            .partial(),
                        'create-button': z.string(),
                        'empty-assignment': z
                            .object({
                                subtitle: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        filters: z
                            .object({
                                'review-required-by-me': z.string(),
                                'workspace-preferences': z.object({
                                    'sub-title': z.string(),
                                    title: z.string(),
                                }),
                            })
                            .partial(),
                        headline: z.string(),
                        'no-data': z.string(),
                        'no-matching': z.string(),
                        'search-placeholder': z.string(),
                        subtitle: z.string(),
                        table: z
                            .object({
                                'next-step': z.string(),
                            })
                            .partial(),
                        tabs: z
                            .object({
                                'assign-users': z.string(),
                                'job-profile-description': z.string(),
                            })
                            .partial(),
                        title: z.string(),
                    })
                    .partial(),
                jobs: z
                    .object({
                        columns: z
                            .object({
                                'application-url': z.string(),
                                creator: z.string(),
                                description: z.string(),
                                duration: z
                                    .object({
                                        title: z.string(),
                                    })
                                    .partial(),
                                'email-placeholder': z.string(),
                                'hiring-manager': z.string(),
                                'job-title': z.string(),
                                language: z.string(),
                                'last-updated': z.string(),
                                location: z.string(),
                                owner: z.string(),
                                'publish-date': z.string(),
                                seniority: z
                                    .object({
                                        title: z.string(),
                                    })
                                    .partial(),
                                shortlist: z
                                    .object({
                                        candidates: z.string(),
                                        candidates_one: z.string(),
                                        candidates_zero: z.string(),
                                        'confirm-remove-dialog': z
                                            .object({
                                                'cancel-button': z.string(),
                                                'confirmation-message': z.string(),
                                                headline: z.string(),
                                                'remove-button': z.string(),
                                            })
                                            .partial(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'skill-groups': z.string(),
                                skills: z.string(),
                                'start-date': z.string(),
                                state: z
                                    .object({
                                        title: z.string(),
                                    })
                                    .partial(),
                                title: z.string(),
                                'work-place': z
                                    .object({
                                        title: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        'create-button': z.string(),
                        filters: z
                            .object({
                                'drawer-title': z.string(),
                                'workspace-preferences': z.object({
                                    'sub-title': z.string(),
                                    title: z.string(),
                                }),
                            })
                            .partial(),
                        subtitle: z.string(),
                        tab: z
                            .object({
                                all: z.string(),
                                archived: z.string(),
                                draft: z.string(),
                                hired: z.string(),
                                published: z.string(),
                            })
                            .partial(),
                        title: z.string(),
                        toolbar: z
                            .object({
                                'owner-filter-label': z.string(),
                                'quick-search-placeholder': z.string(),
                                'shortlist-filter-label': z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                'landing-page': z
                    .object({
                        career: z.string(),
                        engagement: z.string(),
                        legal: z
                            .object({
                                prefix: z.string(),
                                'privacy-policy': z.string(),
                                suffix: z.string(),
                            })
                            .partial(),
                        learning: z.string(),
                        skills: z
                            .object({
                                backend: z.string(),
                                'emotional-design': z.string(),
                                fullstack: z.string(),
                                leadership: z.string(),
                                'problem-solving': z.string(),
                                sensor: z.string(),
                                'solution-selling': z.string(),
                                strategy: z.string(),
                            })
                            .partial(),
                        subtitle: z.string(),
                        title: z.string(),
                        welcome: z.string(),
                        'welcome-back': z.string(),
                    })
                    .partial(),
                'learning-opportunities': z
                    .object({
                        columns: z
                            .object({
                                description: z.string(),
                            })
                            .partial(),
                        'course-title': z.string(),
                        'create-button': z.string(),
                        description: z.string(),
                        filters: z
                            .object({
                                'drawer-title': z.string(),
                                'skills-preferences': z
                                    .object({
                                        'selected-skills': z.string(),
                                        'sub-title': z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'workspace-preferences': z
                                    .object({
                                        'sub-title': z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        'general-info-title': z.string(),
                        headlines: z
                            .object({
                                created: z.string(),
                                'external-id': z.string(),
                                'learning-language': z.string(),
                                owner: z.string(),
                                provider: z.string(),
                                skills: z.string(),
                                state: z.string(),
                                title: z.string(),
                                'updated-at': z.string(),
                            })
                            .partial(),
                        language: z.string(),
                        'learning-language': z.string(),
                        navigation: z
                            .object({
                                courses: z.string(),
                            })
                            .partial(),
                        'archive-confirmation-description': z.string(),
                        'delete-confirmation-description': z.string(),
                        'delete-confirmation-title': z.string(),
                        'owner-name': z.string(),
                        provider: z.string(),
                        'publish-button': z.string(),
                        'publish-notification': z.string(),
                        'skills-title': z.string(),
                        state: z.string(),
                        states: z
                            .object({
                                all: z.string(),
                                archived: z.string(),
                                draft: z.string(),
                                published: z.string(),
                            })
                            .partial(),
                        statistics: z
                            .object({
                                'archived-tooltip': z.string(),
                                'draft-tooltip': z.string(),
                                'published-tooltip': z.string(),
                                'total-tooltip': z.string(),
                            })
                            .partial(),
                        subtitle: z.string(),
                        title: z.string(),
                        'title-placeholder': z.string(),
                        toolbar: z
                            .object({
                                'quick-search-placeholder': z.string(),
                            })
                            .partial(),
                        'trained-skills': z.string(),
                        'translation-original': z.string(),
                        'translation-original-hint': z.string(),
                        type: z.string(),
                        types: z
                            .object({
                                course: z.string(),
                            })
                            .partial(),
                        url: z.string(),
                        'url-invalid': z.string(),
                    })
                    .partial(),
                'learning-opportunity-details': z
                    .object({
                        columns: z
                            .object({
                                'featured-to': z.string(),
                            })
                            .partial(),
                        'no-featured-users': z.string(),
                        tabs: z
                            .object({
                                'course-description': z.string(),
                                featured: z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                'learning-opportunity-recommendations-by-skill-ids': z
                    .object({
                        headline: z.string(),
                        'sub-headline': z.string(),
                    })
                    .partial(),
                'my-growth': z
                    .object({
                        bookmarks: z
                            .object({
                                'search-placeholder': z.string(),
                            })
                            .partial(),
                        headline: z.string(),
                        'sub-headline': z.string(),
                        tabs: z
                            .object({
                                bookmarks: z.string(),
                                featured: z.string(),
                            })
                            .partial(),
                        title: z.string(),
                    })
                    .partial(),
                onboarding: z
                    .object({
                        'change-settings-in-profile-settings': z.string(),
                        consent: z
                            .object({
                                'consent-continue': z.string(),
                                headline: z.string(),
                                'open-dialog': z.string(),
                            })
                            .partial(),
                        'create-profile': z
                            .object({
                                'about-you': z.string(),
                                accept: z.string(),
                                'ambitions-missing': z.string(),
                                'create-profile': z.string(),
                                done: z.string(),
                                edit: z.string(),
                                'edit-ambitions-label': z.string(),
                                'edit-description-label': z.string(),
                                'edit-skills-label': z.string(),
                                'edit-your-ambitions': z.string(),
                                'edit-your-skills': z.string(),
                                'found-ambitions': z.string(),
                                'found-language': z.string(),
                                'found-skills': z.string(),
                                'generate-ambitions-failed': z.string(),
                                'generate-ambitions-label': z.string(),
                                'generate-description-failed': z.string(),
                                'generate-description-label': z.string(),
                                'generate-skills-failed': z.string(),
                                'generate-skills-label': z.string(),
                                'hang-on': z.string(),
                                'search-ambitions': z.string(),
                                'search-language': z.string(),
                                'search-skills': z.string(),
                                'skills-missing': z.string(),
                                title: z.string(),
                                'title-long': z.string(),
                                'try-again': z.string(),
                                'we-generate': z.string(),
                                'what-are-you-interested-in': z.string(),
                                'your-ambitions': z.string(),
                                'your-chosen-ambitions': z.string(),
                                'your-chosen-language': z.string(),
                                'your-chosen-skills': z.string(),
                                'your-skills': z.string(),
                            })
                            .partial(),
                        'cv-manual': z
                            .object({
                                description: z.string(),
                            })
                            .partial(),
                        'cv-upload': z
                            .object({
                                continue: z.string(),
                                description: z.string(),
                                dropzone: z
                                    .object({
                                        description: z.string(),
                                        disclaimer: z.string(),
                                        'file-max-size-message': z.string(),
                                        'format-size-error': z.string(),
                                    })
                                    .partial(),
                                errors: z
                                    .object({
                                        analysis: z.string(),
                                        format: z.string(),
                                        parse: z.string(),
                                        'profile-creation': z.string(),
                                        upload: z.string(),
                                    })
                                    .partial(),
                                'processing-skills': z.string(),
                            })
                            .partial(),
                        dialog: z
                            .object({
                                career: z
                                    .object({
                                        'experience-level': z.string(),
                                        'next-step': z.string(),
                                        subtitle: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                finish: z
                                    .object({
                                        action: z.string(),
                                        text: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                settings: z
                                    .object({
                                        notification: z.string(),
                                        subtitle: z.string(),
                                        title: z.string(),
                                        visibility: z.string(),
                                    })
                                    .partial(),
                                skip: z.string(),
                                welcome: z
                                    .object({
                                        'next-step': z.string(),
                                        text: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        dropzone: z
                            .object({
                                'file-max-size-message': z.string(),
                                'format-size-error': z.string(),
                                'linkedin-cta': z.string(),
                                'pdf-cta': z.string(),
                            })
                            .partial(),
                        errors: z
                            .object({
                                analysis: z.string(),
                                format: z.string(),
                                parse: z.string(),
                                'profile-creation': z.string(),
                                upload: z.string(),
                            })
                            .partial(),
                        headline: z.string(),
                        tagline: z.string(),
                        manual: z
                            .object({
                                'back-to-upload': z.string(),
                                submit: z.string(),
                                subtitle: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'onboarding-success': z
                            .object({
                                congratulations: z.string(),
                                'go-to-dashboard': z.string(),
                                'success-message': z.string(),
                            })
                            .partial(),
                        'personal-information': z
                            .object({
                                department: z.string(),
                                'department-helper-text': z.string(),
                                headline: z.string(),
                                seniority: z.string(),
                                'seniority-helper-text': z.string(),
                                'title-helper-text': z.string(),
                            })
                            .partial(),
                        'profile-visibility': z
                            .object({
                                everything: onboardingProfileVisibilitySettingSchema,
                                nothing: onboardingProfileVisibilitySettingSchema,
                                recruiters: onboardingProfileVisibilitySettingSchema,
                                skills: onboardingProfileVisibilitySettingSchema,
                            })
                            .partial(),
                        'specify-role': z
                            .object({
                                description: z.string(),
                                label: z.string(),
                                'skills-selected': z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'sub-headline': z.string(),
                        upload: z
                            .object({
                                linkedin: z.string(),
                                'need-to-select-file': z.string(),
                                'pdf-file': z.string(),
                                'processing-skills': z.string(),
                                'skip-to-manual': z.string(),
                                'step-1': z.string(),
                                'step-1-description': z.string(),
                                'step-1-image-alt': z.string(),
                                'step-2': z.string(),
                                submit: z.string(),
                                subtitle: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        welcome: z
                            .object({
                                'get-started': z.string(),
                                intro: z.string(),
                                subtitle: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                'personal-home': z
                    .object({
                        aligning: z
                            .object({
                                aligned: z.string(),
                                continue: z.string(),
                                'in-progress': z.string(),
                                open: z.string(),
                                'with-manager': z.string(),
                            })
                            .partial(),
                        alignment: z
                            .object({
                                automations: z.string(),
                                label: z.string(),
                                since: z.string(),
                            })
                            .partial(),
                        automations: z
                            .object({
                                next: z.string(),
                                page: z.string(),
                                pages: z.string(),
                                previous: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        empty: z
                            .object({
                                'no-position': z.string(),
                                'no-position-hint': z.string(),
                                'no-tasks': z.string(),
                            })
                            .partial(),
                        head: z
                            .object({
                                'aligned-since': z.string(),
                                'open-position': z.string(),
                                'your-position': z.string(),
                            })
                            .partial(),
                        'load-error': z.string(),
                        position: z.string(),
                        retry: z.string(),
                        stack: z
                            .object({
                                automations: z.string(),
                                automations_one: z.string(),
                                automations_zero: z.string(),
                                'open-task': z.string(),
                                'tasks-below': z.string(),
                                'tasks-below_one': z.string(),
                                unavailable: z.string(),
                                view: z
                                    .object({
                                        cards: z.string(),
                                        label: z.string(),
                                        list: z.string(),
                                    })
                                    .partial(),
                                'your-tasks': z.string(),
                            })
                            .partial(),
                        tasks: z.string(),
                    })
                    .partial(),
                'position-alignment': z
                    .object({
                        'automations-step': z
                            .object({
                                change: z.string(),
                                counter: z.string(),
                                'deselect-label': z.string(),
                                details: z.string(),
                                done: z.string(),
                                generating: z.string(),
                                guidance: z
                                    .object({
                                        confirmed: z.string(),
                                        'confirmed-undated': z.string(),
                                        'sent-back': z.string(),
                                        start: z.string(),
                                        'with-manager': z.string(),
                                    })
                                    .partial(),
                                hint: z.string(),
                                'hint-read-only': z.string(),
                                'intro-employee-empty': z.string(),
                                'intro-manager-empty': z.string(),
                                'limit-reached': z.string(),
                                'no-tasks': z.string(),
                                pending: z.string(),
                                recommendations: z.string(),
                                remove: z.string(),
                                select: z.string(),
                                'select-label': z.string(),
                                selected: z.string(),
                                'selected-count': z.string(),
                                'selected-count_one': z.string(),
                                task: z.string(),
                                'task-removed': z.string(),
                                unavailable: z.string(),
                                untitled: z.string(),
                            })
                            .partial(),
                        overview: z
                            .object({
                                employee: z
                                    .object({
                                        'automations-text': z.string(),
                                        'automations-title': z.string(),
                                        intro: z.string(),
                                        'position-text': z.string(),
                                        'position-title': z.string(),
                                        'tasks-text': z.string(),
                                        'tasks-title': z.string(),
                                    })
                                    .partial(),
                                manager: z
                                    .object({
                                        'automations-text': z.string(),
                                        'automations-title': z.string(),
                                        intro: z.string(),
                                        'position-text': z.string(),
                                        'position-title': z.string(),
                                        'tasks-text': z.string(),
                                        'tasks-title': z.string(),
                                    })
                                    .partial(),
                                agreed: z.string(),
                                'agreed-undated': z.string(),
                                answer: z
                                    .object({
                                        'employee-confirmed': z.string(),
                                        'employee-not-confirmed': z.string(),
                                        'manager-confirmed': z.string(),
                                        'manager-not-confirmed': z.string(),
                                        none: z.string(),
                                    })
                                    .partial(),
                                'automations-none': z.string(),
                                'open-comments': z.string(),
                                'open-comments_one': z.string(),
                                'sent-back-by': z.string(),
                                'tasks-described': z.string(),
                            })
                            .partial(),
                        bar: z
                            .object({
                                'employee-heading': z.string(),
                                'manager-heading': z.string(),
                                'sent-back': z.string(),
                                'steps-label': z.string(),
                            })
                            .partial(),
                        comments: z
                            .object({
                                add: z.string(),
                                'composer-placeholder': z.string(),
                                'composer-submit': z.string(),
                                empty: z.string(),
                                heading: z.string(),
                                'open-count': z.string(),
                                'read-only-copy': z.string(),
                                'rejected-copy': z.string(),
                                resolved: z.string(),
                                'resolved-toggle': z.string(),
                                'row-button': z.string(),
                                'row-button-count': z.string(),
                                'target-automation': z.string(),
                                'target-automation-unnamed': z.string(),
                                'target-general': z.string(),
                                'target-row': z.string(),
                                'target-section': z.string(),
                                'target-section-unnamed': z.string(),
                                'target-task': z.string(),
                                'target-task-unnamed': z.string(),
                            })
                            .partial(),
                        compare: z
                            .object({
                                columns: z
                                    .object({
                                        base: z.string(),
                                        current: z.string(),
                                        mine: z.string(),
                                        theirs: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        'former-user': z.string(),
                        'empty-me': z.string(),
                        'rejected-previous': z.string(),
                        errors: z
                            .object({
                                'already-open': z.string(),
                                'comment-required': z.string(),
                                generic: z.string(),
                                incomplete: z.string(),
                                'not-a-direct-report': z.string(),
                                'not-allowed': z.string(),
                                'not-found': z.string(),
                                'stale-revision': z.string(),
                                'step-confirmed': z.string(),
                                'tasks-generating': z.string(),
                            })
                            .partial(),
                        'load-error': z.string(),
                        manager: z
                            .object({
                                accept: z
                                    .object({
                                        accepted: z.string(),
                                        action: z.string(),
                                        'blocked-generating': z.string(),
                                        'blocked-generic': z.string(),
                                        'blocked-steps': z.string(),
                                        description: z.string(),
                                        saved: z.string(),
                                    })
                                    .partial(),
                                actions: z
                                    .object({
                                        'confirm-automations': z.string(),
                                        'confirm-position': z.string(),
                                        'confirm-tasks': z.string(),
                                        'send-back': z.string(),
                                        'undo-send-back': z.string(),
                                        withdraw: z.string(),
                                    })
                                    .partial(),
                                cancel: z.string(),
                                complete: z
                                    .object({
                                        action: z.string(),
                                        description: z.string(),
                                        ready: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'send-back': z
                                    .object({
                                        description: z.string(),
                                        'reason-label': z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                edit: z
                                    .object({
                                        save: z.string(),
                                    })
                                    .partial(),
                                guidance: z
                                    .object({
                                        'changed-after-view': z.string(),
                                        confirmed: z.string(),
                                        'sent-back-undated': z.string(),
                                        'not-sent-waiting': z.string(),
                                        'sent-back': z.string(),
                                        'waits-automations': z.string(),
                                        'waits-automations_one': z.string(),
                                        'waits-automations-none': z.string(),
                                        'waits-position-confirmed': z.string(),
                                        'waits-position-not-confirmed': z.string(),
                                        'waits-tasks': z.string(),
                                    })
                                    .partial(),
                                reject: z
                                    .object({
                                        action: z.string(),
                                        confirm: z.string(),
                                        description: z.string(),
                                        'reason-label': z.string(),
                                        'reason-placeholder': z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        'position-step': z
                            .object({
                                'decline-dialog': z
                                    .object({
                                        description: z.string(),
                                        'reason-label': z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                confirm: z.string(),
                                confirmed: z.string(),
                                'confirmed-undated': z.string(),
                                decline: z.string(),
                                intro: z.string(),
                                'latest-changes': z.string(),
                                'new-revision': z.string(),
                                'sent-back': z.string(),
                                untitled: z.string(),
                                'with-manager-confirmed': z.string(),
                                'with-manager-not-confirmed': z.string(),
                            })
                            .partial(),
                        retry: z.string(),
                        status: z
                            .object({
                                employee: z
                                    .object({
                                        changes: z.string(),
                                        confirmed: z.string(),
                                        'with-manager': z.string(),
                                        yours: z.string(),
                                    })
                                    .partial(),
                                manager: z
                                    .object({
                                        changes: z.string(),
                                        confirmed: z.string(),
                                        'with-manager': z.string(),
                                        yours: z.string(),
                                    })
                                    .partial(),
                                phase: z
                                    .object({
                                        aligned: z.string(),
                                        'employee-in-progress': z.string(),
                                        'employee-with-manager': z.string(),
                                        'manager-in-progress': z.string(),
                                        'manager-with-manager': z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        steps: z
                            .object({
                                automations: z.string(),
                                overview: z.string(),
                                position: z.string(),
                                tasks: z.string(),
                            })
                            .partial(),
                        'tasks-step': z
                            .object({
                                practice: z
                                    .object({
                                        'documented-steps': z.string(),
                                        'show-details': z.string(),
                                        'hide-details': z.string(),
                                        loading: z.string(),
                                        retry: z.string(),
                                        'no-steps': z.string(),
                                        matches: z.string(),
                                        yes: z.string(),
                                        no: z.string(),
                                        differently: z.string(),
                                        differences: z.string(),
                                        way: z.string(),
                                        answers: z
                                            .object({
                                                'not-doing': z.string(),
                                                'without-ai': z.string(),
                                                assistant: z.string(),
                                                agent: z.string(),
                                            })
                                            .partial(),
                                        description: z
                                            .object({
                                                'not-doing': z.string(),
                                                'without-ai': z.string(),
                                                assistant: z.string(),
                                                agent: z.string(),
                                            })
                                            .partial(),
                                        frequency: z.string(),
                                        frequencies: z
                                            .object({
                                                daily: z.string(),
                                                weekly: z.string(),
                                                monthly: z.string(),
                                                rarely: z.string(),
                                            })
                                            .partial(),
                                        tools: z.string(),
                                        platforms: z.string(),
                                        'add-tool': z.string(),
                                        add: z.string(),
                                        'uses-skills': z.string(),
                                        'skill-file': z.string(),
                                        'skill-file-soon': z.string(),
                                        skills: z.string(),
                                        'no-skills': z.string(),
                                        actual: z.string(),
                                        'actual-after-answer': z.string(),
                                        saving: z.string(),
                                        'not-answered': z.string(),
                                        percentage: z.string(),
                                        potential: z.string(),
                                        pending: z.string(),
                                        unavailable: z.string(),
                                        summary: z.string(),
                                        remaining: z.string(),
                                        remaining_one: z.string(),
                                    })
                                    .partial(),
                                'band-count': z.string(),
                                'band-count_one': z.string(),
                                'band-empty': z.string(),
                                'task-details': z.string(),
                                bands: z
                                    .object({
                                        contribution: z.string(),
                                        routine: z.string(),
                                    })
                                    .partial(),
                                change: z.string(),
                                'changes-none': z.string(),
                                'changes-removed': z.string(),
                                confirm: z.string(),
                                confirmed: z.string(),
                                'sent-back': z.string(),
                                empty: z.string(),
                                keep: z.string(),
                                next: z.string(),
                                'not-described': z.string(),
                                'not-described-employee': z.string(),
                                'not-described-manager': z.string(),
                                place: z.string(),
                                previous: z.string(),
                                'proposed-removal': z.string(),
                                'removed-by': z.string(),
                                'removed-by-you': z.string(),
                                'readout-employee': z.string(),
                                'readout-manager': z.string(),
                                'removal-employee': z.string(),
                                'removal-manager': z.string(),
                                remove: z.string(),
                                withdraw: z.string(),
                            })
                            .partial(),
                        team: z
                            .object({
                                empty: z.string(),
                                'empty-hint': z.string(),
                                heading: z.string(),
                                'all-employees': z.string(),
                                'needs-attention': z.string(),
                                'start-new': z.string(),
                                'previous-alignments': z.string(),
                                'nothing-waiting': z.string(),
                                'open-position-management': z.string(),
                                open: z.string(),
                                progress: z
                                    .object({
                                        agreed: z.string(),
                                        'agreed-count': z.string(),
                                        'agreed-so-far': z.string(),
                                        'in-progress': z.string(),
                                        'in-review': z.string(),
                                        label: z.string(),
                                        'row-label': z.string(),
                                        'sent-back': z.string(),
                                        started: z.string(),
                                        'steps-done': z.string(),
                                        'waiting-on-you': z.string(),
                                        'waiting-on-you_one': z.string(),
                                    })
                                    .partial(),
                                review: z.string(),
                                people: z.string(),
                                people_one: z.string(),
                                'position-started': z.string(),
                                'open-alignment': z.string(),
                                'start-dialog': z
                                    .object({
                                        confirm: z.string(),
                                        description: z.string(),
                                        'new-description': z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                start: z.string(),
                                'waiting-on-you': z.string(),
                                'waiting-on-you_one': z.string(),
                            })
                            .partial(),
                        regenerate: z
                            .object({
                                placeholder: z.string(),
                            })
                            .partial(),
                        'title-me': z.string(),
                        'title-team': z.string(),
                        'unknown-person': z.string(),
                        'unsaved-edits': z.string(),
                    })
                    .partial(),
                'position-management': z
                    .object({
                        access: z.object({
                            'add-area': z.string(),
                            'any-value': z.string(),
                            area: z.string(),
                            'areas-title': z.string(),
                            assign: z.string(),
                            'by-conditions': z.string(),
                            'by-map-view': z.string(),
                            'coverage-load-error': z.string(),
                            'covered-by': z.string(),
                            'current-areas': z.string(),
                            'grants-load-error': z.string(),
                            'no-areas-for-person': z.string(),
                            'no-candidates': z.string(),
                            'no-overlap': z.string(),
                            'node-not-assignable': z.string(),
                            'partly-overlaps': z.string(),
                            person: z.string(),
                            'pick-person': z.string(),
                            revoke: z.string(),
                            title: z.string(),
                            'whole-architecture': z.string(),
                        }),
                        invitations: z.object({
                            'assign-new': z.string(),
                            'defer-and-assign': z.string(),
                            'invite-and-assign': z.string(),
                            timing: z.string(),
                            later: z.string(),
                            now: z.string(),
                            'deferred-description': z.string(),
                            'immediate-description': z.string(),
                            'send-all': z.string(),
                            'confirm-send': z.string(),
                            'release-title': z.string(),
                            'company-scope': z.string(),
                            gradual: z.string(),
                            progress: z.string(),
                            released: z.string(),
                            'status-deferred': z.string(),
                            'status-queued': z.string(),
                            'status-sent': z.string(),
                            'status-failed': z.string(),
                        }),
                        architecture: z.object({
                            create: z.string(),
                            delete: z.string(),
                            'delete-description': z.string(),
                            'delete-description-with-count': z.string(),
                            'delete-description-with-count_one': z.string(),
                            label: z.string(),
                            'no-architectures': z.string(),
                        }),
                        board: z.object({
                            'select-parent': z.string(),
                        }),
                        'chart-settings': z.object({
                            attributes: z.object({
                                archive: z.string(),
                                create: z.string(),
                                'data-type': z.string(),
                                'data-types': z.object({
                                    boolean: z.string(),
                                    date: z.string(),
                                    number: z.string(),
                                    option: z.string(),
                                    text: z.string(),
                                }),
                                delete: z.string(),
                                'delete-description': z.string(),
                                description: z.string(),
                                edit: z.string(),
                                grouping: z.string(),
                                key: z.string(),
                                'key-hint': z.string(),
                                label: z.string(),
                                'no-attributes': z.string(),
                                restore: z.string(),
                                'tree-node': z.string(),
                                'tree-node-hint': z.string(),
                            }),
                            dimensions: z.object({
                                add: z.string(),
                                'add-placeholder': z.string(),
                                hint: z.string(),
                                'move-down': z.string(),
                                'move-up': z.string(),
                                'no-candidates': z.string(),
                                remove: z.string(),
                                title: z.string(),
                            }),
                            options: z.object({
                                archive: z.string(),
                                create: z.string(),
                                delete: z.string(),
                                'delete-description': z.string(),
                                'delete-description-with-children': z.string(),
                                edit: z.string(),
                                'external-code': z.string(),
                                'no-options': z.string(),
                                'no-parent': z.string(),
                                parent: z.string(),
                                restore: z.string(),
                                value: z.string(),
                                'value-in-use': z.string(),
                            }),
                            tabs: z.object({
                                attributes: z.string(),
                                general: z.string(),
                                views: z.string(),
                            }),
                            title: z.string(),
                            views: z.object({
                                create: z.string(),
                                default: z.string(),
                                delete: z.string(),
                                'delete-description': z.string(),
                                'make-default': z.string(),
                                'no-dimensions-hint': z.string(),
                                'no-views': z.string(),
                                rename: z.string(),
                            }),
                        }),
                        'connected-job-architecture': z.object({
                            change: z.string(),
                            connect: z.string(),
                            description: z.string(),
                            gate: z.string(),
                            'gate-no-permission': z.string(),
                            generating: z.string(),
                            'levels-count': z.string(),
                            'levels-count_one': z.string(),
                            'no-levels': z.string(),
                            'roles-count': z.string(),
                            'roles-count_one': z.string(),
                            'run-in-progress': z.string(),
                            title: z.string(),
                            unavailable: z.string(),
                        }),
                        'create-position': z.object({
                            'areas-not-settable': z.string(),
                            'based-on-level': z.string(),
                            'change-level': z.string(),
                            'contains-selected-level': z.string(),
                            'from-level': z.string(),
                            'from-level-description': z.string(),
                            'from-scratch': z.string(),
                            'from-scratch-description': z.string(),
                            'job-architecture': z.string(),
                            'link-to-level': z.string(),
                            'link-to-level-hint': z.string(),
                            'list-empty': z.string(),
                            'list-load-error': z.string(),
                            'list-truncated': z.string(),
                            'list-truncated-groups': z.string(),
                            'no-level': z.string(),
                            'no-matching-levels': z.string(),
                            'outside-areas': z.string(),
                            'search-levels': z.string(),
                            'search-min-length': z.string(),
                            'select-on-left': z.string(),
                            title: z.string(),
                            'untitled-level': z.string(),
                        }),
                        'default-architecture': z.object({
                            badge: z.string(),
                            'confirm-description': z.string(),
                            'confirm-title': z.string(),
                            'delete-hint': z.string(),
                            description: z.string(),
                            empty: z.string(),
                            'is-default': z.string(),
                            'make-default': z.string(),
                            'no-default': z.string(),
                            'open-position-management': z.string(),
                            'other-default': z.string(),
                            placeholder: z.string(),
                            title: z.string(),
                        }),
                        detail: z.object({
                            archive: z.string(),
                            'archive-description': z.string(),
                            'archive-title': z.string(),
                            'archived-banner': z.string(),
                            'archived-option-in-use': z.string(),
                            people: z.object({
                                assign: z.string(),
                                'assign-employee': z.string(),
                                'assign-error': z.string(),
                                'change-employee': z.string(),
                                'confirm-employee-action': z.string(),
                                'confirm-employee-description': z.string(),
                                'confirm-employee-title': z.string(),
                                'confirm-manager-action': z.string(),
                                'confirm-manager-description': z.string(),
                                'confirm-manager-title': z.string(),
                                employee: z.string(),
                                manager: z.string(),
                                'no-manager': z.string(),
                                'not-assigned': z.string(),
                                'not-published': z.string(),
                                'not-reviewed': z.string(),
                                'set-manager': z.string(),
                                vacant: z.string(),
                            }),
                            automation: z.object({
                                'hours-column': z.string(),
                                levers: z.object({
                                    'agentic-ai': z.string(),
                                    'other-tool': z.string(),
                                    'physical-automation': z.string(),
                                    'software-rpa': z.string(),
                                }),
                                'not-assessed': z.string(),
                                potential: z.string(),
                                task: z.string(),
                                'task-hours': z.string(),
                                'task-total-hours': z.string(),
                                title: z.string(),
                            }),
                            breadcrumb: z.string(),
                            'change-manager': z.string(),
                            changelog: z.object({
                                empty: z.string(),
                                event: z.object({
                                    archived: z.string(),
                                    'assignee-changed': z.string(),
                                    'assignee-removed': z.string(),
                                    created: z.string(),
                                    deleted: z.string(),
                                    'external-id-changed': z.string(),
                                    'external-id-removed': z.string(),
                                    'level-linked': z.string(),
                                    'level-unlinked': z.string(),
                                    'occupancy-changed': z.string(),
                                    'occupancy-removed': z.string(),
                                    published: z.string(),
                                    restored: z.string(),
                                    reviewed: z.string(),
                                    'reviewer-assigned': z.string(),
                                    'reviewer-removed': z.string(),
                                    saved: z.string(),
                                    unknown: z.string(),
                                    unpublished: z.string(),
                                    unreviewed: z.string(),
                                }),
                                'load-error': z.string(),
                                loading: z.string(),
                                open: z.string(),
                                someone: z.string(),
                                title: z.string(),
                                unavailable: z.string(),
                            }),
                            conflict: z.object({
                                concurrent: z.string(),
                                discard: z.string(),
                                'keep-editing': z.string(),
                                stale: z.string(),
                            }),
                            'copy-link': z.string(),
                            'create-option': z.string(),
                            'create-top-level-option': z.string(),
                            delete: z.string(),
                            'delete-description': z.string(),
                            'edit-disabled': z.object({
                                archived: z.string(),
                                'no-permission': z.string(),
                                'not-latest': z.string(),
                                translating: z.string(),
                            }),
                            'edit-latest': z.string(),
                            'edit-with-ai-unavailable': z.string(),
                            'employment-type': z.object({
                                apprentice: z.string(),
                                contractor: z.string(),
                                employee: z.string(),
                                intern: z.string(),
                                temp: z.string(),
                                trainee: z.string(),
                                'working-student': z.string(),
                            }),
                            fields: z.object({
                                'employment-type': z.string(),
                                'reports-to': z.string(),
                            }),
                            level: z.object({
                                'auto-link-description': z.string(),
                                'auto-link-description-no-copy': z.string(),
                                'auto-link-none': z.string(),
                                'auto-link-title': z.string(),
                                'based-on': z.string(),
                                'based-on-version': z.string(),
                                change: z.string(),
                                'choose-manually': z.string(),
                                copy: z.string(),
                                'copy-banner': z.string(),
                                'copy-confirm-description': z.string(),
                                'copy-confirm-description-current': z.string(),
                                'copy-submit': z.string(),
                                link: z.string(),
                                'link-banner': z.string(),
                                'link-submit': z.string(),
                                'other-job-architecture': z.string(),
                                sync: z.string(),
                                'sync-compare': z.string(),
                                'sync-description': z.string(),
                                'sync-title': z.string(),
                                unlink: z.string(),
                                'update-banner': z.string(),
                                'update-banner-action': z.string(),
                            }),
                            'link-copied': z.string(),
                            'load-error': z.string(),
                            loading: z.string(),
                            'no-manager-candidates': z.string(),
                            'no-matching-options': z.string(),
                            'no-matching-people': z.string(),
                            'not-set': z.string(),
                            'open-new-tab': z.string(),
                            pay: z.object({
                                band: z.string(),
                                benefits: z.string(),
                                'career-level': z.string(),
                                'career-level-empty': z.string(),
                                'career-level-no-matches': z.string(),
                                'career-level-none': z.string(),
                                'career-level-search': z.string(),
                                bonus: z.string(),
                                classification: z.string(),
                                currency: z.string(),
                                grade: z.string(),
                                lti: z.string(),
                                region: z.string(),
                                rung: z.string(),
                                'salary-maximum': z.string(),
                                'salary-midpoint': z.string(),
                                'salary-minimum': z.string(),
                                title: z.string(),
                                type: z.string(),
                                'type-hourly': z.string(),
                                'type-salaried': z.string(),
                            }),
                            'people-load-error': z.string(),
                            profile: z.object({
                                empty: z.string(),
                                untitled: z.string(),
                            }),
                            restore: z.string(),
                            'restore-position': z.string(),
                            review: z.object({
                                'assign-myself': z.string(),
                                'assign-reviewer': z.string(),
                                'confirm-publish-description': z.string(),
                                'confirm-publish-title': z.string(),
                                'confirm-review-description': z.string(),
                                'confirm-review-description-new-reviewer': z.string(),
                                'confirm-review-description-replace-reviewer': z.string(),
                                'confirm-review-title': z.string(),
                                'confirm-unpublish-description': z.string(),
                                'confirm-unpublish-title': z.string(),
                                'confirm-unreview-description': z.string(),
                                'confirm-unreview-title': z.string(),
                                disabled: z.object({
                                    archived: z.string(),
                                    'awaiting-review': z.string(),
                                    'not-reviewed': z.string(),
                                }),
                                'mark-reviewed': z.string(),
                                publish: z.string(),
                                'reset-publication': z.string(),
                                'reset-review': z.string(),
                            }),
                            'search-managers': z.string(),
                            'search-options': z.string(),
                            'search-or-add-options': z.string(),
                            'search-people': z.string(),
                            tabs: z.object({
                                details: z.string(),
                                overview: z.string(),
                                tasks: z.string(),
                            }),
                            translation: z.object({
                                failed: z.string(),
                                pending: z.string(),
                                retry: z.string(),
                            }),
                            'value-error': z.object({
                                date: z.string(),
                                invalid: z.string(),
                                'max-length': z.string(),
                                'max-length_one': z.string(),
                                maximum: z.string(),
                                minimum: z.string(),
                                number: z.string(),
                                pattern: z.string(),
                            }),
                            versions: z.object({
                                'aria-label': z.string(),
                                compare: z.string(),
                                version: z.string(),
                            }),
                        }),
                        errors: z.object({
                            'access-coverage-changed': z.string(),
                            'access-grant-duplicate': z.string(),
                            'access-grant-invalid': z.string(),
                            'access-grant-reference': z.string(),
                            'access-grant-target-invalid': z.string(),
                            'architecture-is-default': z.string(),
                            'attribute-in-use': z.string(),
                            'attribute-key-in-use': z.string(),
                            'attribute-not-editable': z.string(),
                            'chart-view-invalid': z.string(),
                            'concurrent-change': z.string(),
                            'external-id-in-use': z.string(),
                            generic: z.string(),
                            'invalid-reporting-line': z.string(),
                            'invalid-value': z.string(),
                            'item-not-found': z.string(),
                            'manager-outside-area': z.string(),
                            'not-a-level': z.string(),
                            'option-in-use': z.string(),
                            'outside-access-area': z.string(),
                            'position-archived': z.string(),
                            'position-not-found': z.string(),
                            rejected: z.string(),
                            'review-denied': z.string(),
                            'reviewer-invalid': z.string(),
                            'stale-version': z.string(),
                            'translation-retry-invalid': z.string(),
                            'version-not-found': z.string(),
                        }),
                        layout: z.object({
                            board: z.string(),
                            canvas: z.string(),
                            table: z.string(),
                        }),
                        'level-mapping': z.object({
                            'apply-failed': z.string(),
                            applying: z.string(),
                            'architecture-picker-label': z.string(),
                            'back-to-positions': z.string(),
                            'career-level': z.string(),
                            'career-level-detail': z.string(),
                            change: z.string(),
                            'create-level': z.string(),
                            'create-level-prompt': z.string(),
                            'create-level-prompt-in-department': z.string(),
                            department: z.string(),
                            errors: z.object({
                                'attributes-invalid': z.string(),
                                'level-not-available': z.string(),
                                'no-levels': z.string(),
                                'nothing-to-map': z.string(),
                                'run-changed': z.string(),
                            }),
                            'grouping-missing': z.string(),
                            headcount: z.string(),
                            headcount_one: z.string(),
                            'leave-unlinked': z.string(),
                            'legal-entity': z.string(),
                            'levels-to-match': z.string(),
                            'levels-to-match_one': z.string(),
                            'levels-without-profile': z.string(),
                            'levels-without-profile_one': z.string(),
                            linked: z.string(),
                            linked_one: z.string(),
                            matching: z.string(),
                            none: z.string(),
                            open: z.string(),
                            other: z.string(),
                            'pick-level': z.string(),
                            preparing: z.string(),
                            'position-title': z.string(),
                            'roles-with-levels': z.string(),
                            'roles-without-levels': z.string(),
                            'roles-without-levels-detail': z.string(),
                            'roles-without-levels_one': z.string(),
                            'run-failed': z.string(),
                            skipped: z.string(),
                            skipped_one: z.string(),
                            'start-description': z.string(),
                            'start-over': z.string(),
                            steps: z.object({
                                confident: z.string(),
                                decide: z.string(),
                                unassigned: z.string(),
                            }),
                            title: z.string(),
                            unlinked: z.string(),
                        }),
                        line: z.object({
                            chip: z.string(),
                            clear: z.string(),
                            'open-position': z.string(),
                            'selected-position': z.string(),
                        }),
                        'load-error': z.string(),
                        loading: z.string(),
                        'manager-change': z.object({
                            assign: z.string(),
                            'chip-add': z.string(),
                            'chip-remove': z.string(),
                            'finished-add': z.string(),
                            'finished-add_one': z.string(),
                            'finished-remove': z.string(),
                            'finished-remove_one': z.string(),
                            'finished-skipped': z.string(),
                            'finished-skipped_one': z.string(),
                            'legend-draft': z.string(),
                            'legend-draft_one': z.string(),
                            'legend-not-published': z.string(),
                            'legend-not-published_one': z.string(),
                            'legend-pending': z.string(),
                            'legend-pending_one': z.string(),
                            'legend-published': z.string(),
                            'legend-published_one': z.string(),
                            'legend-skipped': z.string(),
                            'legend-skipped_one': z.string(),
                            manager: z.string(),
                            'manager-unavailable': z.string(),
                            'managers-load-error': z.string(),
                            'no-holder': z.string(),
                            'no-managers': z.string(),
                            'node-manager': z.string(),
                            'node-manager_one': z.string(),
                            'not-retryable': z.string(),
                            'pick-manager': z.string(),
                            positions: z.string(),
                            'preview-add': z.string(),
                            'preview-add_one': z.string(),
                            'preview-load-error': z.string(),
                            'preview-remove': z.string(),
                            'preview-remove_one': z.string(),
                            'preview-replaced': z.string(),
                            'preview-replaced_one': z.string(),
                            'progress-add': z.string(),
                            'progress-close-hint': z.string(),
                            'progress-remove': z.string(),
                            publish: z.string(),
                            'reason-already-set': z.string(),
                            'reason-loop': z.string(),
                            'reason-manager-limit': z.string(),
                            'reason-own-position': z.string(),
                            remove: z.string(),
                            'replaces-current': z.string(),
                            retry: z.string(),
                            'retry-needs-publish': z.string(),
                            'root-assign': z.string(),
                            'root-remove': z.string(),
                            running: z.string(),
                            'show-skipped': z.string(),
                            'skipped-load-error': z.string(),
                            'skipped-title': z.string(),
                            stopped: z.string(),
                        }),
                        'managers-can-edit': z.object({
                            description: z.string(),
                            title: z.string(),
                        }),
                        map: z.object({
                            'chart-view': z.string(),
                            'clear-filter': z.string(),
                            label: z.string(),
                            'load-error': z.string(),
                            loading: z.string(),
                            more: z.string(),
                            'no-groupable-attribute': z.string(),
                            'no-matching-positions': z.string(),
                            'not-set': z.string(),
                            only: z.string(),
                            'zoom-in': z.string(),
                            'zoom-out': z.string(),
                        }),
                        'no-access': z.string(),
                        'no-areas': z.string(),
                        'org-chart': z.object({
                            'arrange-by': z.string(),
                            'arrange-by-value': z.string(),
                            'collapse-all': z.string(),
                            'fit-to-screen': z.string(),
                            'focus-on-you': z.string(),
                            label: z.string(),
                            'load-error': z.string(),
                            loading: z.string(),
                            'no-matching-positions': z.string(),
                            'not-in-filter': z.string(),
                            'off-screen-left': z.string(),
                            'off-screen-right': z.string(),
                            open: z.string(),
                            pending: z.string(),
                            'pending-reports': z.string(),
                            'pending-reports_one': z.string(),
                            reports: z.string(),
                            'reports-of': z.string(),
                            'reports-with-pending': z.string(),
                            reports_one: z.string(),
                            'root-unavailable': z.string(),
                            'show-from-here': z.string(),
                            'show-of': z.string(),
                            'shows-after-publishing': z.string(),
                            structure: z.string(),
                            'top-of-organization': z.string(),
                            unconnected: z.string(),
                            unconnected_one: z.string(),
                        }),
                        positions: z.object({
                            archived: z.string(),
                            columns: z.object({
                                assignee: z.string(),
                                'external-id': z.string(),
                                level: z.string(),
                                occupancy: z.string(),
                                reviewer: z.string(),
                                status: z.string(),
                                title: z.string(),
                            }),
                            'load-error': z.string(),
                            'load-more-error': z.string(),
                            modify: z.string(),
                            'no-archived-positions': z.string(),
                            'no-positions': z.string(),
                            'no-search-results': z.string(),
                            occupancy: z.object({
                                'approved-open': z.string(),
                                'being-phased-out': z.string(),
                                occupied: z.string(),
                                'signed-hire-not-started': z.string(),
                            }),
                            'search-placeholder': z.string(),
                            'show-active': z.string(),
                            'table-label': z.string(),
                            total: z.string(),
                            total_one: z.string(),
                            'unknown-level': z.string(),
                            'unknown-position': z.string(),
                            untitled: z.string(),
                        }),
                        retry: z.string(),
                        setup: z.object({
                            'answers-error': z.string(),
                            close: z.string(),
                            'company-delimiter-error': z.string(),
                            'company-description-label': z.string(),
                            'company-from-job-architecture': z.string(),
                            'company-from-job-architecture-placeholder': z.string(),
                            'company-name-label': z.string(),
                            'company-priorities-label': z.string(),
                            'company-size-error': z.string(),
                            'company-size-label': z.string(),
                            'company-subtitle': z.string(),
                            'company-title': z.string(),
                            'company-website-label': z.string(),
                            'connect-error': z.string(),
                            'connect-job-architecture': z.string(),
                            'copy-error': z.string(),
                            'edit-company': z.string(),
                            'find-company': z.string(),
                            'generate-error': z.string(),
                            'generate-error-title': z.string(),
                            'generate-start-error': z.string(),
                            generating: z.string(),
                            'generating-leave-hint': z.string(),
                            'load-error': z.string(),
                            'looking-up': z.string(),
                            'missing-headcount': z.string(),
                            preparing: z.string(),
                            preview: z.object({
                                'more-units': z.string(),
                                title: z.string(),
                                units: z.string(),
                            }),
                            progress: z.object({
                                applied: z.string(),
                                applying: z.string(),
                                finishing: z.string(),
                                positions: z.string(),
                                'positions-count': z.string(),
                                preparing: z.string(),
                                queued: z.string(),
                                structure: z.string(),
                                working: z.string(),
                            }),
                            'research-error': z.string(),
                            researching: z.string(),
                            'review-subtitle': z.string(),
                            'review-title': z.string(),
                            'run-error': z.string(),
                            'run-error-architecture-missing': z.string(),
                            'run-error-title': z.string(),
                            'save-error': z.string(),
                            'set-up-with-ai': z.string(),
                            'start-error': z.string(),
                            'step-architecture': z.string(),
                            'step-architecture-description': z.string(),
                            'step-company': z.string(),
                            'step-company-description': z.string(),
                            'step-questions': z.string(),
                            'step-questions-description': z.string(),
                            'step-state': z.object({
                                active: z.string(),
                                done: z.string(),
                                failed: z.string(),
                                upcoming: z.string(),
                                working: z.string(),
                            }),
                            'step-upload': z.string(),
                            'step-upload-description': z.string(),
                            'steps-label': z.string(),
                            'upload-drop': z.string(),
                            'upload-subtitle': z.string(),
                            'upload-title': z.string(),
                            'welcome-subtitle': z.string(),
                            'welcome-title': z.string(),
                        }),
                        split: z.object({
                            'back-to-split': z.string(),
                            'expand-chart': z.string(),
                            'expand-to-table': z.string(),
                            'show-list': z.string(),
                        }),
                    })
                    .partial(),
                'profile-preview': z
                    .object({
                        'open-profile': z.string(),
                        title: z.string(),
                    })
                    .partial(),
                'recruiter-profile': z
                    .object({
                        'about-me': z
                            .object({
                                placeholder: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        ambitions: z
                            .object({
                                placeholder: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        background: z
                            .object({
                                certificates: z
                                    .object({
                                        placeholder: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'course-history': z
                                    .object({
                                        placeholder: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'education-history': z
                                    .object({
                                        placeholder: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                present: z.string(),
                                'project-history': z
                                    .object({
                                        placeholder: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'publication-history': z
                                    .object({
                                        placeholder: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                title: z.string(),
                                'work-history': z
                                    .object({
                                        placeholder: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        contact: z
                            .object({
                                'e-mail': z.string(),
                                phone: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        details: z
                            .object({
                                'job-title': z.string(),
                                location: z.string(),
                                'seniority-level': z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'general-information': z
                            .object({
                                contact: z.string(),
                                teams: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        languages: z
                            .object({
                                placeholder: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'personal-information': z
                            .object({
                                'job-seeking': z
                                    .object({
                                        'not-open': z.string(),
                                        open: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'location-status': z.string(),
                                'seniority-level': z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        skills: z
                            .object({
                                placeholder: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                settings: z
                    .object({
                        'account-deletion': z
                            .object({
                                button: z.string(),
                                'card-description': z.string(),
                                'card-title': z.string(),
                                'debug-link': z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'account-details': z
                            .object({
                                email: z.string(),
                                'full-name': z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'data-privacy': z
                            .object({
                                dialog: z
                                    .object({
                                        'load-error': z.string(),
                                        loading: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'download-gdpr-report': z.string(),
                                'open-privacy-statement': z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'email-notification': z
                            .object({
                                message: z
                                    .object({
                                        description: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                title: z.string(),
                            })
                            .partial(),
                        language: z
                            .object({
                                title: z.string(),
                            })
                            .partial(),
                        'reset-password': z
                            .object({
                                button: z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        'skills-banners': z
                            .object({
                                'button-text': z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        title: z.string(),
                        visibility: z
                            .object({
                                title: z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                signup: z
                    .object({
                        'accept-invite-description': z.string(),
                        'accept-invite-title': z.string(),
                        'acknowledge-and-understood': z.string(),
                        'already-have-an-account': z.string(),
                        'by-clicking-continue': z.string(),
                        'company-name': z.string(),
                        'confirm-password': z.string(),
                        email: z.string(),
                        'email-input-description': z.string(),
                        'email-input-title': z.string(),
                        'email-password-to-sign-in': z.string(),
                        'email-sent-description': z.string(),
                        'email-sent-title': z.string(),
                        'error-account-already-exists': z.string(),
                        'error-code-expired': z.string(),
                        'error-email-not-verified': z.string(),
                        'error-invalid-credentials': z.string(),
                        'error-invalid-email-or-code': z.string(),
                        'error-invalid-invitation': z.string(),
                        'error-invitation-already-accepted': z.string(),
                        'error-unknown-reason': z.string(),
                        'first-name': z.string(),
                        'forgot-password': z.string(),
                        'forgot-password-email-sent-description': z.string(),
                        'forgot-password-screen-description': z.string(),
                        'forgot-password-screen-title': z.string(),
                        'getting-things-ready-description': z.string(),
                        'getting-things-ready-title': z.string(),
                        'last-name': z.string(),
                        'name-and-password-step-description': z.string(),
                        password: z.string(),
                        'password-hint': z.string(),
                        'privacy-policy': z.string(),
                        'reset-password-description': z.string(),
                        'reset-password-title': z.string(),
                        'terms-of-use': z.string(),
                        'url-input-hint': z.string(),
                        'verify-email': z.string(),
                        'welcome-back': z.string(),
                        'welcome-to-cobrainer': z.string(),
                    })
                    .partial(),
                'skill-details': z
                    .object({
                        buttons: z
                            .object({
                                'add-to-required-skills': z.string(),
                                'add-to-skill-profile': z.string(),
                                collapse: z.string(),
                                expand: z.string(),
                                'remove-from-skill-profile': z.string(),
                            })
                            .partial(),
                        headlines: z
                            .object({
                                cluster: z.string(),
                                'job-postings': z.string(),
                                'job-title': z.string(),
                                'published-at': z.string(),
                                'related-skills': z.string(),
                                'required-skills': z.string(),
                                'rows-per-page': z.string(),
                                skill: z.string(),
                                'skill-cluster': z.string(),
                                'skill-cluster-information': z.string(),
                                'skill-description': z.string(),
                                'skill-family': z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        messages: z
                            .object({
                                'general-error': z.string(),
                                'no-description': z.string(),
                                'no-job-postings': z.string(),
                                'no-related-skills': z.string(),
                                'no-url-id': z.string(),
                                'please-wait': z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                'talent-directory': z
                    .object({
                        filters: z
                            .object({
                                'skill-gap': z
                                    .object({
                                        subTitle: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                                'skill-search': z
                                    .object({
                                        placeholder: z.string(),
                                    })
                                    .partial(),
                                skills: z
                                    .object({
                                        subTitle: z.string(),
                                        title: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        'sort-options': z
                            .object({
                                skills: z
                                    .object({
                                        AtoZ: z.string(),
                                        byRelevance: z.string(),
                                        highestToLowest: z.string(),
                                        lowestToHighest: z.string(),
                                        ZtoA: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        title: z.string(),
                    })
                    .partial(),
                'user-certificates': z
                    .object({
                        'acquired-date': z.string(),
                        'acquired-date-in-the-future': z.string(),
                        caption: z.string(),
                        'certificate-title': z.string(),
                        description: z.string(),
                        'expiry-before-acquired': z.string(),
                        'expiry-date': z.string(),
                        'general-info-title': z.string(),
                        issuer: z.string(),
                        'only-one-skill-can-be-added': z.string(),
                        'single-skill-must-be-added': z.string(),
                        skills: z.string(),
                        'skills-list-headline': z.string(),
                        'skills-recommendation-fields-are-missed': z.string(),
                        title: z.string(),
                        'title-add': z.string(),
                        'title-edit': z.string(),
                    })
                    .partial(),
                'user-course-history': z
                    .object({
                        caption: z.string(),
                        'course-title': z.string(),
                        description: z.string(),
                        'end-date': z.string(),
                        from: z.string(),
                        'general-info-title': z.string(),
                        ongoing: z.string(),
                        school: z.string(),
                        skills: z.string(),
                        'skills-list-headline': z.string(),
                        'skills-recommendation-fields-are-missed': z.string(),
                        'skills-recommendation-tooltip': z.string(),
                        'start-date': z.string(),
                        'title-add': z.string(),
                        'title-edit': z.string(),
                        to: z.string(),
                    })
                    .partial(),
                'user-education-history': z
                    .object({
                        caption: z.string(),
                        'currently-studying': z.string(),
                        degree: z.string(),
                        description: z.string(),
                        'end-date': z.string(),
                        'field-of-study': z.string(),
                        from: z.string(),
                        'general-info-title': z.string(),
                        'school-name': z.string(),
                        skills: z.string(),
                        'skills-list-headline': z.string(),
                        'skills-recommendation-fields-are-missed': z.string(),
                        'skills-recommendation-tooltip': z.string(),
                        'start-date': z.string(),
                        'title-add': z.string(),
                        'title-edit': z.string(),
                        to: z.string(),
                    })
                    .partial(),
                'user-profile': z
                    .object({
                        'ambitions-edit': z
                            .object({
                                'add-ambitions': z
                                    .object({
                                        label: z.string(),
                                        'label-disabled': z.string(),
                                        'limit-exceeded': z.string(),
                                        'no-search-results': z.string(),
                                    })
                                    .partial(),
                                headline: z.string(),
                                'number-of-ambitions': z.string(),
                                title: z.string(),
                            })
                            .partial(),
                        banners: z
                            .object({
                                'max-unknown-proficiency-limit': z
                                    .object({
                                        message: z.string(),
                                    })
                                    .partial(),
                                'minimum-skills-requirement': z
                                    .object({
                                        message: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        endorsements: z
                            .object({
                                certificate: z.string(),
                                certificate_one: z.string(),
                                course: z.string(),
                                course_one: z.string(),
                                education: z.string(),
                                education_one: z.string(),
                                project: z.string(),
                                project_one: z.string(),
                                publication: z.string(),
                                publication_one: z.string(),
                                work: z.string(),
                                work_one: z.string(),
                            })
                            .partial(),
                        'language-edit': z
                            .object({
                                'add-languages': z
                                    .object({
                                        label: z.string(),
                                        'label-disabled': z.string(),
                                        'limit-exceeded': z.string(),
                                        'no-search-results': z.string(),
                                    })
                                    .partial(),
                                headline: z.string(),
                                'list-headline': z.string(),
                                'number-of-languages': z.string(),
                            })
                            .partial(),
                        'profile-visibility': z
                            .object({
                                'configure-visibility': z.string(),
                                drawer: z
                                    .object({
                                        'apply-settings-to': z
                                            .object({
                                                'ambitions-label': z.string(),
                                                'overall-visibility-label': z.string(),
                                                'personal-information-label': z.string(),
                                                'profile-history-label': z.string(),
                                                'skills-label': z.string(),
                                                'visible-to-everyone': z.string(),
                                                'visible-to-me': z.string(),
                                                'visible-to-roles-and-users': z.string(),
                                            })
                                            .partial(),
                                        profile: z
                                            .object({
                                                headline: z.string(),
                                                'profile-visibility-label': z.string(),
                                                subtitle: z.string(),
                                                'visibility-label': z.string(),
                                                'visibility-placeholder': z.string(),
                                            })
                                            .partial(),
                                        sections: z
                                            .object({
                                                'no-results': z.string(),
                                                'search-placeholder': z.string(),
                                                'visible-people-label': z.string(),
                                                'visible-roles-label': z.string(),
                                            })
                                            .partial(),
                                    })
                                    .partial(),
                                'profile-visibility-configuration': z.string(),
                                tooltips: z
                                    .object({
                                        private: z.string(),
                                        public: z.string(),
                                        restricted: z.string(),
                                    })
                                    .partial(),
                            })
                            .partial(),
                        'skill-edit': z
                            .object({
                                'add-skills': z
                                    .object({
                                        label: z.string(),
                                        'label-disabled': z.string(),
                                        'limit-exceeded': z.string(),
                                        'suggested-skills-label': z.string(),
                                    })
                                    .partial(),
                                'container-title': z.string(),
                                headline: z.string(),
                                'number-of-ambitions': z.string(),
                                'number-of-skills': z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                'user-project-history': z
                    .object({
                        caption: z.string(),
                        'company-name': z.string(),
                        description: z.string(),
                        'end-date': z.string(),
                        from: z.string(),
                        'general-info-title': z.string(),
                        location: z.string(),
                        'project-title': z.string(),
                        skills: z.string(),
                        'skills-list-headline': z.string(),
                        'skills-recommendation-fields-are-missed': z.string(),
                        'skills-recommendation-tooltip': z.string(),
                        'start-date': z.string(),
                        'still-ongoing': z.string(),
                        'title-add': z.string(),
                        'title-edit': z.string(),
                        to: z.string(),
                    })
                    .partial(),
                'user-publication-history': z
                    .object({
                        caption: z.string(),
                        date: z.string(),
                        description: z.string(),
                        'general-info-title': z.string(),
                        'publication-name': z.string(),
                        publisher: z.string(),
                        skills: z.string(),
                        'skills-list-headline': z.string(),
                        'skills-recommendation-fields-are-missed': z.string(),
                        'skills-recommendation-tooltip': z.string(),
                        'title-add': z.string(),
                        'title-edit': z.string(),
                    })
                    .partial(),
                'user-work-history': z
                    .object({
                        caption: z.string(),
                        'company-name': z.string(),
                        'currently-working': z.string(),
                        department: z.string(),
                        description: z.string(),
                        'end-date': z.string(),
                        from: z.string(),
                        'general-info-title': z.string(),
                        'job-title': z.string(),
                        location: z.string(),
                        skills: z.string(),
                        'skills-list-headline': z.string(),
                        'skills-recommendation-fields-are-missed': z.string(),
                        'skills-recommendation-tooltip': z.string(),
                        'start-date': z.string(),
                        'title-add': z.string(),
                        'title-edit': z.string(),
                        to: z.string(),
                    })
                    .partial(),
                workspace: z
                    .object({
                        'sub-title': z.string(),
                        title: z.string(),
                    })
                    .partial(),
            })
            .partial(),
        providers: z.partialRecord(
            z.union([z.literal('internal'), z.literal('linkedin'), z.literal('udemy'), z.literal('degreed')]),
            z.string(),
        ),
        roles: z.record(userRoleSchema, roleSchema),
        'side-navigation': z
            .object({
                'aria-label': z.string(),
                'opener-alt-text': z.string(),
            })
            .partial(),
        states: z
            .object({
                active: z.string(),
                all: z.string(),
                archived: z.string(),
                draft: z.string(),
                inactive: z.string(),
                published: z.string(),
            })
            .partial(),
        success: z
            .object({
                ambitions: z
                    .object({
                        added: z.string(),
                        updated: z.string(),
                    })
                    .partial(),
                'feature-opportunities': z
                    .object({
                        feature: z.string(),
                    })
                    .partial(),
                job: z
                    .object({
                        archived: z.string(),
                        created: z.string(),
                        deleted: z.string(),
                        draft: z.string(),
                        exported: z.string(),
                        hired: z.string(),
                        published: z.string(),
                        updated: z.string(),
                    })
                    .partial(),
                'job-profile': z
                    .object({
                        created: z.string(),
                        deleted: z.string(),
                        edited: z.string(),
                    })
                    .partial(),
                'learning-opportunities': z
                    .object({
                        'added-to-profile': z.string(),
                        created: z.string(),
                        deleted: z.string(),
                        edited: z.string(),
                        exported: z.string(),
                    })
                    .partial(),
                profile: z
                    .object({
                        exported: z.string(),
                        uploaded: z.string(),
                    })
                    .partial(),
                'profile-visibility': z
                    .object({
                        'more-information': z
                            .object({
                                message: z.string(),
                            })
                            .partial(),
                    })
                    .partial(),
                settings: z
                    .object({
                        'banner-reset': z.string(),
                        'gdpr-downloaded': z.string(),
                    })
                    .partial(),
                shortlist: z
                    .object({
                        added: z.string(),
                        exported: z.string(),
                        removed: z.string(),
                        reordered: z.string(),
                    })
                    .partial(),
                skills: z
                    .object({
                        'added-to-profile': z.string(),
                        exported: z.string(),
                        'removed-from-profile': z.string(),
                    })
                    .partial(),
                users: z
                    .object({
                        exported: z.string(),
                    })
                    .partial(),
            })
            .partial(),
        usermenu: z
            .object({
                'account-settings': z.string(),
                anonymize: z.string(),
                appearance: z.string(),
                'appearance-dark': z.string(),
                'appearance-light': z.string(),
                'appearance-system': z.string(),
                'keyboard-navigation-shortcuts': z.string(),
                'keyboard-shortcuts-off': z.string(),
                'my-profile': z.string(),
                'status-page': z.string(),
                'switch-theme': z.string(),
                tooltip: z.string(),
            })
            .partial(),
        validation: z
            .object({
                'date-period-consistency': z.string(),
                'empty-search-results': z.string(),
                'from-list': z.string(),
                'https-url-only': z.string(),
                maxLength: z.string(),
                minLength: z.string(),
                minTranslationLength: z.string(),
                'not-more-than': z.string(),
                oneOf: z.string(),
                required: z.string(),
                'search-term': z.string(),
                withOneOfPrefix: z.string(),
            })
            .partial(),
    })
    .partial();

const recordSchema = z.record(z.string(), z.string());

const recordSchemaRecursive = z.lazy(() => z.record(z.string(), z.union([z.string(), recordSchema])));

const translationAttributeOverridesSchema = z.object({
    'job-profile-tags': recordSchemaRecursive,
    jobProfile: recordSchemaRecursive,
    jobs: recordSchemaRecursive,
    learningOpportunity: recordSchemaRecursive,
    user: recordSchemaRecursive,
});

export type TranslationWithOverrideAttributes = z.infer<typeof translationAttributeOverridesSchema>;

const translationWithOverrideSchema = translationResourceSchema
    .extend({
        attributes: translationAttributeOverridesSchema.partial(),
        providers: z.record(z.string(), z.string()),
        roles: z.record(z.string(), roleSchema),
    })
    .partial();

export const translationResourceOverrideSchema = translationWithOverrideSchema;

export type TranslationResource = RequiredDeep<z.infer<typeof translationResourceSchema>>;

export type TranslationWithOverride = z.infer<typeof translationWithOverrideSchema>;

type StringKeyOf<T> = keyof {
    readonly [K in keyof T as T[K] extends string
        ? `${K & (number | string)}`
        : T[K] extends Record<string, unknown>
          ? `${K & (number | string)}.${StringKeyOf<NonNullable<T[K]>>}`
          : never]: T[K] extends string ? K : never;
} extends `${infer U}`
    ? U extends symbol
        ? never
        : U
    : never;

export type TranslationKey =
    | StringKeyOf<TranslationResource>
    | `announcements.${string}`
    | `attributes.job-profile-tags.${string}`
    | `attributes.jobProfile.${string}`
    | `attributes.jobs.${string}`
    | `attributes.learningOpportunity.${string}`
    | `attributes.user.${string}`
    | `common.login${string}`
    | `overwrites.${string}`
    | `pages.documentation.${string}`
    | `pages.dashboard-v2.${string}`
    | `pages.job-architecture-v2.export.${string}`
    | `pages.job-architecture-v2.profile-tasks.${string}`
    | `providers.${string}`;

// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration
export type Translate = (key: TranslationKey, options?: TOptions<any>) => string;

export const scopedTranslate =
    (translate: Translate, prefix: string) => (key: string, options?: Parameters<Translate>[1]) =>
        translate(`${prefix}${key}` as TranslationKey, options);
